// src/index.ts
import { createParser } from "eventsource-parser";

// src/error_utils.ts
function errorToString(error) {
  if (error instanceof Error) {
    const cause = "cause" in error ? errorToString(error.cause) : null;
    return `${error.message}${cause ? `
- Cause: ${cause}` : ""}`;
  } else if (typeof error === "string") {
    return error;
  }
  return JSON.stringify(error);
}
function normalizeError(error) {
  if (error instanceof Error) {
    return error;
  }
  return new Error(errorToString(error));
}

// src/errors/errors.ts
var DustError = class extends Error {
  constructor(message, options) {
    super(message);
    this.name = this.constructor.name;
    this.requestId = options?.requestId;
    this.statusCode = options?.statusCode;
    this.cause = options?.cause;
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }
  }
  get isRetryable() {
    return false;
  }
  toString() {
    let str = `${this.name} [${this.code}]: ${this.message}`;
    if (this.statusCode) {
      str += ` (status: ${this.statusCode})`;
    }
    if (this.requestId) {
      str += ` (requestId: ${this.requestId})`;
    }
    return str;
  }
};
var DustAuthenticationError = class extends DustError {
  constructor() {
    super(...arguments);
    this.code = "authentication_error";
  }
};
var DustRateLimitError = class extends DustError {
  constructor(message, options) {
    super(message, options);
    this.code = "rate_limit_error";
    this.retryAfterMs = options?.retryAfterMs;
  }
  get isRetryable() {
    return true;
  }
};
var DustValidationError = class extends DustError {
  constructor(message, options) {
    super(message, options);
    this.code = "validation_error";
    this.field = options?.field;
  }
};
var DustAgentError = class extends DustError {
  constructor(message, options) {
    super(message, options);
    this.code = "agent_error";
    this.agentId = options?.agentId;
  }
};
var DustNetworkError = class extends DustError {
  constructor(message, options) {
    super(message, options);
    this.code = "network_error";
    this._isRetryable = options?.isRetryable ?? true;
  }
  get isRetryable() {
    return this._isRetryable;
  }
};
var DustCancelledError = class extends DustError {
  constructor(message = "Operation cancelled") {
    super(message);
    this.code = "cancelled";
  }
};
var DustTimeoutError = class extends DustError {
  constructor(message, options) {
    super(message, options);
    this.code = "timeout";
    this.timeoutMs = options?.timeoutMs;
  }
  get isRetryable() {
    return true;
  }
};
var DustNotFoundError = class extends DustError {
  constructor(message, options) {
    super(message, options);
    this.code = "not_found";
    this.resourceType = options?.resourceType;
    this.resourceId = options?.resourceId;
  }
};
var DustPermissionError = class extends DustError {
  constructor() {
    super(...arguments);
    this.code = "permission_denied";
  }
};
var DustServerError = class extends DustError {
  constructor() {
    super(...arguments);
    this.code = "server_error";
  }
  get isRetryable() {
    return true;
  }
};
var DustContentTooLargeError = class extends DustError {
  constructor() {
    super(...arguments);
    this.code = "content_too_large";
  }
};
var DustUnknownError = class extends DustError {
  constructor(message, options) {
    super(message, options);
    this.code = "unknown_error";
    this.originalError = options?.originalError;
  }
};
var errorTypeMapping = {
  not_authenticated: DustAuthenticationError,
  invalid_api_key_error: DustAuthenticationError,
  malformed_authorization_header_error: DustAuthenticationError,
  workspace_auth_error: DustAuthenticationError,
  rate_limit_error: DustRateLimitError,
  invalid_request_error: DustValidationError,
  invalid_pagination_parameters: DustValidationError,
  missing_required_parameters: DustValidationError,
  file_type_not_supported: DustValidationError,
  conversation_not_found: DustNotFoundError,
  agent_configuration_not_found: DustNotFoundError,
  data_source_not_found: DustNotFoundError,
  file_not_found: DustNotFoundError,
  message_not_found: DustNotFoundError,
  workspace_not_found: DustNotFoundError,
  space_not_found: DustNotFoundError,
  user_not_found: DustNotFoundError,
  not_found: DustNotFoundError,
  subscription_payment_failed: DustPermissionError,
  plan_limit_error: DustPermissionError,
  plan_message_limit_exceeded: DustPermissionError,
  credits_exhausted: DustPermissionError,
  user_cap_reached: DustPermissionError,
  subscription_required: DustPermissionError,
  workspace_can_use_product_required_error: DustPermissionError,
  content_too_large: DustContentTooLargeError,
  internal_server_error: DustServerError,
  unexpected_network_error: DustNetworkError
};
var statusCodeMapping = {
  401: DustAuthenticationError,
  403: DustPermissionError,
  404: DustNotFoundError,
  408: DustTimeoutError,
  413: DustContentTooLargeError,
  429: DustRateLimitError
};
function apiErrorToDustError(apiError, statusCode) {
  const ErrorClass = errorTypeMapping[apiError.type];
  if (ErrorClass) {
    return new ErrorClass(apiError.message, { statusCode });
  }
  if (statusCode) {
    const StatusErrorClass = statusCodeMapping[statusCode];
    if (StatusErrorClass) {
      return new StatusErrorClass(apiError.message, { statusCode });
    }
    if (statusCode >= 500) {
      return new DustServerError(apiError.message, { statusCode });
    }
  }
  return new DustUnknownError(apiError.message, {
    statusCode,
    originalError: apiError
  });
}
function isDustError(error) {
  return error instanceof DustError;
}
function isRetryableError(error) {
  if (error instanceof DustError) {
    return error.isRetryable;
  }
  return false;
}

// src/high_level/retry.ts
var DEFAULT_RETRY_OPTIONS = {
  maxAttempts: 3,
  initialDelayMs: 500,
  maxDelayMs: 3e4,
  backoffMultiplier: 2,
  jitterFactor: 0.1,
  retryableStatuses: [408, 429, 502, 503, 504]
};
async function sleep(ms, signal) {
  if (signal?.aborted) {
    throw new DustCancelledError("Operation cancelled");
  }
  return new Promise((resolve, reject) => {
    const onAbort = () => {
      clearTimeout(timeoutId);
      reject(new DustCancelledError("Operation cancelled"));
    };
    signal?.addEventListener("abort", onAbort, { once: true });
    const timeoutId = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
  });
}
function calculateDelayWithJitter(baseDelay, jitterFactor, maxDelay) {
  const jitter = baseDelay * jitterFactor * (Math.random() * 2 - 1);
  return Math.min(Math.max(0, baseDelay + jitter), maxDelay);
}
function shouldRetry(error, customCheck) {
  return customCheck ? customCheck(error) : isRetryableError(error);
}
function getRateLimitDelay(error) {
  return error instanceof DustRateLimitError ? error.retryAfterMs : void 0;
}
async function withRetry(operation, options = {}) {
  const opts = { ...DEFAULT_RETRY_OPTIONS, ...options };
  const { signal, isRetryable: customIsRetryable, onRetry } = options;
  let lastError = null;
  let currentDelay = opts.initialDelayMs;
  for (let attempt = 1; attempt <= opts.maxAttempts; attempt++) {
    if (signal?.aborted) {
      throw new DustCancelledError("Operation cancelled");
    }
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      if (error instanceof DustCancelledError) {
        throw error;
      }
      if (!shouldRetry(error, customIsRetryable)) {
        throw error;
      }
      if (attempt >= opts.maxAttempts) {
        throw error;
      }
      const rateLimitDelay = getRateLimitDelay(error);
      const delayMs = rateLimitDelay ?? calculateDelayWithJitter(
        currentDelay,
        opts.jitterFactor,
        opts.maxDelayMs
      );
      onRetry?.(error, attempt, delayMs);
      await sleep(delayMs, signal);
      currentDelay = Math.min(
        currentDelay * opts.backoffMultiplier,
        opts.maxDelayMs
      );
    }
  }
  throw lastError;
}
function createRetry(defaultOptions) {
  return (operation, options = {}) => {
    return withRetry(operation, { ...defaultOptions, ...options });
  };
}

// src/high_level/context.ts
var DEFAULT_CONTEXT = {
  username: "API User",
  timezone: "UTC",
  origin: "api"
};
function getSystemTimezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}
function buildContext(partial) {
  const username = partial?.username ?? DEFAULT_CONTEXT.username;
  return {
    username,
    timezone: partial?.timezone ?? getSystemTimezone(),
    origin: partial?.origin ?? DEFAULT_CONTEXT.origin,
    email: partial?.email,
    fullName: partial?.fullName ?? username,
    profilePictureUrl: partial?.profilePictureUrl
  };
}

// src/high_level/guards.ts
function hasStringProperty(obj, key) {
  return typeof obj === "object" && obj !== null && key in obj && typeof obj[key] === "string";
}
function isAgentMessage(m) {
  return typeof m === "object" && m !== null && "type" in m && m.type === "agent_message" && hasStringProperty(m, "sId") && hasStringProperty(m, "parentMessageId");
}
function isAPIError(error) {
  return hasStringProperty(error, "type") && hasStringProperty(error, "message");
}
function isActionEventData(obj) {
  return hasStringProperty(obj, "id") && hasStringProperty(obj, "type");
}

// src/high_level/types.ts
function isFileIdAttachment(attachment) {
  return "fileId" in attachment;
}
function isFilePathAttachment(attachment) {
  return "path" in attachment;
}
function isBlobAttachment(attachment) {
  return attachment instanceof Blob;
}

// src/high_level/stream.ts
function findFirstAgentMessage(content, parentMessageId) {
  const agentMessage = content.flat().find((m) => isAgentMessage(m) && m.parentMessageId === parentMessageId);
  if (!agentMessage || !isAgentMessage(agentMessage)) {
    throw new DustAgentError("No agent message found in response");
  }
  return agentMessage;
}
var MessageStreamImpl = class {
  constructor(client, params, autoApproveTools = false) {
    this._handlers = /* @__PURE__ */ new Map();
    this._text = "";
    this._chainOfThought = "";
    this._actions = [];
    this._conversationId = null;
    this._messageId = null;
    this._agentMessageId = null;
    this._started = false;
    this._finished = false;
    this._response = null;
    this._error = null;
    this._uploadedFileIds = [];
    this._client = client;
    this._params = params;
    this._autoApproveTools = autoApproveTools;
    this._abortController = new AbortController();
    this._externalSignal = params.signal;
    if (this._externalSignal) {
      if (this._externalSignal.aborted) {
        this._abortController.abort();
      } else {
        this._externalSignal.addEventListener(
          "abort",
          () => this._abortController.abort(),
          { once: true }
        );
      }
    }
  }
  get text() {
    return this._text;
  }
  get chainOfThought() {
    return this._chainOfThought;
  }
  get actions() {
    return [...this._actions];
  }
  get conversationId() {
    return this._conversationId;
  }
  get messageId() {
    return this._messageId;
  }
  on(event, handler) {
    const typedHandler = handler;
    if (event === "toolApprovalRequired") {
      const existingHandlers = this._handlers.get(event);
      if (existingHandlers && existingHandlers.length > 0) {
        this._client._logger.warn(
          {},
          "Overwriting existing toolApprovalRequired handler. Only one handler is allowed."
        );
      }
      this._handlers.set(event, [typedHandler]);
    } else {
      const handlers = this._handlers.get(event) ?? [];
      handlers.push(typedHandler);
      this._handlers.set(event, handlers);
    }
    return () => {
      const handlers = this._handlers.get(event);
      if (handlers) {
        const index = handlers.indexOf(typedHandler);
        if (index !== -1) {
          handlers.splice(index, 1);
        }
      }
    };
  }
  abort() {
    this._abortController.abort();
  }
  async finalMessage() {
    if (this._finished && this._response) {
      return this._response;
    }
    if (this._finished && this._error) {
      throw this._error;
    }
    for await (const _event of this) {
    }
    if (this._error) {
      throw this._error;
    }
    if (!this._response) {
      throw new DustAgentError("Stream ended without a response");
    }
    return this._response;
  }
  async *[Symbol.asyncIterator]() {
    if (this._started) {
      throw new Error("Stream can only be iterated once");
    }
    this._started = true;
    try {
      if (this._params.attachments?.length) {
        yield* this._uploadAttachments(this._params.attachments);
      }
      this._checkCancelled();
      const { conversationId, userMessageId, agentMessageId, conversation } = await this._createConversationAndPostMessage();
      this._conversationId = conversationId;
      this._messageId = userMessageId;
      this._agentMessageId = agentMessageId;
      this._checkCancelled();
      yield* this._streamResponse(conversation, userMessageId);
    } catch (error) {
      const dustError = this._abortController.signal.aborted ? new DustCancelledError("Stream cancelled") : this._toDustError(error);
      this._error = dustError;
      this._finished = true;
      yield { type: "error", error: dustError };
      this._emitToHandlers("error", dustError);
      throw dustError;
    }
  }
  get _signal() {
    return this._abortController.signal;
  }
  _checkCancelled() {
    if (this._signal.aborted) {
      throw new DustCancelledError("Operation cancelled");
    }
  }
  _toDustError(error) {
    if (error instanceof DustError) {
      return error;
    }
    if (error instanceof Error) {
      return new DustUnknownError(error.message, { cause: error });
    }
    return new DustUnknownError(String(error));
  }
  _emitToHandlers(type, ...args) {
    const handlers = this._handlers.get(type);
    if (handlers) {
      for (const handler of handlers) {
        handler(...args);
      }
    }
  }
  _getString(obj, key, defaultValue = "") {
    return hasStringProperty(obj, key) ? obj[key] : defaultValue;
  }
  _setErrorAndFinish(error) {
    this._error = error;
    this._finished = true;
    return { type: "error", error };
  }
  async *_uploadAttachments(attachments) {
    const totalFiles = attachments.length;
    const uploadedFileIds = [];
    for (let i = 0; i < attachments.length; i++) {
      this._checkCancelled();
      const attachment = attachments[i];
      const fileName = this._getFileName(attachment, i);
      const startProgress = {
        fileName,
        uploaded: 0,
        total: this._getFileSize(attachment),
        fileIndex: i,
        totalFiles
      };
      yield { type: "uploadProgress", progress: startProgress };
      this._emitToHandlers("uploadProgress", startProgress);
      let fileId;
      if (isFileIdAttachment(attachment)) {
        fileId = attachment.fileId;
      } else if (isBlobAttachment(attachment)) {
        const file = attachment instanceof File ? attachment : new File([attachment], fileName, {
          type: attachment.type || "application/octet-stream"
        });
        const uploadResult = await this._client.uploadFile({
          contentType: file.type || "application/octet-stream",
          fileName,
          fileSize: file.size,
          useCase: "conversation",
          fileObject: file,
          signal: this._signal
        });
        if (uploadResult.isErr()) {
          if (uploadResult.error instanceof Error) {
            throw uploadResult.error;
          }
          throw apiErrorToDustError(uploadResult.error);
        }
        fileId = uploadResult.value.sId;
      } else {
        throw new Error(
          "File path attachments are not yet supported. Please pass a File or Blob object."
        );
      }
      uploadedFileIds.push(fileId);
      const completeProgress = {
        fileName,
        uploaded: this._getFileSize(attachment),
        total: this._getFileSize(attachment),
        fileIndex: i,
        totalFiles
      };
      yield { type: "uploadProgress", progress: completeProgress };
      this._emitToHandlers("uploadProgress", completeProgress);
    }
    this._uploadedFileIds = uploadedFileIds;
  }
  async _createConversationAndPostMessage() {
    const context = buildContext(this._params.context);
    const contentFragments = this._uploadedFileIds.map((fileId) => ({
      fileId,
      title: "Attachment",
      context
    }));
    if (this._params.conversationId) {
      for (const fragment of contentFragments) {
        const fragmentResult = await this._client.postContentFragment({
          conversationId: this._params.conversationId,
          contentFragment: fragment,
          signal: this._signal
        });
        if (fragmentResult.isErr()) {
          throw apiErrorToDustError(fragmentResult.error);
        }
      }
      const messageResult = await this._client.postUserMessage({
        conversationId: this._params.conversationId,
        message: {
          content: this._params.message,
          mentions: [{ configurationId: this._params.agentId }],
          context
        },
        signal: this._signal
      });
      if (messageResult.isErr()) {
        throw apiErrorToDustError(messageResult.error);
      }
      const convResult = await this._client.getConversation({
        conversationId: this._params.conversationId,
        signal: this._signal
      });
      if (convResult.isErr()) {
        throw apiErrorToDustError(convResult.error);
      }
      const userMessage = messageResult.value;
      const firstAgentMessage = findFirstAgentMessage(
        convResult.value.content,
        userMessage.sId
      );
      return {
        conversationId: this._params.conversationId,
        userMessageId: userMessage.sId,
        agentMessageId: firstAgentMessage.sId,
        conversation: convResult.value
      };
    } else {
      const result = await this._client.createConversation({
        title: null,
        visibility: "unlisted",
        message: {
          content: this._params.message,
          mentions: [{ configurationId: this._params.agentId }],
          context
        },
        contentFragments: contentFragments.length > 0 ? contentFragments : void 0,
        skipToolsValidation: this._params.skipToolsValidation,
        signal: this._signal
      });
      if (result.isErr()) {
        throw apiErrorToDustError(result.error);
      }
      const { conversation, message: userMessage } = result.value;
      if (!userMessage) {
        throw new DustAgentError("No user message returned in response");
      }
      const firstAgentMessage = findFirstAgentMessage(
        conversation.content,
        userMessage.sId
      );
      return {
        conversationId: conversation.sId,
        userMessageId: userMessage.sId,
        agentMessageId: firstAgentMessage.sId,
        conversation
      };
    }
  }
  async *_streamResponse(conversation, userMessageId) {
    const streamResult = await this._client.streamAgentAnswerEvents({
      conversation,
      userMessageId,
      signal: this._signal
    });
    if (streamResult.isErr()) {
      if (streamResult.error instanceof Error) {
        throw new DustUnknownError(streamResult.error.message, {
          cause: streamResult.error
        });
      }
      if (isAPIError(streamResult.error)) {
        throw apiErrorToDustError(streamResult.error);
      }
      throw new DustUnknownError("Unknown stream error");
    }
    for await (const event of streamResult.value.eventStream) {
      this._checkCancelled();
      const mapped = this._mapEvent(event);
      if (mapped) {
        if (mapped.type === "toolApprovalRequired") {
          yield* this._handleToolApproval(mapped.approval);
          continue;
        }
        yield mapped;
        this._emitMappedEvent(mapped);
      }
    }
  }
  _mapEvent(event) {
    switch (event.type) {
      case "generation_tokens": {
        const classification = this._getString(event, "classification");
        const text = this._getString(event, "text");
        if (classification === "tokens") {
          this._text += text;
          return { type: "text", delta: text };
        }
        if (classification === "chain_of_thought") {
          this._chainOfThought += text;
          return { type: "chainOfThought", delta: text };
        }
        return null;
      }
      case "agent_action_success": {
        const actionData = event.action;
        if (!isActionEventData(actionData)) {
          return null;
        }
        const action = {
          id: actionData.id,
          type: actionData.type,
          toolName: actionData.toolName ?? actionData.type,
          input: actionData.input,
          output: actionData.output,
          status: "success"
        };
        this._actions.push(action);
        return { type: "action", action };
      }
      case "mcp_approve_execution": {
        const messageId = this._getString(event, "messageId");
        const actionId = this._getString(event, "actionId");
        const toolName = this._getString(event, "toolName");
        const serverName = this._getString(event, "serverName");
        const description = this._getString(event, "description");
        const approval = {
          messageId,
          actionId,
          toolName,
          serverName,
          input: event.input,
          description,
          approve: async () => {
            if (!this._conversationId) {
              throw new DustAgentError("No conversation ID available");
            }
            await this._client.validateAction({
              conversationId: this._conversationId,
              messageId,
              actionId,
              approved: "approved",
              signal: this._signal
            });
          },
          reject: async () => {
            if (!this._conversationId) {
              throw new DustAgentError("No conversation ID available");
            }
            await this._client.validateAction({
              conversationId: this._conversationId,
              messageId,
              actionId,
              approved: "rejected",
              signal: this._signal
            });
          }
        };
        return { type: "toolApprovalRequired", approval };
      }
      case "agent_message_gracefully_stopped":
      case "agent_message_success": {
        this._response = {
          text: this._text,
          conversationId: this._conversationId,
          messageId: this._messageId,
          agentMessageId: this._agentMessageId,
          actions: this._actions,
          chainOfThought: this._chainOfThought || void 0
        };
        this._finished = true;
        return { type: "done", response: this._response };
      }
      case "agent_error": {
        const message = this._getString(event.error, "message", "Agent error");
        return this._setErrorAndFinish(new DustAgentError(message));
      }
      case "user_message_error": {
        const message = this._getString(
          event.error,
          "message",
          "Message error"
        );
        return this._setErrorAndFinish(new DustValidationError(message));
      }
      case "agent_generation_cancelled":
        return this._setErrorAndFinish(
          new DustCancelledError("Generation cancelled")
        );
      case "tool_error": {
        const actionId = this._getString(event, "actionId");
        const toolName = this._getString(event, "toolName");
        const errorMessage = this._getString(event.error, "message") || void 0;
        const action = {
          id: actionId,
          type: "tool_error",
          toolName,
          input: event.input,
          output: null,
          status: "error",
          error: errorMessage
        };
        this._actions.push(action);
        return { type: "action", action };
      }
      default:
        return null;
    }
  }
  _emitMappedEvent(event) {
    switch (event.type) {
      case "text":
        this._emitToHandlers("text", event.delta);
        return;
      case "chainOfThought":
        this._emitToHandlers("chainOfThought", event.delta);
        return;
      case "action":
        this._emitToHandlers("action", event.action);
        return;
      case "error":
        this._emitToHandlers("error", event.error);
        return;
      case "done":
        this._emitToHandlers("done", event.response);
        return;
    }
  }
  _createBlockedAction(approval, errorMessage) {
    return {
      id: approval.actionId,
      type: "mcp_action",
      toolName: approval.toolName,
      input: approval.input,
      output: null,
      status: "blocked",
      error: errorMessage
    };
  }
  *_yieldBlockedAction(approval, errorMessage) {
    const action = this._createBlockedAction(approval, errorMessage);
    this._actions.push(action);
    yield { type: "action", action };
    this._emitToHandlers("action", action);
  }
  async *_handleToolApproval(approval) {
    if (this._autoApproveTools) {
      await approval.approve();
      return;
    }
    const handlers = this._handlers.get("toolApprovalRequired");
    if (!handlers || handlers.length === 0) {
      await approval.reject();
      yield* this._yieldBlockedAction(
        approval,
        "Tool execution rejected - no approval handler registered"
      );
      return;
    }
    yield { type: "toolApprovalRequired", approval };
    const handler = handlers[0];
    try {
      const approved = await handler(approval);
      if (approved) {
        await approval.approve();
      } else {
        await approval.reject();
        yield* this._yieldBlockedAction(
          approval,
          "Tool execution rejected by user"
        );
      }
    } catch (error) {
      await approval.reject();
      yield* this._yieldBlockedAction(
        approval,
        `Tool execution rejected - handler error: ${error}`
      );
    }
  }
  _getExtensionFromMimeType(mimeType) {
    const mimeToExt = {
      "application/pdf": ".pdf",
      "image/png": ".png",
      "image/jpeg": ".jpg",
      "image/gif": ".gif",
      "image/webp": ".webp",
      "image/svg+xml": ".svg",
      "text/plain": ".txt",
      "text/csv": ".csv",
      "text/html": ".html",
      "application/json": ".json",
      "application/xml": ".xml",
      "application/zip": ".zip",
      "application/msword": ".doc",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": ".docx",
      "application/vnd.ms-excel": ".xls",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": ".xlsx",
      "application/vnd.ms-powerpoint": ".ppt",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation": ".pptx"
    };
    return mimeToExt[mimeType] ?? "";
  }
  _getFileName(attachment, index) {
    if (attachment instanceof File) {
      return attachment.name;
    }
    if ("path" in attachment && attachment.name) {
      return attachment.name;
    }
    if ("path" in attachment) {
      const pathName = attachment.path.split("/").pop();
      if (pathName) {
        return pathName;
      }
    }
    if (attachment instanceof Blob) {
      const ext = this._getExtensionFromMimeType(attachment.type);
      return `attachment-${index}${ext}`;
    }
    return `attachment-${index}`;
  }
  _getFileSize(attachment) {
    if (isFileIdAttachment(attachment)) {
      return 0;
    }
    if (attachment instanceof File || attachment instanceof Blob) {
      return attachment.size;
    }
    return 0;
  }
};

// src/high_level/agents.ts
var AgentsAPI = class {
  constructor(client, options) {
    this._client = client;
    this._retryOptions = { ...DEFAULT_RETRY_OPTIONS, ...options?.retry };
    this._autoApproveTools = options?.autoApproveTools ?? false;
  }
  async sendMessage(params, options) {
    const retryOptions = {};
    if (options?.maxRetries !== void 0) {
      retryOptions.maxAttempts = options.maxRetries + 1;
    }
    return withRetry(
      async () => {
        const stream = this.streamMessage(params);
        return stream.finalMessage();
      },
      { ...this._retryOptions, ...retryOptions, signal: params.signal }
    );
  }
  streamMessage(params) {
    return new MessageStreamImpl(this._client, params, this._autoApproveTools);
  }
};

// src/high_level/conversations.ts
function toConversationInfo(conversation) {
  return {
    id: conversation.sId,
    title: conversation.title,
    visibility: conversation.visibility,
    created: conversation.created,
    updated: conversation.updated ?? conversation.created
  };
}
var ConversationsAPI = class {
  constructor(client) {
    this._client = client;
  }
  async create(params = {}) {
    const result = await this._client.createConversation({
      title: params.title ?? null,
      visibility: params.visibility ?? "unlisted"
    });
    if (result.isErr()) {
      throw apiErrorToDustError(result.error);
    }
    return toConversationInfo(result.value.conversation);
  }
  async get(conversationId) {
    const result = await this._client.getConversation({ conversationId });
    if (result.isErr()) {
      throw apiErrorToDustError(result.error);
    }
    return toConversationInfo(result.value);
  }
  async list() {
    const result = await this._client.getConversations();
    if (result.isErr()) {
      throw apiErrorToDustError(result.error);
    }
    return result.value.map(toConversationInfo);
  }
};

// src/types.ts
import { z as z5 } from "zod";

// src/helpers.ts
import { z } from "zod";
var FlexibleEnumSchema = () => z.custom((val) => {
  return typeof val === "string";
});

// src/mcp_icon_types.ts
import { z as z2 } from "zod";
var MCPInternalActionIconSchema = z2.enum([
  "ActionBrainIcon",
  "ActionChatBubbleBottomCenterTextIcon",
  "ActionChatBubbleThoughtIcon",
  "ActionCloudArrowLeftRightIcon",
  "ActionDocumentTextIcon",
  "ActionEmotionLaughIcon",
  "ActionFrameIcon",
  "ActionGitBranchIcon",
  "ActionGlobeAltIcon",
  "ActionImageIcon",
  "ActionLightbulbIcon",
  "ActionListCheckIcon",
  "ActionLockIcon",
  "ActionMagnifyingGlassIcon",
  "ActionNoiseIcon",
  "ActionRobotIcon",
  "ActionScanIcon",
  "ActionSpeakIcon",
  "ActionTableIcon",
  "ActionTimeIcon",
  "AdomikLogo",
  "AmplitudeLogo",
  "ApifyLogo",
  "AsanaLogo",
  "AshbyLogo",
  "AttioLogo",
  "BigQueryLogo",
  "ToolsIcon",
  "CanvaLogo",
  "ClariLogo",
  "CommandLineIcon",
  "ConfluenceLogo",
  "ContentsquareLogo",
  "CostoryLogo",
  "DriveLogo",
  "FathomLogo",
  "FreshserviceLogo",
  "FrontLogo",
  "GammaLogo",
  "GcalLogo",
  "GithubLogo",
  "GitlabLogo",
  "GmailLogo",
  "GongLogo",
  "GoogleSpreadsheetLogo",
  "GranolaLogo",
  "GuruLogo",
  "HexLogo",
  "HubspotLogo",
  "IntercomLogo",
  "JiraLogo",
  "LemlistLogo",
  "LinearLogo",
  "LumaLogo",
  "MicrosoftExcelLogo",
  "MicrosoftLogo",
  "MicrosoftOutlookLogo",
  "MicrosoftTeamsLogo",
  "MiroLogo",
  "MondayLogo",
  "NaptaLogo",
  "NetSuiteLogo",
  "NotionLogo",
  "OpenaiLogo",
  "PowerBiLogo",
  "PraizLogo",
  "ProductboardLogo",
  "PuzzleIcon",
  "SalesforceLogo",
  "SemrushLogo",
  "SlackLogo",
  "SnowflakeLogo",
  "SalesloftLogo",
  "SlabLogo",
  "StatuspageLogo",
  "StripeLogo",
  "SupabaseLogo",
  "UkgLogo",
  "ValTownLogo",
  "VantaLogo",
  "ZendeskLogo"
]);
var MCPExternalActionIconSchema = z2.enum([
  "ActionArmchairIcon",
  "ActionArrowDownOnSquareIcon",
  "ActionArrowUpOnSquareIcon",
  "ActionAtomIcon",
  "ActionAttachmentIcon",
  "ActionBankIcon",
  "ActionBarcodeIcon",
  "ActionBeerIcon",
  "ActionBookOpenIcon",
  "ActionBracesIcon",
  "ActionBrainIcon",
  "ActionBriefcaseIcon",
  "ActionBuildingIcon",
  "ActionCalculatorIcon",
  "ActionCalendarCheckIcon",
  "ActionCalendarIcon",
  "ActionCameraIcon",
  "ActionCarIcon",
  "ActionCardIcon",
  "ActionChatBubbleBottomCenterTextIcon",
  "ActionChatBubbleThoughtIcon",
  "ActionCheckCircleIcon",
  "ActionClipboardIcon",
  "ActionCloudArrowDownIcon",
  "ActionCloudArrowLeftRightIcon",
  "ActionCloudArrowUpIcon",
  "ActionCodeBlockIcon",
  "ActionCodeBoxIcon",
  "ActionCommand1Icon",
  "ActionCommandIcon",
  "ActionCommunityIcon",
  "ActionCompanyIcon",
  "ActionCubeIcon",
  "ActionCupIcon",
  "ActionCustomerServiceIcon",
  "ActionDashboardIcon",
  "ActionDatabaseIcon",
  "ActionDocumentIcon",
  "ActionDocumentPileIcon",
  "ActionDocumentPlusIcon",
  "ActionDocumentTextIcon",
  "ActionDoubleQuotesIcon",
  "ActionEmotionLaughIcon",
  "ActionExternalLinkIcon",
  "ActionEyeIcon",
  "ActionEyeSlashIcon",
  "ActionFilmIcon",
  "ActionFilterIcon",
  "ActionIdentityIcon",
  "ActionFireIcon",
  "ActionFlagIcon",
  "ActionFlightLandIcon",
  "ActionFlightTakeoffIcon",
  "ActionFolderAddIcon",
  "ActionFolderIcon",
  "ActionFolderOpenIcon",
  "ActionFullscreenExitIcon",
  "ActionFullscreenIcon",
  "ActionGamepadIcon",
  "ActionGitBranchIcon",
  "ActionGitForkIcon",
  "ActionGlobeAltIcon",
  "ActionGlobeIcon",
  "ActionGraduationCapIcon",
  "ActionHandHeartIcon",
  "ActionHandThumbDownIcon",
  "ActionHandThumbUpIcon",
  "ActionHeartIcon",
  "ActionHomeIcon",
  "ActionHospitalIcon",
  "ActionImageIcon",
  "ActionInboxIcon",
  "ActionIncludeIcon",
  "ActionLayoutIcon",
  "ActionLightbulbIcon",
  "ActionListCheckIcon",
  "ActionListIcon",
  "ActionLockIcon",
  "ActionLogoutIcon",
  "ActionMagicIcon",
  "ActionMagnifyingGlassIcon",
  "ActionMailAiIcon",
  "ActionMailCloseIcon",
  "ActionMailIcon",
  "ActionMapIcon",
  "ActionMapPinIcon",
  "ActionMarkPenIcon",
  "ActionMedalIcon",
  "ActionMegaphoneIcon",
  "ActionMenuIcon",
  "ActionMicIcon",
  "ActionMoonIcon",
  "ActionMovieIcon",
  "ActionNoiseIcon",
  "ActionNumbersIcon",
  "ActionPaintIcon",
  "ActionPencilSquareIcon",
  "ActionPieChartIcon",
  "ActionPinDistanceIcon",
  "ActionPingPongIcon",
  "ActionPlanetIcon",
  "ActionPlusCircleIcon",
  "ActionPlusIcon",
  "ActionPrinterIcon",
  "ActionPushpinIcon",
  "ActionRainbowIcon",
  "ActionRobotIcon",
  "ActionRocketIcon",
  "ActionSafeIcon",
  "ActionSaveIcon",
  "ActionScalesIcon",
  "ActionScanIcon",
  "ActionSeedlingIcon",
  "ActionServerIcon",
  "ActionShakeHandsIcon",
  "ActionShipIcon",
  "ActionShirtIcon",
  "ActionShoppingBasketIcon",
  "ActionSlideshowIcon",
  "ActionSparklesIcon",
  "ActionSpeakIcon",
  "ActionSquare3Stack3DIcon",
  "ActionStopSignIcon",
  "ActionStoreIcon",
  "ActionSunIcon",
  "ActionSwordIcon",
  "ActionTShirtIcon",
  "ActionTableIcon",
  "ActionTagIcon",
  "ActionTestTubeIcon",
  "ActionTimeIcon",
  "ActionTrainIcon",
  "ActionTranslateIcon",
  "ActionTrashIcon",
  "ActionTrophyIcon",
  "ActionUmbrellaIcon",
  "ActionUserGroupIcon",
  "ActionUserIcon",
  "ActionVidiconIcon",
  "ActionVolumeUpIcon",
  "ActionXCircleIcon"
]);

// src/output_schemas.ts
import { z as z4 } from "zod";

// src/internal_mime_types.ts
function generateConnectorRelativeMimeTypes({
  provider,
  resourceTypes
}) {
  return resourceTypes.reduce(
    (acc, s) => ({
      ...acc,
      [s]: `application/vnd.dust.${provider.replace("_", "")}.${s.replace("_", "-").toLowerCase()}`
    }),
    {}
  );
}
var DATA_SOURCE_MIME_TYPE = "application/vnd.dust.datasource";
var DATA_WAREHOUSE_MIME_TYPE = "application/vnd.dust.data-warehouse";
var DATA_SOURCE_FOLDER_SPREADSHEET_MIME_TYPE = "application/vnd.dust.folder.spreadsheet";
var DUST_TABLE_MIME_TYPE = "application/vnd.dust.table";
var CONTENT_NODE_MIME_TYPES = {
  GENERIC: {
    DATA_SOURCE: DATA_SOURCE_MIME_TYPE,
    DATA_WAREHOUSE: DATA_WAREHOUSE_MIME_TYPE,
    TABLE: DUST_TABLE_MIME_TYPE
  },
  FOLDER: {
    SPREADSHEET: DATA_SOURCE_FOLDER_SPREADSHEET_MIME_TYPE
  },
  CONFLUENCE: generateConnectorRelativeMimeTypes({
    provider: "confluence",
    resourceTypes: ["FOLDER", "PAGE", "SPACE"]
  }),
  GITHUB: generateConnectorRelativeMimeTypes({
    provider: "github",
    resourceTypes: [
      "REPOSITORY",
      "CODE_ROOT",
      "CODE_DIRECTORY",
      "CODE_FILE",
      // ISSUES is the folder containing all issues.
      "ISSUES",
      // ISSUE is a single issue.
      "ISSUE",
      // DISCUSSIONS is the folder containing all discussions.
      "DISCUSSIONS",
      // DISCUSSION is a single discussion.
      "DISCUSSION"
    ]
  }),
  GOOGLE_DRIVE: generateConnectorRelativeMimeTypes({
    provider: "google_drive",
    // Spreadsheets may contain many sheets, thus resemble folders and are stored as such, but with
    // the special mimeType below.  For files and sheets, we keep Google's mime types.
    resourceTypes: ["SHARED_WITH_ME", "FOLDER", "SPREADSHEET"]
  }),
  INTERCOM: generateConnectorRelativeMimeTypes({
    provider: "intercom",
    resourceTypes: [
      "COLLECTION",
      "TEAMS_FOLDER",
      "CONVERSATION",
      "TEAM",
      "ARTICLE",
      "HELP_CENTER"
    ]
  }),
  MICROSOFT: generateConnectorRelativeMimeTypes({
    provider: "microsoft",
    // Spreadsheets may contain many sheets, thus resemble folders and are
    // stored as such, but with the special mimeType below.
    // For files and sheets, we keep Microsoft's mime types.
    resourceTypes: ["FOLDER", "SPREADSHEET"]
  }),
  NOTION: generateConnectorRelativeMimeTypes({
    provider: "notion",
    resourceTypes: ["UNKNOWN_FOLDER", "SYNCING_FOLDER", "DATABASE", "PAGE"]
  }),
  SLACK: generateConnectorRelativeMimeTypes({
    provider: "slack",
    resourceTypes: ["CHANNEL", "THREAD", "MESSAGES"]
  }),
  SNOWFLAKE: generateConnectorRelativeMimeTypes({
    provider: "snowflake",
    resourceTypes: ["DATABASE", "SCHEMA", "TABLE"]
  }),
  WEBCRAWLER: generateConnectorRelativeMimeTypes({
    provider: "webcrawler",
    resourceTypes: ["FOLDER"]
    // pages are upserted as text/html, not an internal mime type
  }),
  ZENDESK: generateConnectorRelativeMimeTypes({
    provider: "zendesk",
    resourceTypes: [
      "BRAND",
      "HELP_CENTER",
      "CATEGORY",
      "ARTICLE",
      // TICKETS is the folder containing all tickets.
      "TICKETS",
      // TICKET is a single ticket.
      "TICKET"
    ]
  }),
  BIGQUERY: generateConnectorRelativeMimeTypes({
    provider: "bigquery",
    resourceTypes: ["DATABASE", "SCHEMA", "TABLE"]
  }),
  SALESFORCE: generateConnectorRelativeMimeTypes({
    provider: "salesforce",
    resourceTypes: ["SYNCED_QUERY_FOLDER"]
  }),
  GONG: generateConnectorRelativeMimeTypes({
    provider: "gong",
    resourceTypes: ["TRANSCRIPT", "TRANSCRIPT_FOLDER"]
  }),
  DUST_PROJECT: generateConnectorRelativeMimeTypes({
    provider: "dust_project",
    resourceTypes: [
      "CONVERSATION_FOLDER",
      "CONVERSATION_MESSAGES",
      "CONTEXT_FOLDER",
      "MOUNT_FOLDER"
    ]
  })
};
var INCLUDABLE_INTERNAL_CONTENT_NODE_MIME_TYPES = {
  CONFLUENCE: [CONTENT_NODE_MIME_TYPES.CONFLUENCE.PAGE],
  GITHUB: [
    CONTENT_NODE_MIME_TYPES.GITHUB.ISSUE,
    CONTENT_NODE_MIME_TYPES.GITHUB.DISCUSSION,
    CONTENT_NODE_MIME_TYPES.GITHUB.CODE_FILE
  ],
  GOOGLE_DRIVE: [],
  INTERCOM: [
    CONTENT_NODE_MIME_TYPES.INTERCOM.CONVERSATION,
    CONTENT_NODE_MIME_TYPES.INTERCOM.ARTICLE
  ],
  MICROSOFT: [],
  NOTION: [CONTENT_NODE_MIME_TYPES.NOTION.PAGE],
  SLACK: [
    CONTENT_NODE_MIME_TYPES.SLACK.THREAD,
    CONTENT_NODE_MIME_TYPES.SLACK.MESSAGES
  ],
  SNOWFLAKE: [],
  WEBCRAWLER: [],
  ZENDESK: [
    CONTENT_NODE_MIME_TYPES.ZENDESK.TICKET,
    CONTENT_NODE_MIME_TYPES.ZENDESK.ARTICLE
  ],
  BIGQUERY: [],
  SALESFORCE: [],
  GONG: [],
  DUST_PROJECT: [
    CONTENT_NODE_MIME_TYPES.DUST_PROJECT.CONVERSATION_MESSAGES,
    CONTENT_NODE_MIME_TYPES.DUST_PROJECT.CONTEXT_FOLDER
  ]
};
function generateToolMimeTypes({
  category,
  resourceTypes
}) {
  return resourceTypes.reduce(
    (acc, s) => ({
      ...acc,
      [s]: `application/vnd.dust.${category.replace(/_/g, "-").toLowerCase()}.${s.replace(/_/g, "-").toLowerCase()}`
    }),
    {}
  );
}
var TOOL_MIME_TYPES = {
  TOOL_INPUT: generateToolMimeTypes({
    category: "TOOL_INPUT",
    resourceTypes: [
      "DATA_SOURCE",
      "DATA_WAREHOUSE",
      "TABLE",
      "AGENT",
      "STRING",
      "NUMBER",
      "BOOLEAN",
      "ENUM",
      "LIST",
      "DUST_APP",
      "TIME_FRAME",
      "JSON_SCHEMA",
      "SECRET",
      "DUST_POD"
    ]
  }),
  TOOL_OUTPUT: generateToolMimeTypes({
    category: "TOOL_OUTPUT",
    resourceTypes: [
      "AGENT_PAUSE_TOOL_OUTPUT",
      "BROWSE_RESULT",
      "DATA_SOURCE_SEARCH_RESULT",
      "FILESYSTEM_PATH",
      "DATA_SOURCE_NODE_LIST",
      "DATA_SOURCE_NODE_CONTENT",
      "DATA_SOURCE_INCLUDE_RESULT",
      "ENABLE_SKILL_RESULT",
      "EXTRACT_QUERY",
      "EXTRACT_RESULT",
      // File generated by the tool.
      "FILE",
      // File path only, no FileResource in DB.
      "FILE_PATH",
      // Image returned by the `files__cat` tool for vision-capable models.
      "MODEL_VISION_IMAGE",
      // Final output of the reasoning when successful with the non-CoT tokens.
      "REASONING_SUCCESS",
      // Content of a SQL query formulated by the model.
      "SQL_QUERY",
      // Error when executing a query.
      "EXECUTE_TABLES_QUERY_ERROR",
      // Generic thinking tokens.
      "THINKING",
      "DATABASE_SCHEMA",
      "QUERY_WRITING_INSTRUCTIONS",
      "EXAMPLE_ROWS",
      "TOOL_MARKER",
      "WEBSEARCH_QUERY",
      "WEBSEARCH_RESULT",
      "RUN_AGENT_RESULT",
      "RUN_AGENT_HANDOVER",
      "RUN_AGENT_QUERY",
      "WARNING",
      "SKILL_AUTHORING_RESULT",
      "TOOLSET_LIST_RESULT",
      "TOOLSET_DESCRIBE_RESULT",
      // Legacy, kept for backwards compatibility.
      "GET_DATABASE_SCHEMA_MARKER",
      "EXECUTE_TABLES_QUERY_MARKER"
    ]
  })
};
var INTERNAL_MIME_TYPES = {
  ...CONTENT_NODE_MIME_TYPES,
  ...TOOL_MIME_TYPES
};
var INTERNAL_MIME_TYPES_VALUES = Object.values(
  CONTENT_NODE_MIME_TYPES
).flatMap((value) => Object.values(value).map((v) => v));
var INCLUDABLE_INTERNAL_MIME_TYPES_VALUES = Object.values(
  INCLUDABLE_INTERNAL_CONTENT_NODE_MIME_TYPES
).flatMap((value) => Object.values(value).map((v) => v));
function isDustMimeType(mimeType) {
  return INTERNAL_MIME_TYPES_VALUES.includes(mimeType);
}
function isIncludableInternalMimeType(mimeType) {
  return INCLUDABLE_INTERNAL_MIME_TYPES_VALUES.includes(mimeType);
}

// src/raw_mcp_types.ts
import { z as z3 } from "zod";
var JSONRPC_VERSION = "2.0";
var ProgressTokenSchema = z3.union([z3.string(), z3.number().int()]);
var CursorSchema = z3.string();
var RequestMetaSchema = z3.object({
  /**
   * If specified, the caller is requesting out-of-band progress notifications for this request (as represented by notifications/progress). The value of this parameter is an opaque token that will be attached to any subsequent notifications. The receiver is not obligated to provide these notifications.
   */
  progressToken: z3.optional(ProgressTokenSchema)
}).passthrough();
var BaseRequestParamsSchema = z3.object({
  _meta: z3.optional(RequestMetaSchema)
}).passthrough();
var RequestSchema = z3.object({
  method: z3.string(),
  params: z3.optional(BaseRequestParamsSchema)
});
var BaseNotificationParamsSchema = z3.object({
  /**
   * See [MCP specification](https://github.com/modelcontextprotocol/modelcontextprotocol/blob/47339c03c143bb4ec01a26e721a1b8fe66634ebe/docs/specification/draft/basic/index.mdx#general-fields)
   * for notes on _meta usage.
   */
  _meta: z3.optional(z3.object({}).passthrough())
}).passthrough();
var NotificationSchema = z3.object({
  method: z3.string(),
  params: z3.optional(BaseNotificationParamsSchema)
});
var ResultSchema = z3.object({
  /**
   * See [MCP specification](https://github.com/modelcontextprotocol/modelcontextprotocol/blob/47339c03c143bb4ec01a26e721a1b8fe66634ebe/docs/specification/draft/basic/index.mdx#general-fields)
   * for notes on _meta usage.
   */
  _meta: z3.optional(z3.object({}).passthrough())
}).passthrough();
var RequestIdSchema = z3.union([z3.string(), z3.number().int()]);
var JSONRPCRequestSchema = z3.object({
  jsonrpc: z3.literal(JSONRPC_VERSION),
  id: RequestIdSchema
}).merge(RequestSchema).strict();
var JSONRPCNotificationSchema = z3.object({
  jsonrpc: z3.literal(JSONRPC_VERSION)
}).merge(NotificationSchema).strict();
var JSONRPCResponseSchema = z3.object({
  jsonrpc: z3.literal(JSONRPC_VERSION),
  id: RequestIdSchema,
  result: ResultSchema
}).strict();
var JSONRPCErrorSchema = z3.object({
  jsonrpc: z3.literal(JSONRPC_VERSION),
  id: RequestIdSchema,
  error: z3.object({
    /**
     * The error type that occurred.
     */
    code: z3.number().int(),
    /**
     * A short description of the error. The message SHOULD be limited to a concise single sentence.
     */
    message: z3.string(),
    /**
     * Additional information about the error. The value of this member is defined by the sender (e.g. detailed error information, nested errors etc.).
     */
    data: z3.optional(z3.unknown())
  })
}).strict();
var JSONRPCMessageSchema = z3.union([
  JSONRPCRequestSchema,
  JSONRPCNotificationSchema,
  JSONRPCResponseSchema,
  JSONRPCErrorSchema
]);
var EmptyResultSchema = ResultSchema.strict();
var CancelledNotificationSchema = NotificationSchema.extend({
  method: z3.literal("notifications/cancelled"),
  params: BaseNotificationParamsSchema.extend({
    /**
     * The ID of the request to cancel.
     *
     * This MUST correspond to the ID of a request previously issued in the same direction.
     */
    requestId: RequestIdSchema,
    /**
     * An optional string describing the reason for the cancellation. This MAY be logged or presented to the user.
     */
    reason: z3.string().optional()
  })
});
var BaseMetadataSchema = z3.object({
  /** Intended for programmatic or logical use, but used as a display name in past specs or fallback */
  name: z3.string(),
  /**
   * Intended for UI and end-user contexts — optimized to be human-readable and easily understood,
   * even by those unfamiliar with domain-specific terminology.
   *
   * If not provided, the name should be used for display (except for Tool,
   * where `annotations.title` should be given precedence over using `name`,
   * if present).
   */
  title: z3.optional(z3.string())
}).passthrough();
var ImplementationSchema = BaseMetadataSchema.extend({
  version: z3.string()
});
var ClientCapabilitiesSchema = z3.object({
  /**
   * Experimental, non-standard capabilities that the client supports.
   */
  experimental: z3.optional(z3.object({}).passthrough()),
  /**
   * Present if the client supports sampling from an LLM.
   */
  sampling: z3.optional(z3.object({}).passthrough()),
  /**
   * Present if the client supports eliciting user input.
   */
  elicitation: z3.optional(z3.object({}).passthrough()),
  /**
   * Present if the client supports listing roots.
   */
  roots: z3.optional(
    z3.object({
      /**
       * Whether the client supports issuing notifications for changes to the roots list.
       */
      listChanged: z3.optional(z3.boolean())
    }).passthrough()
  )
}).passthrough();
var InitializeRequestSchema = RequestSchema.extend({
  method: z3.literal("initialize"),
  params: BaseRequestParamsSchema.extend({
    /**
     * The latest version of the Model Context Protocol that the client supports. The client MAY decide to support older versions as well.
     */
    protocolVersion: z3.string(),
    capabilities: ClientCapabilitiesSchema,
    clientInfo: ImplementationSchema
  })
});
var ServerCapabilitiesSchema = z3.object({
  /**
   * Experimental, non-standard capabilities that the server supports.
   */
  experimental: z3.optional(z3.object({}).passthrough()),
  /**
   * Present if the server supports sending log messages to the client.
   */
  logging: z3.optional(z3.object({}).passthrough()),
  /**
   * Present if the server supports sending completions to the client.
   */
  completions: z3.optional(z3.object({}).passthrough()),
  /**
   * Present if the server offers any prompt templates.
   */
  prompts: z3.optional(
    z3.object({
      /**
       * Whether this server supports issuing notifications for changes to the prompt list.
       */
      listChanged: z3.optional(z3.boolean())
    }).passthrough()
  ),
  /**
   * Present if the server offers any resources to read.
   */
  resources: z3.optional(
    z3.object({
      /**
       * Whether this server supports clients subscribing to resource updates.
       */
      subscribe: z3.optional(z3.boolean()),
      /**
       * Whether this server supports issuing notifications for changes to the resource list.
       */
      listChanged: z3.optional(z3.boolean())
    }).passthrough()
  ),
  /**
   * Present if the server offers any tools to call.
   */
  tools: z3.optional(
    z3.object({
      /**
       * Whether this server supports issuing notifications for changes to the tool list.
       */
      listChanged: z3.optional(z3.boolean())
    }).passthrough()
  )
}).passthrough();
var InitializeResultSchema = ResultSchema.extend({
  /**
   * The version of the Model Context Protocol that the server wants to use. This may not match the version that the client requested. If the client cannot support this version, it MUST disconnect.
   */
  protocolVersion: z3.string(),
  capabilities: ServerCapabilitiesSchema,
  serverInfo: ImplementationSchema,
  /**
   * Instructions describing how to use the server and its features.
   *
   * This can be used by clients to improve the LLM's understanding of available tools, resources, etc. It can be thought of like a "hint" to the model. For example, this information MAY be added to the system prompt.
   */
  instructions: z3.optional(z3.string())
});
var InitializedNotificationSchema = NotificationSchema.extend({
  method: z3.literal("notifications/initialized")
});
var PingRequestSchema = RequestSchema.extend({
  method: z3.literal("ping")
});
var ProgressSchema = z3.object({
  /**
   * The progress thus far. This should increase every time progress is made, even if the total is unknown.
   */
  progress: z3.number(),
  /**
   * Total number of items to process (or total progress required), if known.
   */
  total: z3.optional(z3.number()),
  /**
   * An optional message describing the current progress.
   */
  message: z3.optional(z3.string())
}).passthrough();
var ProgressNotificationSchema = NotificationSchema.extend({
  method: z3.literal("notifications/progress"),
  params: BaseNotificationParamsSchema.merge(ProgressSchema).extend({
    /**
     * The progress token which was given in the initial request, used to associate this notification with the request that is proceeding.
     */
    progressToken: ProgressTokenSchema
  })
});
var PaginatedRequestSchema = RequestSchema.extend({
  params: BaseRequestParamsSchema.extend({
    /**
     * An opaque token representing the current pagination position.
     * If provided, the server should return results starting after this cursor.
     */
    cursor: z3.optional(CursorSchema)
  }).optional()
});
var PaginatedResultSchema = ResultSchema.extend({
  /**
   * An opaque token representing the pagination position after the last returned result.
   * If present, there may be more results available.
   */
  nextCursor: z3.optional(CursorSchema)
});
var ResourceContentsSchema = z3.object({
  /**
   * The URI of this resource.
   */
  uri: z3.string(),
  /**
   * The MIME type of this resource, if known.
   */
  mimeType: z3.optional(z3.string()),
  /**
   * See [MCP specification](https://github.com/modelcontextprotocol/modelcontextprotocol/blob/47339c03c143bb4ec01a26e721a1b8fe66634ebe/docs/specification/draft/basic/index.mdx#general-fields)
   * for notes on _meta usage.
   */
  _meta: z3.optional(z3.object({}).passthrough())
}).passthrough();
var TextResourceContentsSchema = ResourceContentsSchema.extend({
  /**
   * The text of the item. This must only be set if the item can actually be represented as text (not binary data).
   */
  text: z3.string()
});
var Base64Schema = z3.string().refine(
  (val) => {
    try {
      atob(val);
      return true;
    } catch {
      return false;
    }
  },
  { message: "Invalid Base64 string" }
);
var BlobResourceContentsSchema = ResourceContentsSchema.extend({
  /**
   * A base64-encoded string representing the binary data of the item.
   */
  blob: Base64Schema
});
var ResourceSchema = BaseMetadataSchema.extend({
  /**
   * The URI of this resource.
   */
  uri: z3.string(),
  /**
   * A description of what this resource represents.
   *
   * This can be used by clients to improve the LLM's understanding of available resources. It can be thought of like a "hint" to the model.
   */
  description: z3.optional(z3.string()),
  /**
   * The MIME type of this resource, if known.
   */
  mimeType: z3.optional(z3.string()),
  /**
   * See [MCP specification](https://github.com/modelcontextprotocol/modelcontextprotocol/blob/47339c03c143bb4ec01a26e721a1b8fe66634ebe/docs/specification/draft/basic/index.mdx#general-fields)
   * for notes on _meta usage.
   */
  _meta: z3.optional(z3.object({}).passthrough())
});
var ResourceTemplateSchema = BaseMetadataSchema.extend({
  /**
   * A URI template (according to RFC 6570) that can be used to construct resource URIs.
   */
  uriTemplate: z3.string(),
  /**
   * A description of what this template is for.
   *
   * This can be used by clients to improve the LLM's understanding of available resources. It can be thought of like a "hint" to the model.
   */
  description: z3.optional(z3.string()),
  /**
   * The MIME type for all resources that match this template. This should only be included if all resources matching this template have the same type.
   */
  mimeType: z3.optional(z3.string()),
  /**
   * See [MCP specification](https://github.com/modelcontextprotocol/modelcontextprotocol/blob/47339c03c143bb4ec01a26e721a1b8fe66634ebe/docs/specification/draft/basic/index.mdx#general-fields)
   * for notes on _meta usage.
   */
  _meta: z3.optional(z3.object({}).passthrough())
});
var ListResourcesRequestSchema = PaginatedRequestSchema.extend({
  method: z3.literal("resources/list")
});
var ListResourcesResultSchema = PaginatedResultSchema.extend({
  resources: z3.array(ResourceSchema)
});
var ListResourceTemplatesRequestSchema = PaginatedRequestSchema.extend(
  {
    method: z3.literal("resources/templates/list")
  }
);
var ListResourceTemplatesResultSchema = PaginatedResultSchema.extend({
  resourceTemplates: z3.array(ResourceTemplateSchema)
});
var ReadResourceRequestSchema = RequestSchema.extend({
  method: z3.literal("resources/read"),
  params: BaseRequestParamsSchema.extend({
    /**
     * The URI of the resource to read. The URI can use any protocol; it is up to the server how to interpret it.
     */
    uri: z3.string()
  })
});
var ReadResourceResultSchema = ResultSchema.extend({
  contents: z3.array(
    z3.union([TextResourceContentsSchema, BlobResourceContentsSchema])
  )
});
var ResourceListChangedNotificationSchema = NotificationSchema.extend({
  method: z3.literal("notifications/resources/list_changed")
});
var SubscribeRequestSchema = RequestSchema.extend({
  method: z3.literal("resources/subscribe"),
  params: BaseRequestParamsSchema.extend({
    /**
     * The URI of the resource to subscribe to. The URI can use any protocol; it is up to the server how to interpret it.
     */
    uri: z3.string()
  })
});
var UnsubscribeRequestSchema = RequestSchema.extend({
  method: z3.literal("resources/unsubscribe"),
  params: BaseRequestParamsSchema.extend({
    /**
     * The URI of the resource to unsubscribe from.
     */
    uri: z3.string()
  })
});
var ResourceUpdatedNotificationSchema = NotificationSchema.extend({
  method: z3.literal("notifications/resources/updated"),
  params: BaseNotificationParamsSchema.extend({
    /**
     * The URI of the resource that has been updated. This might be a sub-resource of the one that the client actually subscribed to.
     */
    uri: z3.string()
  })
});
var PromptArgumentSchema = z3.object({
  /**
   * The name of the argument.
   */
  name: z3.string(),
  /**
   * A human-readable description of the argument.
   */
  description: z3.optional(z3.string()),
  /**
   * Whether this argument must be provided.
   */
  required: z3.optional(z3.boolean())
}).passthrough();
var PromptSchema = BaseMetadataSchema.extend({
  /**
   * An optional description of what this prompt provides
   */
  description: z3.optional(z3.string()),
  /**
   * A list of arguments to use for templating the prompt.
   */
  arguments: z3.optional(z3.array(PromptArgumentSchema)),
  /**
   * See [MCP specification](https://github.com/modelcontextprotocol/modelcontextprotocol/blob/47339c03c143bb4ec01a26e721a1b8fe66634ebe/docs/specification/draft/basic/index.mdx#general-fields)
   * for notes on _meta usage.
   */
  _meta: z3.optional(z3.object({}).passthrough())
});
var ListPromptsRequestSchema = PaginatedRequestSchema.extend({
  method: z3.literal("prompts/list")
});
var ListPromptsResultSchema = PaginatedResultSchema.extend({
  prompts: z3.array(PromptSchema)
});
var GetPromptRequestSchema = RequestSchema.extend({
  method: z3.literal("prompts/get"),
  params: BaseRequestParamsSchema.extend({
    /**
     * The name of the prompt or prompt template.
     */
    name: z3.string(),
    /**
     * Arguments to use for templating the prompt.
     */
    arguments: z3.optional(z3.record(z3.string()))
  })
});
var TextContentSchema = z3.object({
  type: z3.literal("text"),
  /**
   * The text content of the message.
   */
  text: z3.string(),
  /**
   * See [MCP specification](https://github.com/modelcontextprotocol/modelcontextprotocol/blob/47339c03c143bb4ec01a26e721a1b8fe66634ebe/docs/specification/draft/basic/index.mdx#general-fields)
   * for notes on _meta usage.
   */
  _meta: z3.optional(z3.object({}).passthrough())
}).passthrough();
var ImageContentSchema = z3.object({
  type: z3.literal("image"),
  /**
   * The base64-encoded image data.
   */
  data: Base64Schema,
  /**
   * The MIME type of the image. Different providers may support different image types.
   */
  mimeType: z3.string(),
  /**
   * See [MCP specification](https://github.com/modelcontextprotocol/modelcontextprotocol/blob/47339c03c143bb4ec01a26e721a1b8fe66634ebe/docs/specification/draft/basic/index.mdx#general-fields)
   * for notes on _meta usage.
   */
  _meta: z3.optional(z3.object({}).passthrough())
}).passthrough();
var AudioContentSchema = z3.object({
  type: z3.literal("audio"),
  /**
   * The base64-encoded audio data.
   */
  data: Base64Schema,
  /**
   * The MIME type of the audio. Different providers may support different audio types.
   */
  mimeType: z3.string(),
  /**
   * See [MCP specification](https://github.com/modelcontextprotocol/modelcontextprotocol/blob/47339c03c143bb4ec01a26e721a1b8fe66634ebe/docs/specification/draft/basic/index.mdx#general-fields)
   * for notes on _meta usage.
   */
  _meta: z3.optional(z3.object({}).passthrough())
}).passthrough();
var EmbeddedResourceSchema = z3.object({
  type: z3.literal("resource"),
  resource: z3.union([TextResourceContentsSchema, BlobResourceContentsSchema]),
  /**
   * See [MCP specification](https://github.com/modelcontextprotocol/modelcontextprotocol/blob/47339c03c143bb4ec01a26e721a1b8fe66634ebe/docs/specification/draft/basic/index.mdx#general-fields)
   * for notes on _meta usage.
   */
  _meta: z3.optional(z3.object({}).passthrough())
}).passthrough();
var ResourceLinkSchema = ResourceSchema.extend({
  type: z3.literal("resource_link")
});
var ContentBlockSchema = z3.union([
  TextContentSchema,
  ImageContentSchema,
  AudioContentSchema,
  ResourceLinkSchema,
  EmbeddedResourceSchema
]);
var PromptMessageSchema = z3.object({
  role: z3.enum(["user", "assistant"]),
  content: ContentBlockSchema
}).passthrough();
var GetPromptResultSchema = ResultSchema.extend({
  /**
   * An optional description for the prompt.
   */
  description: z3.optional(z3.string()),
  messages: z3.array(PromptMessageSchema)
});
var PromptListChangedNotificationSchema = NotificationSchema.extend({
  method: z3.literal("notifications/prompts/list_changed")
});
var ToolAnnotationsSchema = z3.object({
  /**
   * A human-readable title for the tool.
   */
  title: z3.optional(z3.string()),
  /**
   * If true, the tool does not modify its environment.
   *
   * Default: false
   */
  readOnlyHint: z3.optional(z3.boolean()),
  /**
   * If true, the tool may perform destructive updates to its environment.
   * If false, the tool performs only additive updates.
   *
   * (This property is meaningful only when `readOnlyHint == false`)
   *
   * Default: true
   */
  destructiveHint: z3.optional(z3.boolean()),
  /**
   * If true, calling the tool repeatedly with the same arguments
   * will have no additional effect on the its environment.
   *
   * (This property is meaningful only when `readOnlyHint == false`)
   *
   * Default: false
   */
  idempotentHint: z3.optional(z3.boolean()),
  /**
   * If true, this tool may interact with an "open world" of external
   * entities. If false, the tool's domain of interaction is closed.
   * For example, the world of a web search tool is open, whereas that
   * of a memory tool is not.
   *
   * Default: true
   */
  openWorldHint: z3.optional(z3.boolean())
}).passthrough();
var ToolSchema = BaseMetadataSchema.extend({
  /**
   * A human-readable description of the tool.
   */
  description: z3.optional(z3.string()),
  /**
   * A JSON Schema object defining the expected parameters for the tool.
   */
  inputSchema: z3.object({
    type: z3.literal("object"),
    properties: z3.optional(z3.object({}).passthrough()),
    required: z3.optional(z3.array(z3.string()))
  }).passthrough(),
  /**
   * An optional JSON Schema object defining the structure of the tool's output returned in
   * the structuredContent field of a CallToolResult.
   */
  outputSchema: z3.optional(
    z3.object({
      type: z3.literal("object"),
      properties: z3.optional(z3.object({}).passthrough()),
      required: z3.optional(z3.array(z3.string()))
    }).passthrough()
  ),
  /**
   * Optional additional tool information.
   */
  annotations: z3.optional(ToolAnnotationsSchema),
  /**
   * See [MCP specification](https://github.com/modelcontextprotocol/modelcontextprotocol/blob/47339c03c143bb4ec01a26e721a1b8fe66634ebe/docs/specification/draft/basic/index.mdx#general-fields)
   * for notes on _meta usage.
   */
  _meta: z3.optional(z3.object({}).passthrough())
});
var ListToolsRequestSchema = PaginatedRequestSchema.extend({
  method: z3.literal("tools/list")
});
var ListToolsResultSchema = PaginatedResultSchema.extend({
  tools: z3.array(ToolSchema)
});
var CallToolResultSchema = ResultSchema.extend({
  /**
   * A list of content objects that represent the result of the tool call.
   *
   * If the Tool does not define an outputSchema, this field MUST be present in the result.
   * For backwards compatibility, this field is always present, but it may be empty.
   */
  content: z3.array(ContentBlockSchema).default([]),
  /**
   * An object containing structured tool output.
   *
   * If the Tool defines an outputSchema, this field MUST be present in the result, and contain a JSON object that matches the schema.
   */
  structuredContent: z3.object({}).passthrough().optional(),
  /**
   * Whether the tool call ended in an error.
   *
   * If not set, this is assumed to be false (the call was successful).
   *
   * Any errors that originate from the tool SHOULD be reported inside the result
   * object, with `isError` set to true, _not_ as an MCP protocol-level error
   * response. Otherwise, the LLM would not be able to see that an error occurred
   * and self-correct.
   *
   * However, any errors in _finding_ the tool, an error indicating that the
   * server does not support tool calls, or any other exceptional conditions,
   * should be reported as an MCP error response.
   */
  isError: z3.optional(z3.boolean())
});
var CompatibilityCallToolResultSchema = CallToolResultSchema.or(
  ResultSchema.extend({
    toolResult: z3.unknown()
  })
);
var CallToolRequestSchema = RequestSchema.extend({
  method: z3.literal("tools/call"),
  params: BaseRequestParamsSchema.extend({
    name: z3.string(),
    arguments: z3.optional(z3.record(z3.unknown()))
  })
});
var ToolListChangedNotificationSchema = NotificationSchema.extend({
  method: z3.literal("notifications/tools/list_changed")
});
var LoggingLevelSchema = z3.enum([
  "debug",
  "info",
  "notice",
  "warning",
  "error",
  "critical",
  "alert",
  "emergency"
]);
var SetLevelRequestSchema = RequestSchema.extend({
  method: z3.literal("logging/setLevel"),
  params: BaseRequestParamsSchema.extend({
    /**
     * The level of logging that the client wants to receive from the server. The server should send all logs at this level and higher (i.e., more severe) to the client as notifications/logging/message.
     */
    level: LoggingLevelSchema
  })
});
var LoggingMessageNotificationSchema = NotificationSchema.extend({
  method: z3.literal("notifications/message"),
  params: BaseNotificationParamsSchema.extend({
    /**
     * The severity of this log message.
     */
    level: LoggingLevelSchema,
    /**
     * An optional name of the logger issuing this message.
     */
    logger: z3.optional(z3.string()),
    /**
     * The data to be logged, such as a string message or an object. Any JSON serializable type is allowed here.
     */
    data: z3.unknown()
  })
});
var ModelHintSchema = z3.object({
  /**
   * A hint for a model name.
   */
  name: z3.string().optional()
}).passthrough();
var ModelPreferencesSchema = z3.object({
  /**
   * Optional hints to use for model selection.
   */
  hints: z3.optional(z3.array(ModelHintSchema)),
  /**
   * How much to prioritize cost when selecting a model.
   */
  costPriority: z3.optional(z3.number().min(0).max(1)),
  /**
   * How much to prioritize sampling speed (latency) when selecting a model.
   */
  speedPriority: z3.optional(z3.number().min(0).max(1)),
  /**
   * How much to prioritize intelligence and capabilities when selecting a model.
   */
  intelligencePriority: z3.optional(z3.number().min(0).max(1))
}).passthrough();
var SamplingMessageSchema = z3.object({
  role: z3.enum(["user", "assistant"]),
  content: z3.union([
    TextContentSchema,
    ImageContentSchema,
    AudioContentSchema
  ])
}).passthrough();
var CreateMessageRequestSchema = RequestSchema.extend({
  method: z3.literal("sampling/createMessage"),
  params: BaseRequestParamsSchema.extend({
    messages: z3.array(SamplingMessageSchema),
    /**
     * An optional system prompt the server wants to use for sampling. The client MAY modify or omit this prompt.
     */
    systemPrompt: z3.optional(z3.string()),
    /**
     * A request to include context from one or more MCP servers (including the caller), to be attached to the prompt. The client MAY ignore this request.
     */
    includeContext: z3.optional(z3.enum(["none", "thisServer", "allServers"])),
    temperature: z3.optional(z3.number()),
    /**
     * The maximum number of tokens to sample, as requested by the server. The client MAY choose to sample fewer tokens than requested.
     */
    maxTokens: z3.number().int(),
    stopSequences: z3.optional(z3.array(z3.string())),
    /**
     * Optional metadata to pass through to the LLM provider. The format of this metadata is provider-specific.
     */
    metadata: z3.optional(z3.object({}).passthrough()),
    /**
     * The server's preferences for which model to select.
     */
    modelPreferences: z3.optional(ModelPreferencesSchema)
  })
});
var CreateMessageResultSchema = ResultSchema.extend({
  /**
   * The name of the model that generated the message.
   */
  model: z3.string(),
  /**
   * The reason why sampling stopped.
   */
  stopReason: z3.optional(
    z3.enum(["endTurn", "stopSequence", "maxTokens"]).or(z3.string())
  ),
  role: z3.enum(["user", "assistant"]),
  content: z3.discriminatedUnion("type", [
    TextContentSchema,
    ImageContentSchema,
    AudioContentSchema
  ])
});
var BooleanSchemaSchema = z3.object({
  type: z3.literal("boolean"),
  title: z3.optional(z3.string()),
  description: z3.optional(z3.string()),
  default: z3.optional(z3.boolean())
}).passthrough();
var StringSchemaSchema = z3.object({
  type: z3.literal("string"),
  title: z3.optional(z3.string()),
  description: z3.optional(z3.string()),
  minLength: z3.optional(z3.number()),
  maxLength: z3.optional(z3.number()),
  format: z3.optional(z3.enum(["email", "uri", "date", "date-time"]))
}).passthrough();
var NumberSchemaSchema = z3.object({
  type: z3.enum(["number", "integer"]),
  title: z3.optional(z3.string()),
  description: z3.optional(z3.string()),
  minimum: z3.optional(z3.number()),
  maximum: z3.optional(z3.number())
}).passthrough();
var EnumSchemaSchema = z3.object({
  type: z3.literal("string"),
  title: z3.optional(z3.string()),
  description: z3.optional(z3.string()),
  enum: z3.array(z3.string()),
  enumNames: z3.optional(z3.array(z3.string()))
}).passthrough();
var PrimitiveSchemaDefinitionSchema = z3.union([
  BooleanSchemaSchema,
  StringSchemaSchema,
  NumberSchemaSchema,
  EnumSchemaSchema
]);
var ElicitRequestSchema = RequestSchema.extend({
  method: z3.literal("elicitation/create"),
  params: BaseRequestParamsSchema.extend({
    /**
     * The message to present to the user.
     */
    message: z3.string(),
    /**
     * The schema for the requested user input.
     */
    requestedSchema: z3.object({
      type: z3.literal("object"),
      properties: z3.record(z3.string(), PrimitiveSchemaDefinitionSchema),
      required: z3.optional(z3.array(z3.string()))
    }).passthrough()
  })
});
var ElicitResultSchema = ResultSchema.extend({
  /**
   * The user's response action.
   */
  action: z3.enum(["accept", "decline", "cancel"]),
  /**
   * The collected user input content (only present if action is "accept").
   */
  content: z3.optional(z3.record(z3.string(), z3.unknown()))
});
var ResourceTemplateReferenceSchema = z3.object({
  type: z3.literal("ref/resource"),
  /**
   * The URI or URI template of the resource.
   */
  uri: z3.string()
}).passthrough();
var PromptReferenceSchema = z3.object({
  type: z3.literal("ref/prompt"),
  /**
   * The name of the prompt or prompt template
   */
  name: z3.string()
}).passthrough();
var CompleteRequestSchema = RequestSchema.extend({
  method: z3.literal("completion/complete"),
  params: BaseRequestParamsSchema.extend({
    ref: z3.union([PromptReferenceSchema, ResourceTemplateReferenceSchema]),
    /**
     * The argument's information
     */
    argument: z3.object({
      /**
       * The name of the argument
       */
      name: z3.string(),
      /**
       * The value of the argument to use for completion matching.
       */
      value: z3.string()
    }).passthrough(),
    context: z3.optional(
      z3.object({
        /**
         * Previously-resolved variables in a URI template or prompt.
         */
        arguments: z3.optional(z3.record(z3.string(), z3.string()))
      })
    )
  })
});
var CompleteResultSchema = ResultSchema.extend({
  completion: z3.object({
    /**
     * An array of completion values. Must not exceed 100 items.
     */
    values: z3.array(z3.string()).max(100),
    /**
     * The total number of completion options available. This can exceed the number of values actually sent in the response.
     */
    total: z3.optional(z3.number().int()),
    /**
     * Indicates whether there are additional completion options beyond those provided in the current response, even if the exact total is unknown.
     */
    hasMore: z3.optional(z3.boolean())
  }).passthrough()
});
var RootSchema = z3.object({
  /**
   * The URI identifying the root. This *must* start with file:// for now.
   */
  uri: z3.string().startsWith("file://"),
  /**
   * An optional name for the root.
   */
  name: z3.optional(z3.string()),
  /**
   * See [MCP specification](https://github.com/modelcontextprotocol/modelcontextprotocol/blob/47339c03c143bb4ec01a26e721a1b8fe66634ebe/docs/specification/draft/basic/index.mdx#general-fields)
   * for notes on _meta usage.
   */
  _meta: z3.optional(z3.object({}).passthrough())
}).passthrough();
var ListRootsRequestSchema = RequestSchema.extend({
  method: z3.literal("roots/list")
});
var ListRootsResultSchema = ResultSchema.extend({
  roots: z3.array(RootSchema)
});
var RootsListChangedNotificationSchema = NotificationSchema.extend({
  method: z3.literal("notifications/roots/list_changed")
});
var ClientRequestSchema = z3.union([
  PingRequestSchema,
  InitializeRequestSchema,
  CompleteRequestSchema,
  SetLevelRequestSchema,
  GetPromptRequestSchema,
  ListPromptsRequestSchema,
  ListResourcesRequestSchema,
  ListResourceTemplatesRequestSchema,
  ReadResourceRequestSchema,
  SubscribeRequestSchema,
  UnsubscribeRequestSchema,
  CallToolRequestSchema,
  ListToolsRequestSchema
]);
var ClientNotificationSchema = z3.union([
  CancelledNotificationSchema,
  ProgressNotificationSchema,
  InitializedNotificationSchema,
  RootsListChangedNotificationSchema
]);
var ClientResultSchema = z3.union([
  EmptyResultSchema,
  CreateMessageResultSchema,
  ElicitResultSchema,
  ListRootsResultSchema
]);
var ServerRequestSchema = z3.union([
  PingRequestSchema,
  CreateMessageRequestSchema,
  ElicitRequestSchema,
  ListRootsRequestSchema
]);
var ServerNotificationSchema = z3.union([
  CancelledNotificationSchema,
  ProgressNotificationSchema,
  LoggingMessageNotificationSchema,
  ResourceUpdatedNotificationSchema,
  ResourceListChangedNotificationSchema,
  ToolListChangedNotificationSchema,
  PromptListChangedNotificationSchema
]);
var ServerResultSchema = z3.union([
  EmptyResultSchema,
  InitializeResultSchema,
  CompleteResultSchema,
  GetPromptResultSchema,
  ListPromptsResultSchema,
  ListResourcesResultSchema,
  ListResourceTemplatesResultSchema,
  ReadResourceResultSchema,
  CallToolResultSchema,
  ListToolsResultSchema
]);

// src/output_schemas.ts
function isTextContent(content) {
  return content.type === "text";
}
var ToolGeneratedFileSchema = z4.object({
  text: z4.string(),
  uri: z4.string(),
  mimeType: z4.literal(INTERNAL_MIME_TYPES.TOOL_OUTPUT.FILE),
  fileId: z4.string(),
  title: z4.string(),
  contentType: z4.string(),
  snippet: z4.string().nullable(),
  hidden: z4.boolean().optional()
});
function isToolGeneratedFile(outputBlock) {
  return outputBlock.type === "resource" && ToolGeneratedFileSchema.safeParse(outputBlock.resource).success;
}
var ThinkingOutputSchema = z4.object({
  mimeType: z4.literal(INTERNAL_MIME_TYPES.TOOL_OUTPUT.THINKING),
  text: z4.string(),
  uri: z4.literal("")
});
function isThinkingOutput(outputBlock) {
  return outputBlock.type === "resource" && ThinkingOutputSchema.safeParse(outputBlock.resource).success;
}
var ReasoningSuccessOutputSchema = z4.object({
  mimeType: z4.literal(INTERNAL_MIME_TYPES.TOOL_OUTPUT.REASONING_SUCCESS),
  text: z4.string(),
  uri: z4.literal("")
});
function isReasoningSuccessOutput(outputBlock) {
  return outputBlock.type === "resource" && ReasoningSuccessOutputSchema.safeParse(outputBlock.resource).success;
}
var SqlQueryOutputSchema = z4.object({
  mimeType: z4.literal(INTERNAL_MIME_TYPES.TOOL_OUTPUT.SQL_QUERY),
  text: z4.string(),
  uri: z4.literal("")
});
function isSqlQueryOutput(outputBlock) {
  return outputBlock.type === "resource" && SqlQueryOutputSchema.safeParse(outputBlock.resource).success;
}
var isResourceWithName = (resource) => {
  return "name" in resource && typeof resource.name === "string";
};
var DatabaseSchemaResourceSchema = z4.object({
  mimeType: z4.literal(INTERNAL_MIME_TYPES.TOOL_OUTPUT.DATABASE_SCHEMA),
  text: z4.string(),
  uri: z4.string()
});
var isDatabaseSchemaResourceType = (outputBlock) => {
  return outputBlock.type === "resource" && DatabaseSchemaResourceSchema.safeParse(outputBlock.resource).success;
};
var QueryWritingInstructionsResourceSchema = z4.object({
  mimeType: z4.literal(
    INTERNAL_MIME_TYPES.TOOL_OUTPUT.QUERY_WRITING_INSTRUCTIONS
  ),
  text: z4.string(),
  uri: z4.string()
});
var isQueryWritingInstructionsResourceType = (outputBlock) => {
  return outputBlock.type === "resource" && QueryWritingInstructionsResourceSchema.safeParse(outputBlock.resource).success;
};
var ExampleRowsResourceSchema = z4.object({
  mimeType: z4.literal(INTERNAL_MIME_TYPES.TOOL_OUTPUT.EXAMPLE_ROWS),
  text: z4.string(),
  uri: z4.string()
});
var isExampleRowsResourceType = (outputBlock) => {
  return outputBlock.type === "resource" && ExampleRowsResourceSchema.safeParse(outputBlock.resource).success;
};
var SearchResultResourceSchema = z4.object({
  mimeType: z4.literal(
    INTERNAL_MIME_TYPES.TOOL_OUTPUT.DATA_SOURCE_SEARCH_RESULT
  ),
  uri: z4.string(),
  text: z4.string(),
  // Document metadata
  id: z4.string(),
  tags: z4.array(z4.string()),
  ref: z4.string(),
  chunks: z4.array(z4.string()),
  source: z4.object({
    provider: z4.string().optional()
  })
});
var isSearchResultResourceType = (outputBlock) => {
  return outputBlock.type === "resource" && SearchResultResourceSchema.safeParse(outputBlock.resource).success;
};
var WarningResourceSchema = z4.object({
  mimeType: z4.literal(INTERNAL_MIME_TYPES.TOOL_OUTPUT.WARNING),
  warningTitle: z4.string(),
  text: z4.string(),
  warningData: z4.record(z4.string(), z4.unknown()).optional(),
  uri: z4.literal("")
});
var isWarningResourceType = (outputBlock) => {
  return outputBlock.type === "resource" && WarningResourceSchema.safeParse(outputBlock.resource).success;
};
var IncludeResultResourceSchema = z4.object({
  mimeType: z4.literal(
    INTERNAL_MIME_TYPES.TOOL_OUTPUT.DATA_SOURCE_INCLUDE_RESULT
  ),
  uri: z4.string(),
  text: z4.string(),
  // Document metadata
  id: z4.string(),
  tags: z4.array(z4.string()),
  ref: z4.string(),
  chunks: z4.array(z4.string()),
  source: z4.object({
    name: z4.string(),
    provider: z4.string().optional()
  })
});
var isIncludeResultResourceType = (outputBlock) => {
  return outputBlock.type === "resource" && IncludeResultResourceSchema.safeParse(outputBlock.resource).success;
};
var WebsearchResultResourceSchema = z4.object({
  mimeType: z4.literal(INTERNAL_MIME_TYPES.TOOL_OUTPUT.WEBSEARCH_RESULT),
  title: z4.string(),
  text: z4.string(),
  uri: z4.string(),
  reference: z4.string()
});
var isWebsearchResultResourceType = (outputBlock) => {
  return outputBlock.type === "resource" && WebsearchResultResourceSchema.safeParse(outputBlock.resource).success;
};
var BrowseResultResourceSchema = z4.object({
  mimeType: z4.literal(INTERNAL_MIME_TYPES.TOOL_OUTPUT.BROWSE_RESULT),
  requestedUrl: z4.string(),
  uri: z4.string(),
  // Browsed url, might differ from the requested url
  text: z4.string(),
  title: z4.string().optional(),
  description: z4.string().optional(),
  responseCode: z4.string(),
  errorMessage: z4.string().optional()
});
var isBrowseResultResourceType = (outputBlock) => {
  return outputBlock.type === "resource" && BrowseResultResourceSchema.safeParse(outputBlock.resource).success;
};
var RunAgentQueryResourceSchema = z4.object({
  mimeType: z4.literal(INTERNAL_MIME_TYPES.TOOL_OUTPUT.RUN_AGENT_QUERY),
  text: z4.string(),
  childAgentId: z4.string(),
  uri: z4.literal("")
});
var isRunAgentQueryResourceType = (outputBlock) => {
  return outputBlock.type === "resource" && RunAgentQueryResourceSchema.safeParse(outputBlock.resource).success;
};
var RunAgentResultResourceSchema = z4.object({
  mimeType: z4.literal(INTERNAL_MIME_TYPES.TOOL_OUTPUT.RUN_AGENT_RESULT),
  conversationId: z4.string(),
  text: z4.string(),
  uri: z4.string(),
  refs: z4.record(
    z4.string(),
    z4.object({
      description: z4.string().optional(),
      href: z4.string().optional(),
      title: z4.string(),
      provider: z4.string()
    })
  ).optional()
});
var isRunAgentResultResourceType = (outputBlock) => {
  return outputBlock.type === "resource" && RunAgentResultResourceSchema.safeParse(outputBlock.resource).success;
};
var ToolsetsResultResourceSchema = z4.object({
  mimeType: z4.literal(INTERNAL_MIME_TYPES.TOOL_OUTPUT.TOOLSET_LIST_RESULT),
  text: z4.string(),
  uri: z4.literal(""),
  id: z4.string(),
  description: z4.string()
});
var isToolsetsResultResourceType = (outputBlock) => {
  return outputBlock.type === "resource" && ToolsetsResultResourceSchema.safeParse(outputBlock.resource).success;
};
var ExtractQueryResourceSchema = z4.object({
  mimeType: z4.literal(INTERNAL_MIME_TYPES.TOOL_OUTPUT.EXTRACT_QUERY),
  text: z4.string(),
  uri: z4.literal("")
});
var isExtractQueryResourceType = (outputBlock) => {
  return outputBlock.type === "resource" && ExtractQueryResourceSchema.safeParse(outputBlock.resource).success;
};
var ExtractResultResourceSchema = z4.object({
  mimeType: z4.literal(INTERNAL_MIME_TYPES.TOOL_OUTPUT.EXTRACT_RESULT),
  uri: z4.string(),
  text: z4.string(),
  // File metadata
  fileId: z4.string(),
  title: z4.string(),
  contentType: z4.string(),
  snippet: z4.string().nullable()
});
var isExtractResultResourceType = (outputBlock) => {
  return outputBlock.type === "resource" && ExtractResultResourceSchema.safeParse(outputBlock.resource).success;
};
var NotificationImageContentSchema = z4.object({
  type: z4.literal("image"),
  mimeType: z4.string()
});
function isImageProgressOutput(output) {
  return output !== void 0 && output.type === "image";
}
var NotificationInteractiveContentFileContentSchema = z4.object({
  type: z4.literal("interactive_content_file"),
  fileId: z4.string(),
  mimeType: z4.string(),
  title: z4.string(),
  updatedAt: z4.string()
});
function isInteractiveContentFileContentOutput(output) {
  return output !== void 0 && output.type === "interactive_content_file";
}
var NotificationStoreResourceContentSchema = z4.object({
  type: z4.literal("store_resource"),
  contents: z4.array(
    z4.object({
      type: z4.literal("resource"),
      resource: z4.object({
        mimeType: z4.string(),
        text: z4.string(),
        uri: z4.string()
      }).passthrough()
    })
    // Allow additional properties
  )
});
var NotificationTextContentSchema = z4.object({
  type: z4.literal("text"),
  text: z4.string()
});
var NotificationRunAgentContentSchema = z4.object({
  type: z4.literal("run_agent"),
  childAgentId: z4.string(),
  conversationId: z4.string(),
  query: z4.string()
});
function isStoreResourceProgressOutput(output) {
  return output !== void 0 && output.type === "store_resource";
}
var ProgressNotificationOutputSchema = z4.union([
  NotificationInteractiveContentFileContentSchema,
  NotificationImageContentSchema,
  NotificationRunAgentContentSchema,
  NotificationStoreResourceContentSchema,
  NotificationTextContentSchema
]).optional();
var ProgressNotificationContentSchema = z4.object({
  // Required for the MCP protocol.
  progress: z4.number(),
  total: z4.number(),
  progressToken: z4.union([z4.string(), z4.number()]),
  // This one is deprecated, use _meta.data instead
  data: z4.object({
    label: z4.string(),
    output: ProgressNotificationOutputSchema
  }).optional(),
  // Custom data.
  _meta: z4.object({
    data: z4.object({
      label: z4.string(),
      output: ProgressNotificationOutputSchema
    })
  })
});
var MCPProgressNotificationSchema = NotificationSchema.extend({
  method: z4.literal("notifications/progress"),
  params: ProgressNotificationContentSchema
});
function isMCPProgressNotificationType(notification) {
  return MCPProgressNotificationSchema.safeParse(notification).success;
}
var OAuthProviderSchema = FlexibleEnumSchema();
var AuthRequiredOutputResourceSchema = z4.object({
  mimeType: z4.literal(INTERNAL_MIME_TYPES.TOOL_OUTPUT.AGENT_PAUSE_TOOL_OUTPUT),
  type: z4.literal("tool_personal_auth_required"),
  provider: OAuthProviderSchema,
  scope: z4.string().optional(),
  text: z4.string(),
  uri: z4.string()
});
var BlockedAwaitingInputOutputResourceSchema = z4.object({
  mimeType: z4.literal(INTERNAL_MIME_TYPES.TOOL_OUTPUT.AGENT_PAUSE_TOOL_OUTPUT),
  type: z4.literal("tool_blocked_awaiting_input"),
  text: z4.string(),
  uri: z4.string(),
  blockingEvents: z4.array(z4.any()),
  state: z4.any()
});
var EarlyExitOutputResourceSchema = z4.object({
  mimeType: z4.literal(INTERNAL_MIME_TYPES.TOOL_OUTPUT.AGENT_PAUSE_TOOL_OUTPUT),
  type: z4.literal("tool_early_exit"),
  text: z4.string(),
  isError: z4.boolean(),
  reason: z4.enum(["deploy_interruption", "user_cancellation", "none"]).optional(),
  uri: z4.string()
});
var FileAuthRequiredOutputResourceSchema = z4.object({
  mimeType: z4.literal(INTERNAL_MIME_TYPES.TOOL_OUTPUT.AGENT_PAUSE_TOOL_OUTPUT),
  type: z4.literal("tool_file_auth_required"),
  fileId: z4.string(),
  fileName: z4.string(),
  connectionId: z4.string(),
  mimeType_file: z4.string(),
  text: z4.string(),
  uri: z4.string()
});
var UserQuestionItemSchema = z4.object({
  question: z4.string(),
  options: z4.array(
    z4.object({
      label: z4.string(),
      description: z4.string().nullable()
    })
  ),
  multiSelect: z4.boolean()
});
var UserAnswerRequiredOutputResourceSchema = z4.object({
  mimeType: z4.literal(INTERNAL_MIME_TYPES.TOOL_OUTPUT.AGENT_PAUSE_TOOL_OUTPUT),
  type: z4.literal("tool_user_answer_required"),
  question: UserQuestionItemSchema,
  text: z4.string(),
  uri: z4.string()
});
var AgentPauseOutputResourceSchema = z4.union([
  AuthRequiredOutputResourceSchema,
  BlockedAwaitingInputOutputResourceSchema,
  EarlyExitOutputResourceSchema,
  FileAuthRequiredOutputResourceSchema,
  UserAnswerRequiredOutputResourceSchema
]);
var isAgentPauseOutputResourceType = (outputBlock) => {
  return outputBlock.type === "resource" && AgentPauseOutputResourceSchema.safeParse(outputBlock.resource).success;
};

// src/timezone_names.ts
var TIMEZONE_NAMES = [
  "Africa/Abidjan",
  "Africa/Accra",
  "Africa/Addis_Ababa",
  "Africa/Algiers",
  "Africa/Asmara",
  "Africa/Asmera",
  "Africa/Bamako",
  "Africa/Bangui",
  "Africa/Banjul",
  "Africa/Bissau",
  "Africa/Blantyre",
  "Africa/Brazzaville",
  "Africa/Bujumbura",
  "Africa/Cairo",
  "Africa/Casablanca",
  "Africa/Ceuta",
  "Africa/Conakry",
  "Africa/Dakar",
  "Africa/Dar_es_Salaam",
  "Africa/Djibouti",
  "Africa/Douala",
  "Africa/El_Aaiun",
  "Africa/Freetown",
  "Africa/Gaborone",
  "Africa/Harare",
  "Africa/Johannesburg",
  "Africa/Juba",
  "Africa/Kampala",
  "Africa/Khartoum",
  "Africa/Kigali",
  "Africa/Kinshasa",
  "Africa/Lagos",
  "Africa/Libreville",
  "Africa/Lome",
  "Africa/Luanda",
  "Africa/Lubumbashi",
  "Africa/Lusaka",
  "Africa/Malabo",
  "Africa/Maputo",
  "Africa/Maseru",
  "Africa/Mbabane",
  "Africa/Mogadishu",
  "Africa/Monrovia",
  "Africa/Nairobi",
  "Africa/Ndjamena",
  "Africa/Niamey",
  "Africa/Nouakchott",
  "Africa/Ouagadougou",
  "Africa/Porto-Novo",
  "Africa/Sao_Tome",
  "Africa/Timbuktu",
  "Africa/Tripoli",
  "Africa/Tunis",
  "Africa/Windhoek",
  "America/Adak",
  "America/Anchorage",
  "America/Anguilla",
  "America/Antigua",
  "America/Araguaina",
  "America/Argentina/Buenos_Aires",
  "America/Argentina/Catamarca",
  "America/Argentina/ComodRivadavia",
  "America/Argentina/Cordoba",
  "America/Argentina/Jujuy",
  "America/Argentina/La_Rioja",
  "America/Argentina/Mendoza",
  "America/Argentina/Rio_Gallegos",
  "America/Argentina/Salta",
  "America/Argentina/San_Juan",
  "America/Argentina/San_Luis",
  "America/Argentina/Tucuman",
  "America/Argentina/Ushuaia",
  "America/Aruba",
  "America/Asuncion",
  "America/Atikokan",
  "America/Atka",
  "America/Bahia",
  "America/Bahia_Banderas",
  "America/Barbados",
  "America/Belem",
  "America/Belize",
  "America/Blanc-Sablon",
  "America/Boa_Vista",
  "America/Bogota",
  "America/Boise",
  "America/Buenos_Aires",
  "America/Cambridge_Bay",
  "America/Campo_Grande",
  "America/Cancun",
  "America/Caracas",
  "America/Catamarca",
  "America/Cayenne",
  "America/Cayman",
  "America/Chicago",
  "America/Chihuahua",
  "America/Coral_Harbour",
  "America/Cordoba",
  "America/Costa_Rica",
  "America/Creston",
  "America/Cuiaba",
  "America/Curacao",
  "America/Danmarkshavn",
  "America/Dawson",
  "America/Dawson_Creek",
  "America/Denver",
  "America/Detroit",
  "America/Dominica",
  "America/Edmonton",
  "America/Eirunepe",
  "America/El_Salvador",
  "America/Ensenada",
  "America/Fort_Nelson",
  "America/Fort_Wayne",
  "America/Fortaleza",
  "America/Glace_Bay",
  "America/Godthab",
  "America/Goose_Bay",
  "America/Grand_Turk",
  "America/Grenada",
  "America/Guadeloupe",
  "America/Guatemala",
  "America/Guayaquil",
  "America/Guyana",
  "America/Halifax",
  "America/Havana",
  "America/Hermosillo",
  "America/Indiana/Indianapolis",
  "America/Indiana/Knox",
  "America/Indiana/Marengo",
  "America/Indiana/Petersburg",
  "America/Indiana/Tell_City",
  "America/Indiana/Vevay",
  "America/Indiana/Vincennes",
  "America/Indiana/Winamac",
  "America/Indianapolis",
  "America/Inuvik",
  "America/Iqaluit",
  "America/Jamaica",
  "America/Jujuy",
  "America/Juneau",
  "America/Kentucky/Louisville",
  "America/Kentucky/Monticello",
  "America/Knox_IN",
  "America/Kralendijk",
  "America/La_Paz",
  "America/Lima",
  "America/Los_Angeles",
  "America/Louisville",
  "America/Lower_Princes",
  "America/Maceio",
  "America/Managua",
  "America/Manaus",
  "America/Marigot",
  "America/Martinique",
  "America/Matamoros",
  "America/Mazatlan",
  "America/Mendoza",
  "America/Menominee",
  "America/Merida",
  "America/Metlakatla",
  "America/Mexico_City",
  "America/Miquelon",
  "America/Moncton",
  "America/Monterrey",
  "America/Montevideo",
  "America/Montreal",
  "America/Montserrat",
  "America/Nassau",
  "America/New_York",
  "America/Nipigon",
  "America/Nome",
  "America/Noronha",
  "America/North_Dakota/Beulah",
  "America/North_Dakota/Center",
  "America/North_Dakota/New_Salem",
  "America/Ojinaga",
  "America/Panama",
  "America/Pangnirtung",
  "America/Paramaribo",
  "America/Phoenix",
  "America/Port-au-Prince",
  "America/Port_of_Spain",
  "America/Porto_Acre",
  "America/Porto_Velho",
  "America/Puerto_Rico",
  "America/Punta_Arenas",
  "America/Rainy_River",
  "America/Rankin_Inlet",
  "America/Recife",
  "America/Regina",
  "America/Resolute",
  "America/Rio_Branco",
  "America/Rosario",
  "America/Santa_Isabel",
  "America/Santarem",
  "America/Santiago",
  "America/Santo_Domingo",
  "America/Sao_Paulo",
  "America/Scoresbysund",
  "America/Shiprock",
  "America/Sitka",
  "America/St_Barthelemy",
  "America/St_Johns",
  "America/St_Kitts",
  "America/St_Lucia",
  "America/St_Thomas",
  "America/St_Vincent",
  "America/Swift_Current",
  "America/Tegucigalpa",
  "America/Thule",
  "America/Thunder_Bay",
  "America/Tijuana",
  "America/Toronto",
  "America/Tortola",
  "America/Vancouver",
  "America/Virgin",
  "America/Whitehorse",
  "America/Winnipeg",
  "America/Yakutat",
  "America/Yellowknife",
  "Antarctica/Casey",
  "Antarctica/Davis",
  "Antarctica/DumontDUrville",
  "Antarctica/Macquarie",
  "Antarctica/Mawson",
  "Antarctica/McMurdo",
  "Antarctica/Palmer",
  "Antarctica/Rothera",
  "Antarctica/South_Pole",
  "Antarctica/Syowa",
  "Antarctica/Troll",
  "Antarctica/Vostok",
  "Arctic/Longyearbyen",
  "Asia/Aden",
  "Asia/Almaty",
  "Asia/Amman",
  "Asia/Anadyr",
  "Asia/Aqtau",
  "Asia/Aqtobe",
  "Asia/Ashgabat",
  "Asia/Ashkhabad",
  "Asia/Atyrau",
  "Asia/Baghdad",
  "Asia/Bahrain",
  "Asia/Baku",
  "Asia/Bangkok",
  "Asia/Barnaul",
  "Asia/Beirut",
  "Asia/Bishkek",
  "Asia/Brunei",
  "Asia/Calcutta",
  "Asia/Chita",
  "Asia/Choibalsan",
  "Asia/Chongqing",
  "Asia/Chungking",
  "Asia/Colombo",
  "Asia/Dacca",
  "Asia/Damascus",
  "Asia/Dhaka",
  "Asia/Dili",
  "Asia/Dubai",
  "Asia/Dushanbe",
  "Asia/Famagusta",
  "Asia/Gaza",
  "Asia/Harbin",
  "Asia/Hebron",
  "Asia/Ho_Chi_Minh",
  "Asia/Hong_Kong",
  "Asia/Hovd",
  "Asia/Irkutsk",
  "Asia/Istanbul",
  "Asia/Jakarta",
  "Asia/Jayapura",
  "Asia/Jerusalem",
  "Asia/Kabul",
  "Asia/Kamchatka",
  "Asia/Karachi",
  "Asia/Kashgar",
  "Asia/Kathmandu",
  "Asia/Katmandu",
  "Asia/Khandyga",
  "Asia/Kolkata",
  "Asia/Krasnoyarsk",
  "Asia/Kuala_Lumpur",
  "Asia/Kuching",
  "Asia/Kuwait",
  "Asia/Macao",
  "Asia/Macau",
  "Asia/Magadan",
  "Asia/Makassar",
  "Asia/Manila",
  "Asia/Muscat",
  "Asia/Nicosia",
  "Asia/Novokuznetsk",
  "Asia/Novosibirsk",
  "Asia/Omsk",
  "Asia/Oral",
  "Asia/Phnom_Penh",
  "Asia/Pontianak",
  "Asia/Pyongyang",
  "Asia/Qatar",
  "Asia/Qyzylorda",
  "Asia/Rangoon",
  "Asia/Riyadh",
  "Asia/Saigon",
  "Asia/Sakhalin",
  "Asia/Samarkand",
  "Asia/Seoul",
  "Asia/Shanghai",
  "Asia/Singapore",
  "Asia/Srednekolymsk",
  "Asia/Taipei",
  "Asia/Tashkent",
  "Asia/Tbilisi",
  "Asia/Tehran",
  "Asia/Tel_Aviv",
  "Asia/Thimbu",
  "Asia/Thimphu",
  "Asia/Tokyo",
  "Asia/Tomsk",
  "Asia/Ujung_Pandang",
  "Asia/Ulaanbaatar",
  "Asia/Ulan_Bator",
  "Asia/Urumqi",
  "Asia/Ust-Nera",
  "Asia/Vientiane",
  "Asia/Vladivostok",
  "Asia/Yakutsk",
  "Asia/Yangon",
  "Asia/Yekaterinburg",
  "Asia/Yerevan",
  "Atlantic/Azores",
  "Atlantic/Bermuda",
  "Atlantic/Canary",
  "Atlantic/Cape_Verde",
  "Atlantic/Faeroe",
  "Atlantic/Faroe",
  "Atlantic/Jan_Mayen",
  "Atlantic/Madeira",
  "Atlantic/Reykjavik",
  "Atlantic/South_Georgia",
  "Atlantic/St_Helena",
  "Atlantic/Stanley",
  "Australia/ACT",
  "Australia/Adelaide",
  "Australia/Brisbane",
  "Australia/Broken_Hill",
  "Australia/Canberra",
  "Australia/Currie",
  "Australia/Darwin",
  "Australia/Eucla",
  "Australia/Hobart",
  "Australia/LHI",
  "Australia/Lindeman",
  "Australia/Lord_Howe",
  "Australia/Melbourne",
  "Australia/NSW",
  "Australia/North",
  "Australia/Perth",
  "Australia/Queensland",
  "Australia/South",
  "Australia/Sydney",
  "Australia/Tasmania",
  "Australia/Victoria",
  "Australia/West",
  "Australia/Yancowinna",
  "Brazil/Acre",
  "Brazil/DeNoronha",
  "Brazil/East",
  "Brazil/West",
  "CET",
  "CST6CDT",
  "Canada/Atlantic",
  "Canada/Central",
  "Canada/Eastern",
  "Canada/Mountain",
  "Canada/Newfoundland",
  "Canada/Pacific",
  "Canada/Saskatchewan",
  "Canada/Yukon",
  "Chile/Continental",
  "Chile/EasterIsland",
  "Cuba",
  "EET",
  "EST",
  "EST5EDT",
  "Egypt",
  "Eire",
  "Etc/GMT",
  "Etc/GMT+0",
  "Etc/GMT+1",
  "Etc/GMT+10",
  "Etc/GMT+11",
  "Etc/GMT+12",
  "Etc/GMT+2",
  "Etc/GMT+3",
  "Etc/GMT+4",
  "Etc/GMT+5",
  "Etc/GMT+6",
  "Etc/GMT+7",
  "Etc/GMT+8",
  "Etc/GMT+9",
  "Etc/GMT-0",
  "Etc/GMT-1",
  "Etc/GMT-10",
  "Etc/GMT-11",
  "Etc/GMT-12",
  "Etc/GMT-13",
  "Etc/GMT-14",
  "Etc/GMT-2",
  "Etc/GMT-3",
  "Etc/GMT-4",
  "Etc/GMT-5",
  "Etc/GMT-6",
  "Etc/GMT-7",
  "Etc/GMT-8",
  "Etc/GMT-9",
  "Etc/GMT0",
  "Etc/Greenwich",
  "Etc/UCT",
  "Etc/UTC",
  "Etc/Universal",
  "Etc/Zulu",
  "Europe/Amsterdam",
  "Europe/Andorra",
  "Europe/Astrakhan",
  "Europe/Athens",
  "Europe/Belfast",
  "Europe/Belgrade",
  "Europe/Berlin",
  "Europe/Bratislava",
  "Europe/Brussels",
  "Europe/Bucharest",
  "Europe/Budapest",
  "Europe/Busingen",
  "Europe/Chisinau",
  "Europe/Copenhagen",
  "Europe/Dublin",
  "Europe/Gibraltar",
  "Europe/Guernsey",
  "Europe/Helsinki",
  "Europe/Isle_of_Man",
  "Europe/Istanbul",
  "Europe/Jersey",
  "Europe/Kaliningrad",
  "Europe/Kiev",
  "Europe/Kirov",
  "Europe/Lisbon",
  "Europe/Ljubljana",
  "Europe/London",
  "Europe/Luxembourg",
  "Europe/Madrid",
  "Europe/Malta",
  "Europe/Mariehamn",
  "Europe/Minsk",
  "Europe/Monaco",
  "Europe/Moscow",
  "Europe/Nicosia",
  "Europe/Oslo",
  "Europe/Paris",
  "Europe/Podgorica",
  "Europe/Prague",
  "Europe/Riga",
  "Europe/Rome",
  "Europe/Samara",
  "Europe/San_Marino",
  "Europe/Sarajevo",
  "Europe/Saratov",
  "Europe/Simferopol",
  "Europe/Skopje",
  "Europe/Sofia",
  "Europe/Stockholm",
  "Europe/Tallinn",
  "Europe/Tirane",
  "Europe/Tiraspol",
  "Europe/Ulyanovsk",
  "Europe/Uzhgorod",
  "Europe/Vaduz",
  "Europe/Vatican",
  "Europe/Vienna",
  "Europe/Vilnius",
  "Europe/Volgograd",
  "Europe/Warsaw",
  "Europe/Zagreb",
  "Europe/Zaporozhye",
  "Europe/Zurich",
  "GB",
  "GB-Eire",
  "GMT",
  "GMT+0",
  "GMT-0",
  "GMT0",
  "Greenwich",
  "HST",
  "Hongkong",
  "Iceland",
  "Indian/Antananarivo",
  "Indian/Chagos",
  "Indian/Christmas",
  "Indian/Cocos",
  "Indian/Comoro",
  "Indian/Kerguelen",
  "Indian/Mahe",
  "Indian/Maldives",
  "Indian/Mauritius",
  "Indian/Mayotte",
  "Indian/Reunion",
  "Iran",
  "Israel",
  "Jamaica",
  "Japan",
  "Kwajalein",
  "Libya",
  "MET",
  "MST",
  "MST7MDT",
  "Mexico/BajaNorte",
  "Mexico/BajaSur",
  "Mexico/General",
  "NZ",
  "NZ-CHAT",
  "Navajo",
  "PRC",
  "PST8PDT",
  "Pacific/Apia",
  "Pacific/Auckland",
  "Pacific/Bougainville",
  "Pacific/Chatham",
  "Pacific/Chuuk",
  "Pacific/Easter",
  "Pacific/Efate",
  "Pacific/Enderbury",
  "Pacific/Fakaofo",
  "Pacific/Fiji",
  "Pacific/Funafuti",
  "Pacific/Galapagos",
  "Pacific/Gambier",
  "Pacific/Guadalcanal",
  "Pacific/Guam",
  "Pacific/Honolulu",
  "Pacific/Johnston",
  "Pacific/Kiritimati",
  "Pacific/Kosrae",
  "Pacific/Kwajalein",
  "Pacific/Majuro",
  "Pacific/Marquesas",
  "Pacific/Midway",
  "Pacific/Nauru",
  "Pacific/Niue",
  "Pacific/Norfolk",
  "Pacific/Noumea",
  "Pacific/Pago_Pago",
  "Pacific/Palau",
  "Pacific/Pitcairn",
  "Pacific/Pohnpei",
  "Pacific/Ponape",
  "Pacific/Port_Moresby",
  "Pacific/Rarotonga",
  "Pacific/Saipan",
  "Pacific/Samoa",
  "Pacific/Tahiti",
  "Pacific/Tarawa",
  "Pacific/Tongatapu",
  "Pacific/Truk",
  "Pacific/Wake",
  "Pacific/Wallis",
  "Pacific/Yap",
  "Poland",
  "Portugal",
  "ROC",
  "ROK",
  "Singapore",
  "Turkey",
  "UCT",
  "US/Alaska",
  "US/Aleutian",
  "US/Arizona",
  "US/Central",
  "US/East-Indiana",
  "US/Eastern",
  "US/Hawaii",
  "US/Indiana-Starke",
  "US/Michigan",
  "US/Mountain",
  "US/Pacific",
  "US/Pacific-New",
  "US/Samoa",
  "UTC",
  "Universal",
  "W-SU",
  "WET",
  "Zulu"
];

// src/types.ts
var ModelProviderIdSchema = FlexibleEnumSchema();
var ModelLLMIdSchema = FlexibleEnumSchema();
var EmbeddingProviderIdSchema = FlexibleEnumSchema();
var ConnectorsAPIErrorTypeSchema = FlexibleEnumSchema();
var ConnectorsAPIErrorSchema = z5.object({
  type: ConnectorsAPIErrorTypeSchema,
  message: z5.string()
});
var ModelIdSchema = z5.number();
function isConnectorsAPIError(obj) {
  return typeof obj === "object" && obj !== null && "message" in obj && typeof obj.message === "string" && "type" in obj && typeof obj.type === "string" && ConnectorsAPIErrorSchema.safeParse(obj).success;
}
var supportedOtherFileFormats = {
  "application/msword": [".doc", ".docx"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
    ".doc",
    ".docx"
  ],
  "application/vnd.ms-powerpoint": [".ppt", ".pptx"],
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": [
    ".ppt",
    ".pptx"
  ],
  "application/vnd.google-apps.document": [],
  "application/vnd.google-apps.presentation": [],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
    ".xlsx"
  ],
  "application/vnd.google-apps.spreadsheet": [],
  "application/vnd.ms-excel": [".xls"],
  "application/pdf": [".pdf"],
  "application/vnd.dust.section.json": [".json"],
  "message/rfc822": [".eml"],
  "text/comma-separated-values": [".csv"],
  "text/csv": [".csv"],
  "text/markdown": [".md", ".markdown"],
  "text/plain": [".txt", ".log", ".cfg", ".conf"],
  "text/tab-separated-values": [".tsv"],
  "text/tsv": [".tsv"],
  "text/vnd.dust.attachment.slack.thread": [".txt"],
  "text/vnd.dust.attachment.pasted": [".txt"],
  "text/html": [".html", ".htm", ".xhtml", ".xhtml+xml"],
  "text/xml": [".xml"],
  "text/calendar": [".ics"],
  "text/css": [".css"],
  "text/javascript": [".js", ".mjs", ".jsx"],
  "text/typescript": [".ts", ".tsx"],
  "application/json": [".json"],
  "application/xml": [".xml"],
  "application/x-sh": [".sh"],
  "text/x-sh": [".sh"],
  "text/x-python": [".py"],
  "text/x-python-script": [".py"],
  "application/x-yaml": [".yaml", ".yml"],
  "text/yaml": [".yaml", ".yml"],
  "text/vnd.yaml": [".yaml", ".yml"],
  "text/x-c": [".c", ".cc", ".cpp", ".cxx", ".dic", ".h", ".hh"],
  "text/x-csharp": [".cs"],
  "text/x-java-source": [".java"],
  "text/x-php": [".php"],
  "text/x-ruby": [".rb"],
  "text/x-sql": [".sql"],
  "text/x-swift": [".swift"],
  "text/x-rust": [".rs"],
  "text/x-go": [".go"],
  "text/x-kotlin": [".kt", ".kts"],
  "text/x-scala": [".scala"],
  "text/x-groovy": [".groovy"],
  "text/x-perl": [".pl", ".pm"],
  "text/x-perl-script": [".pl", ".pm"],
  "application/octet-stream": []
};
var supportedImageFileFormats = {
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "image/gif": [".gif"],
  "image/webp": [".webp"],
  "image/svg+xml": [".svg"],
  "image/bmp": [".bmp"],
  "image/x-icon": [".ico"]
};
var supportedAudioFileFormats = {
  "audio/mpeg": [".mp3", ".mp4"],
  "audio/x-m4a": [".m4a", ".mp4"],
  "audio/ogg": [".ogg"],
  "audio/wav": [".wav"],
  "audio/x-wav": [".wav"],
  "audio/webm": [".webm"],
  // Chrome sometimes uses video/webm for audio files, and we can still process them as audio only files
  "video/webm": [".webm"]
};
var supportedFontFileFormats = {
  "font/woff": [".woff"],
  "font/woff2": [".woff2"],
  "font/otf": [".otf"],
  "font/ttf": [".ttf"],
  "font/collection": [".ttc", ".otc"]
};
var PostWebhookTriggerResponseSchema = z5.object({
  success: z5.literal(true)
});
var supportedOtherContentTypes = Object.keys(
  supportedOtherFileFormats
);
var supportedImageContentTypes = Object.keys(
  supportedImageFileFormats
);
var supportedAudioContentTypes = Object.keys(
  supportedAudioFileFormats
);
var supportedFileExtensions = [
  ...Object.keys(supportedOtherFileFormats),
  ...Object.keys(supportedImageFileFormats)
];
var supportedUploadableContentType = [
  ...supportedOtherContentTypes,
  ...supportedImageContentTypes,
  ...supportedAudioContentTypes
];
var SupportedContentFragmentTypeSchema = FlexibleEnumSchema();
var SupportedFileContentFragmentTypeSchema = FlexibleEnumSchema();
var FrameContentTypeSchema = z5.literal("application/vnd.dust.frame");
var FrameSlideshowContentTypeSchema = z5.literal(
  "application/vnd.dust.frame.slideshow"
);
var ActionGeneratedFileContentTypeSchema = z5.union([
  SupportedFileContentFragmentTypeSchema,
  FrameContentTypeSchema,
  FrameSlideshowContentTypeSchema
]);
function isSupportedFileContentType(contentType) {
  return supportedUploadableContentType.includes(
    contentType
  );
}
function isSupportedPlainTextContentType(contentType) {
  return supportedOtherContentTypes.includes(contentType);
}
function isSupportedImageContentType(contentType) {
  return supportedImageContentTypes.includes(contentType);
}
function isSupportedAudioContentType(contentType) {
  return supportedAudioContentTypes.includes(contentType);
}
var USER_MESSAGE_ORIGINS = [
  "api",
  "cli",
  "cli_programmatic",
  "email",
  "excel",
  "extension",
  "gsheet",
  "make",
  "n8n",
  "powerpoint",
  "raycast",
  "slack",
  "slack_workflow",
  "teams",
  "transcript",
  "triggered_programmatic",
  "triggered",
  "wakeup",
  "web",
  "zapier",
  "zendesk",
  "onboarding_conversation",
  "agent_sidekick",
  "project_kickoff",
  "reinforced_skill_notification",
  "reinforcement",
  "branch_anchor"
];
var UserMessageOriginEnumSchema = z5.enum(USER_MESSAGE_ORIGINS);
var UserMessageOriginSchema = UserMessageOriginEnumSchema.catch("api").or(z5.null()).or(z5.undefined());
var VisibilitySchema = FlexibleEnumSchema();
var RankSchema = z5.object({
  rank: z5.number()
});
var Ok = class {
  constructor(value) {
    this.value = value;
  }
  isOk() {
    return true;
  }
  isErr() {
    return false;
  }
};
var Err = class {
  constructor(error) {
    this.error = error;
  }
  isOk() {
    return false;
  }
  isErr() {
    return true;
  }
};
var Timezone = z5.string().refine((s) => TIMEZONE_NAMES.includes(s), {
  message: "Invalid timezone"
});
var ConnectorProvidersSchema = FlexibleEnumSchema();
var isConnectorProvider = (provider) => ConnectorProvidersSchema.safeParse(provider).success;
var EditedByUserSchema = z5.object({
  editedAt: z5.number().nullable(),
  fullName: z5.string().nullable(),
  imageUrl: z5.string().nullable(),
  email: z5.string().nullable(),
  userId: z5.string().nullable()
});
var DataSourceTypeSchema = z5.object({
  id: ModelIdSchema,
  sId: z5.string(),
  createdAt: z5.number(),
  name: z5.string(),
  description: z5.string().nullable(),
  assistantDefaultSelected: z5.boolean(),
  dustAPIProjectId: z5.string(),
  dustAPIDataSourceId: z5.string(),
  connectorId: z5.string().nullable(),
  connectorProvider: ConnectorProvidersSchema.nullable(),
  editedByUser: EditedByUserSchema.nullable().optional()
});
function isFolder(ds) {
  return !ds.connectorProvider;
}
function isWebsite(ds) {
  return ds.connectorProvider === "webcrawler";
}
var CoreAPIDocumentChunkSchema = z5.object({
  text: z5.string(),
  hash: z5.string(),
  offset: z5.number(),
  vector: z5.array(z5.number()).nullable().optional(),
  score: z5.number().nullable().optional()
});
var CoreAPIDocumentSchema = z5.object({
  data_source_id: z5.string(),
  created: z5.number(),
  document_id: z5.string(),
  timestamp: z5.number(),
  parent_id: z5.string().nullable().optional(),
  tags: z5.array(z5.string()),
  source_url: z5.string().nullable().optional(),
  hash: z5.string(),
  text_size: z5.number(),
  chunk_count: z5.number(),
  chunks: z5.array(CoreAPIDocumentChunkSchema),
  text: z5.string().nullable().optional()
});
var CoreAPILightDocumentSchema = z5.object({
  hash: z5.string(),
  text_size: z5.number(),
  chunk_count: z5.number(),
  token_count: z5.number(),
  created: z5.number()
});
var CoreAPIRowValueSchema = z5.union([
  z5.number(),
  z5.string(),
  z5.boolean(),
  z5.object({
    type: z5.literal("datetime"),
    epoch: z5.number(),
    string_value: z5.string().optional()
  }),
  z5.null()
]);
var CoreAPIRowSchema = z5.object({
  row_id: z5.string(),
  value: z5.record(CoreAPIRowValueSchema)
});
var CoreAPITableSchema = z5.array(
  z5.object({
    name: z5.string(),
    value_type: z5.enum(["int", "float", "text", "bool", "datetime"]),
    possible_values: z5.array(z5.string()).nullable().optional()
  })
);
var CoreAPITablePublicSchema = z5.object({
  table_id: z5.string(),
  name: z5.string(),
  description: z5.string(),
  schema: CoreAPITableSchema.nullable(),
  timestamp: z5.number(),
  tags: z5.array(z5.string()),
  parents: z5.array(z5.string()),
  parent_id: z5.string().nullable().optional(),
  mime_type: z5.string().optional(),
  title: z5.string().optional()
});
var DataSourceViewCategoriesSchema = FlexibleEnumSchema();
var BlockTypeSchema = FlexibleEnumSchema();
var StatusSchema = z5.enum(["running", "succeeded", "errored"]);
var BlockRunConfigSchema = z5.record(z5.any());
var BlockStatusSchema = z5.object({
  block_type: BlockTypeSchema,
  name: z5.string(),
  status: StatusSchema,
  success_count: z5.number(),
  error_count: z5.number()
});
var RunConfigSchema = z5.object({
  blocks: BlockRunConfigSchema
});
var TraceTypeSchema = z5.object({
  value: z5.unknown().nullable(),
  error: z5.string().nullable(),
  meta: z5.unknown().nullable()
});
var RunStatusSchema = z5.object({
  run: StatusSchema,
  blocks: z5.array(BlockStatusSchema)
});
var RunTypeSchema = z5.object({
  run_id: z5.string(),
  created: z5.number(),
  run_type: z5.enum(["deploy", "local", "execute"]),
  app_hash: z5.string().nullable().optional(),
  specification_hash: z5.string().nullable().optional(),
  config: RunConfigSchema,
  status: RunStatusSchema,
  traces: z5.array(
    z5.tuple([
      z5.tuple([BlockTypeSchema, z5.string()]),
      z5.array(z5.array(TraceTypeSchema))
    ])
  ),
  results: z5.array(
    z5.array(
      z5.object({
        value: z5.unknown().nullable().optional(),
        error: z5.string().nullable().optional()
      })
    )
  ).nullable().optional()
});
var TokensClassificationSchema = FlexibleEnumSchema();
var GenerationTokensEventSchema = z5.object({
  type: z5.literal("generation_tokens"),
  created: z5.number(),
  configurationId: z5.string(),
  messageId: z5.string(),
  text: z5.string(),
  classification: z5.union([
    TokensClassificationSchema,
    z5.enum(["opening_delimiter", "closing_delimiter"])
  ]),
  delimiterClassification: TokensClassificationSchema.nullable().optional()
});
var DataSourceViewKindSchema = FlexibleEnumSchema();
var DataSourceViewSchema = z5.object({
  category: DataSourceViewCategoriesSchema,
  createdAt: z5.number(),
  dataSource: DataSourceTypeSchema,
  editedByUser: EditedByUserSchema.nullable().optional(),
  id: ModelIdSchema,
  kind: DataSourceViewKindSchema,
  parentsIn: z5.array(z5.string()).nullable(),
  sId: z5.string(),
  updatedAt: z5.number(),
  spaceId: z5.string()
});
var RetrievalDocumentChunkTypeSchema = z5.object({
  offset: z5.number(),
  score: z5.number().nullable(),
  text: z5.string()
});
var RetrievalDocumentTypeSchema = z5.object({
  chunks: z5.array(RetrievalDocumentChunkTypeSchema),
  documentId: z5.string(),
  dataSourceView: DataSourceViewSchema.nullable(),
  id: ModelIdSchema,
  reference: z5.string(),
  score: z5.number().nullable(),
  sourceUrl: z5.string().nullable(),
  tags: z5.array(z5.string()),
  timestamp: z5.number()
});
var WhitelistableFeaturesSchema = FlexibleEnumSchema();
var WorkspaceSegmentationSchema = FlexibleEnumSchema().nullable();
var RoleSchema = z5.enum([
  "admin",
  "business_admin",
  "builder",
  "user",
  "none"
]);
var LightWorkspaceSchema = z5.object({
  id: ModelIdSchema,
  sId: z5.string(),
  name: z5.string(),
  role: RoleSchema,
  segmentation: WorkspaceSegmentationSchema,
  whiteListedProviders: ModelProviderIdSchema.array().nullable(),
  defaultEmbeddingProvider: EmbeddingProviderIdSchema.nullable(),
  regionalModelsOnly: z5.boolean().default(false)
});
var WorkspaceSchema = LightWorkspaceSchema.extend({
  ssoEnforced: z5.boolean().optional()
});
var ExtensionWorkspaceSchema = WorkspaceSchema.extend({
  blacklistedDomains: z5.array(z5.string()).nullable()
});
var UserProviderSchema = FlexibleEnumSchema().nullable();
var UserSchema = z5.object({
  sId: z5.string(),
  id: ModelIdSchema,
  createdAt: z5.number(),
  provider: UserProviderSchema,
  username: z5.string(),
  email: z5.string(),
  firstName: z5.string(),
  lastName: z5.string().nullable(),
  fullName: z5.string(),
  image: z5.string().nullable()
});
var WebsearchResultSchema = z5.object({
  title: z5.string(),
  snippet: z5.string(),
  link: z5.string(),
  reference: z5.string()
});
var ActionGeneratedFileSchema = z5.object({
  fileId: z5.string(),
  title: z5.string(),
  contentType: ActionGeneratedFileContentTypeSchema,
  snippet: z5.string().nullable(),
  hidden: z5.boolean().optional(),
  isInProjectContext: z5.boolean().optional()
});
var DisplayLabelsSchema = z5.object({
  running: z5.string(),
  done: z5.string()
}).nullable();
var AgentActionTypeSchema = z5.object({
  id: ModelIdSchema,
  sId: z5.string(),
  createdAt: z5.number(),
  updatedAt: z5.number(),
  mcpServerId: z5.string().nullable(),
  internalMCPServerName: z5.string().nullable(),
  toolName: z5.string(),
  agentMessageId: ModelIdSchema,
  functionCallName: z5.string(),
  functionCallId: z5.string(),
  status: z5.string(),
  params: z5.record(z5.any()),
  step: z5.number(),
  executionDurationMs: z5.number().nullable(),
  citationsAllocated: z5.number(),
  output: CallToolResultSchema.shape.content.nullable(),
  generatedFiles: z5.array(ActionGeneratedFileSchema),
  displayLabels: DisplayLabelsSchema
});
var GlobalAgentStatusSchema = FlexibleEnumSchema();
var AgentStatusSchema = FlexibleEnumSchema();
var AgentConfigurationStatusSchema = z5.union([
  AgentStatusSchema,
  GlobalAgentStatusSchema
]);
var AgentConfigurationScopeSchema = FlexibleEnumSchema();
var AgentConfigurationViewSchema = FlexibleEnumSchema();
var AgentUsageTypeSchema = z5.object({
  messageCount: z5.number(),
  conversationCount: z5.number(),
  userCount: z5.number(),
  timePeriodSec: z5.number()
});
var AgentRecentAuthorsSchema = z5.array(z5.string()).readonly();
var AgentModelConfigurationSchema = z5.object({
  providerId: ModelProviderIdSchema,
  modelId: ModelLLMIdSchema,
  temperature: z5.number()
});
var LightAgentConfigurationSchema = z5.object({
  id: ModelIdSchema,
  versionCreatedAt: z5.string().nullable(),
  sId: z5.string(),
  version: z5.number(),
  versionAuthorId: ModelIdSchema.nullable(),
  instructions: z5.string().nullable(),
  model: AgentModelConfigurationSchema,
  status: AgentConfigurationStatusSchema,
  scope: AgentConfigurationScopeSchema,
  userFavorite: z5.boolean(),
  name: z5.string(),
  description: z5.string(),
  pictureUrl: z5.string(),
  lastAuthors: AgentRecentAuthorsSchema.optional(),
  usage: AgentUsageTypeSchema.optional(),
  maxStepsPerRun: z5.number(),
  // TODO(2025-10-20 flav): Remove once SDK JS does not rely on it anymore.
  visualizationEnabled: z5.boolean().optional(),
  templateId: z5.string().nullable(),
  groupIds: z5.array(z5.string()).optional(),
  requestedGroupIds: z5.array(z5.array(z5.string())).optional(),
  requestedSpaceIds: z5.array(z5.string()).optional()
});
var ContentFragmentContextSchema = z5.object({
  username: z5.string().optional().nullable(),
  fullName: z5.string().optional().nullable(),
  email: z5.string().optional().nullable(),
  profilePictureUrl: z5.string().optional().nullable()
});
var ContentNodeTypeSchema = z5.union([
  z5.literal("document"),
  z5.literal("table"),
  z5.literal("folder")
]);
var ContentNodesViewTypeSchema = z5.union([
  z5.literal("table"),
  z5.literal("document"),
  z5.literal("all")
]);
var ContentFragmentNodeData = z5.object({
  nodeId: z5.string(),
  nodeDataSourceViewId: z5.string(),
  nodeType: ContentNodeTypeSchema,
  provider: ConnectorProvidersSchema.nullable(),
  spaceName: z5.string()
});
var ContentFragmentExpiredReasonSchema = z5.union([
  z5.literal("data_source_deleted"),
  z5.literal("file_deleted")
]);
var BaseContentFragmentSchema = z5.object({
  type: z5.literal("content_fragment"),
  id: ModelIdSchema,
  sId: z5.string(),
  created: z5.number(),
  visibility: VisibilitySchema,
  version: z5.number(),
  sourceUrl: z5.string().nullable(),
  title: z5.string(),
  contentType: SupportedContentFragmentTypeSchema,
  context: ContentFragmentContextSchema,
  contentFragmentId: z5.string(),
  contentFragmentVersion: z5.union([
    z5.literal("latest"),
    z5.literal("superseded")
  ])
});
var FileContentFragmentSchema = z5.union([
  BaseContentFragmentSchema.extend({
    contentFragmentType: z5.literal("file"),
    expiredReason: z5.null(),
    fileId: z5.string().nullable(),
    snippet: z5.string().nullable(),
    generatedTables: z5.array(z5.string()),
    textUrl: z5.string(),
    textBytes: z5.number().nullable(),
    sourceProvider: z5.string().nullable(),
    sourceIcon: z5.string().nullable()
  }),
  BaseContentFragmentSchema.extend({
    contentFragmentType: z5.literal("file"),
    expiredReason: ContentFragmentExpiredReasonSchema,
    fileId: z5.null(),
    snippet: z5.null(),
    generatedTables: z5.array(z5.never()),
    textUrl: z5.null(),
    textBytes: z5.null(),
    sourceProvider: z5.null(),
    sourceIcon: z5.null()
  })
]);
var ContentNodeContentFragmentSchema = z5.union([
  BaseContentFragmentSchema.extend({
    contentFragmentType: z5.literal("content_node"),
    expiredReason: z5.null(),
    nodeId: z5.string(),
    nodeDataSourceViewId: z5.string(),
    nodeType: ContentNodeTypeSchema,
    contentNodeData: ContentFragmentNodeData
  }),
  BaseContentFragmentSchema.extend({
    contentFragmentType: z5.literal("content_node"),
    expiredReason: ContentFragmentExpiredReasonSchema,
    nodeId: z5.null(),
    nodeDataSourceViewId: z5.null(),
    nodeType: z5.null(),
    contentNodeData: z5.null()
  })
]);
var ContentFragmentSchema = z5.union([
  FileContentFragmentSchema,
  ContentNodeContentFragmentSchema
]);
var AgentMentionSchema = z5.object({
  //TODO(rcs): add a mentionType: "agent", but it requires some refactoring
  configurationId: z5.string()
});
var UserMentionSchema = z5.object({
  type: z5.literal("user"),
  userId: z5.string()
});
var MentionSchema = z5.union([AgentMentionSchema, UserMentionSchema]);
var UserMessageContextSchema = z5.object({
  username: z5.string(),
  timezone: Timezone,
  fullName: z5.string().optional().nullable(),
  email: z5.string().optional().nullable(),
  profilePictureUrl: z5.string().optional().nullable(),
  origin: UserMessageOriginSchema,
  clientSideMCPServerIds: z5.array(z5.string()).optional().nullable(),
  selectedMCPServerViewIds: z5.array(z5.string()).optional().nullable(),
  lastTriggerRunAt: z5.number().optional().nullable()
});
var AgenticMessageDataSchema = z5.object({
  type: z5.union([z5.literal("run_agent"), z5.literal("agent_handover")]),
  originMessageId: z5.string()
});
var UserMessageSchema = z5.object({
  id: ModelIdSchema,
  created: z5.number(),
  type: z5.literal("user_message"),
  sId: z5.string(),
  visibility: VisibilitySchema,
  version: z5.number(),
  user: UserSchema.nullable(),
  mentions: z5.array(MentionSchema),
  content: z5.string(),
  context: UserMessageContextSchema,
  agenticMessageData: AgenticMessageDataSchema.optional().nullable()
});
var UserMessageWithRankTypeSchema = UserMessageSchema.and(RankSchema);
var AgentMessageStatusSchema = FlexibleEnumSchema();
var AgentMessageTypeSchema = z5.object({
  id: ModelIdSchema,
  agentMessageId: ModelIdSchema,
  created: z5.number(),
  type: z5.literal("agent_message"),
  sId: z5.string(),
  visibility: VisibilitySchema,
  version: z5.number(),
  parentMessageId: z5.string().nullable(),
  parentAgentMessageId: z5.string().nullable(),
  configuration: LightAgentConfigurationSchema,
  status: AgentMessageStatusSchema,
  actions: z5.array(AgentActionTypeSchema),
  content: z5.string().nullable(),
  chainOfThought: z5.string().nullable(),
  rawContents: z5.array(
    z5.object({
      step: z5.number(),
      content: z5.string()
    })
  ),
  error: z5.object({
    code: z5.string(),
    message: z5.string(),
    metadata: z5.record(z5.any()).nullable()
  }).nullable()
});
function isAgentMessage2(message) {
  return AgentMessageTypeSchema.safeParse(message).success;
}
var AgentMessageFeedbackSchema = z5.object({
  messageId: z5.string(),
  agentMessageId: z5.number(),
  userId: z5.number(),
  thumbDirection: z5.union([z5.literal("up"), z5.literal("down")]),
  content: z5.string().nullable(),
  createdAt: z5.number(),
  agentConfigurationId: z5.string(),
  agentConfigurationVersion: z5.number(),
  isConversationShared: z5.boolean()
});
var ConversationVisibilitySchema = FlexibleEnumSchema();
var ConversationWithoutContentSchema = z5.object({
  id: ModelIdSchema,
  created: z5.number(),
  updated: z5.number().optional(),
  unread: z5.boolean(),
  actionRequired: z5.boolean(),
  owner: WorkspaceSchema,
  sId: z5.string(),
  title: z5.string().nullable(),
  visibility: ConversationVisibilitySchema,
  groupIds: z5.array(z5.string()).optional(),
  requestedGroupIds: z5.array(z5.array(z5.string())).optional()
  // Same as groupIds, should be removed once the chrome extension is updated
});
var ConversationSchema = ConversationWithoutContentSchema.extend({
  content: z5.array(
    z5.union([
      z5.array(UserMessageSchema),
      z5.array(AgentMessageTypeSchema),
      z5.array(ContentFragmentSchema)
    ])
  ),
  url: z5.string()
});
var ConversationForDataSourceSyncUserSchema = UserSchema.pick({
  sId: true,
  fullName: true,
  username: true,
  email: true
});
var ConversationForDataSourceSyncContentFragmentSchema = z5.object({
  type: z5.literal("content_fragment"),
  created: z5.number(),
  visibility: VisibilitySchema,
  contentFragmentId: z5.string(),
  contentType: SupportedContentFragmentTypeSchema,
  title: z5.string(),
  version: z5.number(),
  sourceUrl: z5.string().nullable()
});
var ConversationForDataSourceSyncUserMessageSchema = z5.object({
  type: z5.literal("user_message"),
  created: z5.number(),
  visibility: VisibilitySchema,
  user: ConversationForDataSourceSyncUserSchema.nullable(),
  content: z5.string(),
  contentFragments: z5.array(ConversationForDataSourceSyncContentFragmentSchema)
});
var ConversationForDataSourceSyncAgentMessageSchema = z5.object({
  type: z5.literal("agent_message"),
  created: z5.number(),
  visibility: VisibilitySchema,
  configuration: z5.object({
    name: z5.string()
  }),
  content: z5.string().nullable()
});
var ConversationForDataSourceSyncMessageSchema = z5.discriminatedUnion(
  "type",
  [
    ConversationForDataSourceSyncUserMessageSchema,
    ConversationForDataSourceSyncAgentMessageSchema
  ]
);
var ConversationForDataSourceSyncSchema = z5.object({
  sId: z5.string(),
  created: z5.number(),
  updated: z5.number().optional(),
  title: z5.string().nullable(),
  visibility: ConversationVisibilitySchema,
  url: z5.string(),
  content: z5.array(ConversationForDataSourceSyncMessageSchema)
});
var ConversationMessageReactionsSchema = z5.array(
  z5.object({
    messageId: z5.string(),
    reactions: z5.array(
      z5.object({
        emoji: z5.string(),
        users: z5.array(
          z5.object({
            userId: ModelIdSchema.nullable(),
            username: z5.string(),
            fullName: z5.string().nullable()
          })
        )
      })
    )
  })
);
var MCPStakeLevelSchema = z5.enum(["low", "medium", "high", "never_ask"]).optional();
var MCPValidationMetadataSchema = z5.object({
  agentName: z5.string(),
  icon: z5.union([MCPInternalActionIconSchema, MCPExternalActionIconSchema]).optional(),
  mcpServerName: z5.string(),
  pubsubMessageId: z5.string().optional(),
  toolName: z5.string()
});
var MCPParamsEventSchema = z5.object({
  type: z5.literal("tool_params"),
  created: z5.number(),
  configurationId: z5.string(),
  messageId: z5.string(),
  action: AgentActionTypeSchema
});
var NotificationImageContentSchema2 = z5.object({
  type: z5.literal("image"),
  mimeType: z5.string()
});
var NotificationTextContentSchema2 = z5.object({
  type: z5.literal("text"),
  text: z5.string()
});
var NotificationToolApproveBubbleUpContentSchema = z5.object({
  type: z5.literal("tool_approval_bubble_up"),
  configurationId: z5.string(),
  conversationId: z5.string(),
  messageId: z5.string(),
  actionId: z5.string(),
  inputs: z5.record(z5.any()),
  stake: MCPStakeLevelSchema,
  metadata: MCPValidationMetadataSchema
});
var NotificationRunAgentContentSchema2 = z5.object({
  type: z5.literal("run_agent"),
  childAgentId: z5.string(),
  conversationId: z5.string(),
  agentMessageId: z5.string().nullable().optional(),
  query: z5.string(),
  childConversationUrl: z5.string().nullable().optional(),
  childConversationEventsUrl: z5.string().nullable().optional()
});
var NotificationStoreResourceContentSchema2 = z5.object({
  type: z5.literal("store_resource"),
  contents: z5.array(
    z5.object({
      type: z5.literal("resource"),
      resource: z5.object({
        mimeType: z5.string(),
        text: z5.string(),
        uri: z5.string()
      }).passthrough()
    })
    // Allow additional properties
  )
});
var NotificationContentSchema = z5.union([
  NotificationInteractiveContentFileContentSchema,
  NotificationImageContentSchema2,
  NotificationRunAgentContentSchema2,
  NotificationStoreResourceContentSchema2,
  NotificationTextContentSchema2,
  NotificationToolApproveBubbleUpContentSchema
]);
var ToolNotificationProgressSchema = z5.object({
  progress: z5.number(),
  total: z5.number(),
  // This one is deprecated, use _meta.data instead
  data: z5.object({
    label: z5.string(),
    output: NotificationContentSchema.optional()
  }).optional(),
  _meta: z5.object({
    data: z5.object({
      label: z5.string(),
      output: NotificationContentSchema.optional()
    })
  })
});
var ToolNotificationEventSchema = z5.object({
  type: z5.literal("tool_notification"),
  created: z5.number(),
  configurationId: z5.string(),
  messageId: z5.string(),
  action: AgentActionTypeSchema,
  notification: ToolNotificationProgressSchema
});
var ToolExecutionBlockedStatusSchema = z5.enum([
  "blocked_authentication_required",
  "blocked_file_authorization_required",
  "blocked_validation_required",
  "blocked_child_action_input_required",
  "blocked_user_answer_required"
]);
var ToolExecutionMetadataSchema = z5.object({
  actionId: z5.string(),
  inputs: z5.record(z5.any()),
  stake: MCPStakeLevelSchema,
  metadata: MCPValidationMetadataSchema.extend({
    mcpServerDisplayName: z5.string().optional(),
    mcpServerId: z5.string().optional()
  }),
  argumentsRequiringApproval: z5.array(z5.string()).optional(),
  approvalArgsLabel: z5.string().optional()
});
var BlockedActionExecutionSchema = ToolExecutionMetadataSchema.extend({
  messageId: z5.string(),
  conversationId: z5.string(),
  status: ToolExecutionBlockedStatusSchema,
  // Present only when status is "blocked_user_answer_required".
  question: UserQuestionItemSchema.optional()
});
var MCPApproveExecutionEventSchema = ToolExecutionMetadataSchema.extend({
  type: z5.literal("tool_approve_execution"),
  userId: z5.string().optional(),
  configurationId: z5.string(),
  conversationId: z5.string(),
  created: z5.number(),
  isLastBlockingEventForStep: z5.boolean().optional(),
  messageId: z5.string()
});
var AuthErrorSchema = z5.object({
  mcpServerId: z5.string(),
  message: z5.string(),
  provider: OAuthProviderSchema,
  scope: z5.string().optional(),
  toolName: z5.string()
});
var ToolPersonalAuthRequiredEventSchema = ToolExecutionMetadataSchema.extend({
  type: z5.literal("tool_personal_auth_required"),
  configurationId: z5.string(),
  conversationId: z5.string(),
  created: z5.number(),
  authError: AuthErrorSchema,
  isLastBlockingEventForStep: z5.boolean().optional(),
  messageId: z5.string(),
  metadata: MCPValidationMetadataSchema.extend({
    mcpServerDisplayName: z5.string(),
    mcpServerId: z5.string()
  })
});
var FileAuthErrorSchema = z5.object({
  fileId: z5.string(),
  fileName: z5.string(),
  connectionId: z5.string(),
  mimeType: z5.string(),
  toolName: z5.string(),
  message: z5.string()
});
var ToolFileAuthRequiredEventSchema = ToolExecutionMetadataSchema.extend({
  type: z5.literal("tool_file_auth_required"),
  configurationId: z5.string(),
  conversationId: z5.string(),
  created: z5.number(),
  fileAuthError: FileAuthErrorSchema,
  isLastBlockingEventForStep: z5.boolean().optional(),
  messageId: z5.string(),
  metadata: MCPValidationMetadataSchema.extend({
    mcpServerDisplayName: z5.string(),
    mcpServerId: z5.string()
  })
});
var ToolErrorEventSchema = z5.object({
  type: z5.literal("tool_error"),
  created: z5.number(),
  configurationId: z5.string(),
  isLastBlockingEventForStep: z5.boolean().optional(),
  messageId: z5.string(),
  error: z5.object({
    code: z5.string(),
    message: z5.string(),
    metadata: z5.record(z5.any()).nullable()
  })
});
var AgentErrorEventSchema = z5.object({
  type: z5.literal("agent_error"),
  created: z5.number(),
  configurationId: z5.string(),
  messageId: z5.string(),
  error: z5.object({
    code: z5.string(),
    message: z5.string(),
    metadata: z5.record(z5.any()).nullable()
  }),
  runIds: z5.array(z5.string()).optional()
});
var ToolAskUserQuestionEventSchema = ToolExecutionMetadataSchema.extend({
  type: z5.literal("tool_ask_user_question"),
  userId: z5.string().optional(),
  configurationId: z5.string(),
  conversationId: z5.string(),
  created: z5.number(),
  messageId: z5.string(),
  question: UserQuestionItemSchema
});
var AgentActionSpecificEventSchema = z5.union([
  MCPParamsEventSchema,
  ToolNotificationEventSchema,
  MCPApproveExecutionEventSchema,
  ToolPersonalAuthRequiredEventSchema,
  ToolAskUserQuestionEventSchema,
  ToolFileAuthRequiredEventSchema
]);
var AgentToolCallStartedEventSchema = z5.object({
  type: z5.literal("tool_call_started"),
  created: z5.number(),
  configurationId: z5.string(),
  messageId: z5.string(),
  toolCallId: z5.string().optional(),
  toolCallIndex: z5.number().optional(),
  toolName: z5.string()
});
var AgentActionSuccessEventSchema = z5.object({
  type: z5.literal("agent_action_success"),
  created: z5.number(),
  configurationId: z5.string(),
  messageId: z5.string(),
  action: AgentActionTypeSchema
});
var AgentMessageSuccessEventSchema = z5.object({
  type: z5.literal("agent_message_success"),
  created: z5.number(),
  configurationId: z5.string(),
  messageId: z5.string(),
  message: AgentMessageTypeSchema,
  runIds: z5.array(z5.string())
});
var AgentMessageGracefullyStoppedEventSchema = z5.object({
  type: z5.literal("agent_message_gracefully_stopped"),
  created: z5.number(),
  configurationId: z5.string(),
  messageId: z5.string(),
  message: AgentMessageTypeSchema,
  runIds: z5.array(z5.string())
});
var AgentMessageDoneEventSchema = z5.object({
  type: z5.literal("agent_message_done"),
  created: z5.number(),
  conversationId: z5.string(),
  configurationId: z5.string(),
  messageId: z5.string()
});
var AgentGenerationCancelledEventSchema = z5.object({
  type: z5.literal("agent_generation_cancelled"),
  created: z5.number(),
  configurationId: z5.string(),
  messageId: z5.string()
});
var AgentContextPrunedEventSchema = z5.object({
  type: z5.literal("agent_context_pruned"),
  created: z5.number(),
  configurationId: z5.string(),
  messageId: z5.string()
});
var UserMessageErrorEventSchema = z5.object({
  type: z5.literal("user_message_error"),
  created: z5.number(),
  error: z5.object({
    code: z5.string(),
    message: z5.string()
  })
});
var UserMessageNewEventSchema = z5.object({
  type: z5.literal("user_message_new"),
  created: z5.number(),
  messageId: z5.string(),
  message: UserMessageSchema.and(RankSchema)
});
var UserMessagePromotedEventSchema = z5.object({
  type: z5.literal("user_message_promoted"),
  created: z5.number(),
  messageId: z5.string()
});
var AgentMessageNewEventSchema = z5.object({
  type: z5.literal("agent_message_new"),
  created: z5.number(),
  configurationId: z5.string(),
  messageId: z5.string(),
  message: AgentMessageTypeSchema.and(RankSchema)
});
var ConversationTitleEventSchema = z5.object({
  type: z5.literal("conversation_title"),
  created: z5.number(),
  title: z5.string()
});
var ConversationEventDataSchema = z5.union([
  UserMessageNewEventSchema,
  UserMessagePromotedEventSchema,
  AgentMessageNewEventSchema,
  AgentMessageDoneEventSchema,
  ConversationTitleEventSchema
]);
var ConversationEventTypeSchema = z5.object({
  eventId: z5.string(),
  data: ConversationEventDataSchema
});
var AgentMessageEventDataSchema = z5.union([
  AgentActionSpecificEventSchema,
  AgentActionSuccessEventSchema,
  AgentContextPrunedEventSchema,
  AgentErrorEventSchema,
  AgentGenerationCancelledEventSchema,
  AgentMessageDoneEventSchema,
  AgentMessageGracefullyStoppedEventSchema,
  AgentMessageSuccessEventSchema,
  AgentToolCallStartedEventSchema,
  GenerationTokensEventSchema,
  ToolErrorEventSchema,
  UserMessageErrorEventSchema
]);
var AgentMessageEventTypeSchema = z5.object({
  eventId: z5.string(),
  data: AgentMessageEventDataSchema
});
var CoreAPIErrorSchema = z5.object({
  message: z5.string(),
  code: z5.string()
});
var CoreAPITokenTypeSchema = z5.tuple([z5.number(), z5.string()]);
var APIErrorTypeSchema = FlexibleEnumSchema();
var APIErrorSchema = z5.object({
  type: APIErrorTypeSchema,
  message: z5.string(),
  data_source_error: CoreAPIErrorSchema.optional(),
  run_error: CoreAPIErrorSchema.optional(),
  app_error: CoreAPIErrorSchema.optional(),
  connectors_error: ConnectorsAPIErrorSchema.optional()
});
var WorkspaceDomainSchema = z5.object({
  domain: z5.string(),
  domainAutoJoinEnabled: z5.boolean()
});
var DustAppTypeSchema = z5.object({
  appHash: z5.string(),
  appId: z5.string(),
  workspaceId: z5.string()
});
var DustAppConfigTypeSchema = z5.record(z5.unknown());
var DustAppRunErroredEventSchema = z5.object({
  type: z5.literal("error"),
  content: z5.object({
    code: z5.string(),
    message: z5.string()
  })
});
var DustAppRunRunStatusEventSchema = z5.object({
  type: z5.literal("run_status"),
  content: z5.object({
    status: z5.enum(["running", "succeeded", "errored"]),
    run_id: z5.string()
  })
});
var DustAppRunBlockStatusEventSchema = z5.object({
  type: z5.literal("block_status"),
  content: z5.object({
    block_type: BlockTypeSchema,
    name: z5.string(),
    status: StatusSchema,
    success_count: z5.number(),
    error_count: z5.number()
  })
});
var DustAppRunBlockExecutionEventSchema = z5.object({
  type: z5.literal("block_execution"),
  content: z5.object({
    block_type: BlockTypeSchema,
    block_name: z5.string(),
    execution: z5.array(
      z5.array(
        z5.object({
          value: z5.unknown().nullable(),
          error: z5.string().nullable(),
          meta: z5.unknown().nullable()
        })
      )
    )
  })
});
var DustAppRunFinalEventSchema = z5.object({
  type: z5.literal("final")
});
var DustAppRunTokensEventSchema = z5.object({
  type: z5.literal("tokens"),
  content: z5.object({
    block_type: z5.string(),
    block_name: z5.string(),
    input_index: z5.number(),
    map: z5.object({
      name: z5.string(),
      iteration: z5.number()
    }).nullable(),
    tokens: z5.object({
      text: z5.string(),
      tokens: z5.array(z5.string()).optional(),
      logprobs: z5.array(z5.number()).optional()
    })
  })
});
var DustAppRunReasoningTokensEventSchema = z5.object({
  type: z5.literal("reasoning_tokens"),
  content: z5.object({
    block_type: z5.string(),
    block_name: z5.string(),
    input_index: z5.number(),
    map: z5.object({
      name: z5.string(),
      iteration: z5.number()
    }).nullable(),
    tokens: z5.object({
      text: z5.string()
    })
  })
});
var DustAppRunReasoningItemEventSchema = z5.object({
  type: z5.literal("reasoning_item"),
  content: z5.object({
    block_type: z5.string(),
    block_name: z5.string(),
    input_index: z5.number(),
    map: z5.object({
      name: z5.string(),
      iteration: z5.number()
    }).nullable(),
    item: z5.unknown()
  })
});
var DustAppRunFunctionCallEventSchema = z5.object({
  type: z5.literal("function_call"),
  content: z5.object({
    block_type: z5.string(),
    block_name: z5.string(),
    input_index: z5.number(),
    map: z5.object({
      name: z5.string(),
      iteration: z5.number()
    }).nullable(),
    function_call: z5.object({
      name: z5.string()
    })
  })
});
var DustAppRunFunctionCallArgumentsTokensEventSchema = z5.object({
  type: z5.literal("function_call_arguments_tokens"),
  content: z5.object({
    block_type: z5.string(),
    block_name: z5.string(),
    input_index: z5.number(),
    map: z5.object({
      name: z5.string(),
      iteration: z5.number()
    }).nullable(),
    tokens: z5.object({
      text: z5.string()
    })
  })
});
var SpaceKindSchema = FlexibleEnumSchema();
var SpaceTypeSchema = z5.object({
  createdAt: z5.number(),
  groupIds: z5.array(z5.string()),
  isRestricted: z5.boolean(),
  kind: SpaceKindSchema,
  name: z5.string(),
  sId: z5.string(),
  updatedAt: z5.number()
});
var DatasetSchemaEntryType = FlexibleEnumSchema();
var DatasetSchema = z5.object({
  name: z5.string(),
  description: z5.string().nullable(),
  data: z5.array(z5.record(z5.any())).nullable().optional(),
  schema: z5.array(
    z5.object({
      key: z5.string(),
      type: DatasetSchemaEntryType,
      description: z5.string().nullable()
    })
  ).nullable().optional()
});
var AppTypeSchema = z5.object({
  id: ModelIdSchema,
  sId: z5.string(),
  name: z5.string(),
  description: z5.string().nullable(),
  savedSpecification: z5.string().nullable(),
  savedConfig: z5.string().nullable(),
  savedRun: z5.string().nullable(),
  dustAPIProjectId: z5.string(),
  space: SpaceTypeSchema,
  datasets: z5.array(DatasetSchema).optional(),
  coreSpecifications: z5.record(z5.string()).optional()
});
var AppImportTypeSchema = z5.object({
  id: ModelIdSchema.optional(),
  sId: z5.string(),
  name: z5.string(),
  description: z5.string().nullable(),
  savedSpecification: z5.string().nullable(),
  savedConfig: z5.string().nullable(),
  savedRun: z5.string().nullable(),
  dustAPIProjectId: z5.string(),
  datasets: z5.array(DatasetSchema).optional(),
  coreSpecifications: z5.record(z5.string()).optional()
});
var RunAppResponseSchema = z5.object({
  run: RunTypeSchema
});
var GetDataSourcesResponseSchema = z5.object({
  data_sources: DataSourceTypeSchema.array()
});
var GetOrPatchAgentConfigurationResponseSchema = z5.object({
  agentConfiguration: LightAgentConfigurationSchema,
  skippedActions: z5.array(z5.object({ name: z5.string(), reason: z5.string() })).optional()
});
var PatchAgentConfigurationRequestSchema = z5.object({
  userFavorite: z5.boolean().optional()
}).passthrough();
var DeleteAgentConfigurationResponseSchema = z5.object({
  success: z5.boolean()
});
var GetAgentConfigurationYAMLExportResponseSchema = z5.string();
var ImportAgentConfigurationFromYAMLResponseSchema = z5.object({
  agentConfiguration: LightAgentConfigurationSchema,
  skippedActions: z5.array(z5.object({ name: z5.string(), reason: z5.string() }))
});
var GetAgentConfigurationsResponseSchema = z5.object({
  agentConfigurations: LightAgentConfigurationSchema.array()
});
var PostContentFragmentResponseSchema = z5.object({
  contentFragment: ContentFragmentSchema
});
var CreateConversationResponseSchema = z5.object({
  conversation: ConversationSchema,
  message: UserMessageSchema.optional()
});
var GetFeedbacksResponseSchema = z5.object({
  feedbacks: z5.array(AgentMessageFeedbackSchema)
});
var PublicPostMessageFeedbackRequestBodySchema = z5.object({
  thumbDirection: z5.string(),
  feedbackContent: z5.string().nullable().optional(),
  isConversationShared: z5.boolean().optional()
});
var PostMessageFeedbackResponseSchema = z5.object({
  success: z5.literal(true)
});
var PostUserMessageResponseSchema = z5.object({
  message: UserMessageSchema
});
var RetryMessageResponseSchema = z5.object({
  message: AgentMessageTypeSchema
});
var GetConversationResponseSchema = z5.object({
  conversation: ConversationSchema,
  hasMore: z5.boolean().optional(),
  lastValue: z5.string().nullable().optional()
});
var PatchConversationRequestSchema = z5.union([
  z5.object({
    read: z5.boolean()
  }),
  z5.object({
    title: z5.string()
  })
]);
var PatchConversationResponseSchema = z5.object({
  success: z5.boolean()
});
var TokenizeResponseSchema = z5.object({
  tokens: CoreAPITokenTypeSchema.array()
});
var GetActiveMemberEmailsInWorkspaceResponseSchema = z5.object({
  emails: z5.array(z5.string())
});
var GetWorkspaceVerifiedDomainsResponseSchema = z5.object({
  verified_domains: WorkspaceDomainSchema.array()
});
var GetWorkspaceFeatureFlagsResponseSchema = z5.object({
  feature_flags: WhitelistableFeaturesSchema.array()
});
var PublicPostMessagesRequestBodySchema = z5.intersection(
  z5.object({
    content: z5.string().min(1),
    mentions: z5.array(MentionSchema),
    context: UserMessageContextSchema.extend({
      clientSideMCPServerIds: z5.array(z5.string()).optional().nullable()
    }),
    agenticMessageData: AgenticMessageDataSchema.optional()
  }),
  z5.object({
    blocking: z5.boolean().optional(),
    skipToolsValidation: z5.boolean().optional()
  }).partial()
);
var PublicPostEditMessagesRequestBodySchema = z5.object({
  content: z5.string(),
  mentions: z5.array(MentionSchema),
  skipToolsValidation: z5.boolean().optional().default(false)
});
var PublicContentFragmentWithContentSchema = z5.object({
  title: z5.string(),
  url: z5.string().optional().nullable(),
  content: z5.string(),
  contentType: z5.string(),
  fileId: z5.undefined().nullable(),
  nodeId: z5.undefined().nullable(),
  nodeDataSourceViewId: z5.undefined().nullable(),
  context: ContentFragmentContextSchema.optional().nullable(),
  // Undocumented for now -- allows to supersede an existing content fragment.
  supersededContentFragmentId: z5.string().optional().nullable()
});
var PublicContentFragmentWithFileIdSchema = z5.object({
  title: z5.string(),
  fileId: z5.string(),
  url: z5.string().optional().nullable(),
  content: z5.undefined().nullable(),
  contentType: z5.undefined().nullable(),
  nodeId: z5.undefined().nullable(),
  nodeDataSourceViewId: z5.undefined().nullable(),
  context: ContentFragmentContextSchema.optional().nullable(),
  // Undocumented for now -- allows to supersede an existing content fragment.
  supersededContentFragmentId: z5.string().optional().nullable()
});
var PublicContentFragmentWithContentNodeSchema = z5.object({
  title: z5.string(),
  nodeId: z5.string(),
  nodeDataSourceViewId: z5.string(),
  url: z5.undefined().nullable(),
  content: z5.undefined().nullable(),
  contentType: z5.undefined().nullable(),
  fileId: z5.undefined().nullable(),
  context: ContentFragmentContextSchema.optional().nullable(),
  supersededContentFragmentId: z5.string().optional().nullable()
});
var PublicPostContentFragmentRequestBodySchema = z5.union([
  PublicContentFragmentWithContentSchema,
  PublicContentFragmentWithFileIdSchema,
  PublicContentFragmentWithContentNodeSchema
]);
var PublicPostConversationsRequestBodySchema = z5.intersection(
  z5.object({
    title: z5.string().nullable().optional(),
    visibility: z5.enum(["workspace", "unlisted", "deleted", "test"]).optional().default("unlisted"),
    depth: z5.number().optional(),
    spaceId: z5.string().optional(),
    message: z5.union([
      z5.intersection(
        z5.object({
          content: z5.string().min(1),
          mentions: z5.array(MentionSchema),
          context: UserMessageContextSchema,
          agenticMessageData: AgenticMessageDataSchema.optional()
        }),
        z5.object({
          blocking: z5.boolean().optional()
        }).partial()
      ),
      z5.undefined()
    ]),
    contentFragment: z5.union([
      PublicContentFragmentWithContentSchema,
      PublicContentFragmentWithFileIdSchema,
      PublicContentFragmentWithContentNodeSchema,
      z5.undefined()
    ]),
    contentFragments: z5.union([
      z5.union([
        PublicContentFragmentWithContentSchema,
        PublicContentFragmentWithFileIdSchema,
        PublicContentFragmentWithContentNodeSchema
      ]).array(),
      z5.undefined()
    ])
  }),
  z5.object({
    blocking: z5.boolean().optional(),
    skipToolsValidation: z5.boolean().optional()
  }).partial()
);
var PostConversationsResponseSchema = z5.object({
  conversation: ConversationSchema,
  message: UserMessageSchema.optional(),
  contentFragment: ContentFragmentSchema.optional()
});
var GetConversationsResponseSchema = z5.object({
  conversations: ConversationWithoutContentSchema.array()
});
var SearchDataSourceViewsRequestSchema = z5.object({
  dataSourceId: z5.string().optional(),
  kind: z5.string().optional(),
  vaultId: z5.string().optional(),
  vaultKind: z5.string().optional()
});
var SearchDataSourceViewsResponseSchema = z5.object({
  data_source_views: DataSourceViewSchema.array()
});
var ListMemberEmailsResponseSchema = z5.object({
  emails: z5.array(z5.string())
});
var ValidateMemberRequestSchema = z5.object({
  email: z5.string()
});
var ValidateMemberResponseSchema = z5.object({
  valid: z5.boolean()
});
var GetAppsResponseSchema = z5.object({
  apps: AppTypeSchema.array()
});
var PostAppsRequestSchema = z5.object({
  apps: AppImportTypeSchema.array()
});
var ImportAppsResponseSchema = z5.object({
  apps: z5.object({
    sId: z5.string(),
    name: z5.string(),
    error: z5.string().optional()
  }).array()
});
var DataSourceViewResponseSchema = z5.object({
  dataSourceView: DataSourceViewSchema
});
var PatchDataSourceViewRequestSchema = z5.union([
  z5.object({
    parentsToAdd: z5.union([z5.array(z5.string()), z5.undefined()]),
    parentsToRemove: z5.array(z5.string()).optional()
  }).transform((o) => ({
    parentsToAdd: o.parentsToAdd,
    parentsToRemove: o.parentsToRemove
  })),
  z5.object({
    parentsIn: z5.array(z5.string())
  })
]);
var DataSourceSearchQuerySchema = z5.object({
  query: z5.string(),
  top_k: z5.coerce.number(),
  full_text: z5.coerce.boolean(),
  target_document_tokens: z5.coerce.number().optional(),
  timestamp_gt: z5.coerce.number().optional(),
  timestamp_lt: z5.coerce.number().optional(),
  tags_in: z5.array(z5.string()).optional(),
  tags_not: z5.array(z5.string()).optional(),
  parents_in: z5.array(z5.string()).optional(),
  parents_not: z5.array(z5.string()).optional()
});
var DataSourceSearchResponseSchema = z5.object({
  documents: CoreAPIDocumentSchema.array()
});
var DataSourceViewsListResponseSchema = z5.object({
  dataSourceViews: DataSourceViewSchema.array()
});
var FrontDataSourceDocumentSectionSchema = z5.lazy(
  () => z5.object({
    prefix: z5.string().nullable(),
    content: z5.string().nullable(),
    sections: z5.array(FrontDataSourceDocumentSectionSchema)
  })
);
var PostDataSourceDocumentRequestSchema = z5.object({
  timestamp: z5.number().nullable().optional(),
  tags: z5.array(z5.string()).nullable().optional(),
  parent_id: z5.string().nullable().optional(),
  parents: z5.array(z5.string()).nullable().optional(),
  source_url: z5.string().nullable().optional(),
  upsert_context: z5.object({
    sync_type: z5.union([z5.enum(["batch", "incremental"]), z5.undefined()])
  }).transform((o) => ({
    sync_type: o.sync_type
  })).optional(),
  text: z5.string().nullable().optional(),
  section: FrontDataSourceDocumentSectionSchema.nullable().optional(),
  light_document_output: z5.boolean().optional(),
  async: z5.boolean().nullable().optional(),
  mime_type: z5.string().nullable().optional(),
  title: z5.string().nullable().optional()
});
var GetDocumentResponseSchema = z5.object({
  document: CoreAPIDocumentSchema
});
var CoreAPIDataSourceDocumentBlobSchema = z5.object({
  document_id: z5.string(),
  mime_type: z5.string(),
  parent_id: z5.string().nullable(),
  parents: z5.array(z5.string()),
  section: FrontDataSourceDocumentSectionSchema,
  source_url: z5.string().nullable(),
  tags: z5.array(z5.string()),
  timestamp: z5.number(),
  title: z5.string()
});
var GetDocumentBlobResponseSchema = z5.object({
  blob: CoreAPIDataSourceDocumentBlobSchema
});
var DeleteDocumentResponseSchema = z5.object({
  document: z5.object({
    document_id: z5.string()
  })
});
var UpsertDocumentResponseSchema = z5.object({
  document: z5.union([
    CoreAPIDocumentSchema,
    CoreAPILightDocumentSchema,
    z5.object({
      document_id: z5.string()
    })
  ]),
  data_source: DataSourceTypeSchema
});
var PostParentsResponseSchema = z5.object({
  updated: z5.boolean()
});
var CheckUpsertQueueResponseSchema = z5.object({
  running_count: z5.number()
});
var GetSpaceConversationsForDataSourceResponseSchema = z5.object({
  conversations: z5.array(ConversationForDataSourceSyncSchema)
});
var GetSpaceConversationIdsResponseSchema = z5.object({
  conversationIds: z5.array(z5.string())
});
var ProjectMetadataSchema = z5.object({
  sId: z5.string(),
  createdAt: z5.number(),
  updatedAt: z5.number(),
  spaceId: z5.string(),
  description: z5.string().nullable(),
  archivedAt: z5.number().nullable().optional(),
  todoGenerationEnabled: z5.boolean().optional(),
  lastTodoAnalysisAt: z5.number().nullable().optional(),
  members: z5.array(z5.string())
});
var GetSpaceMetadataResponseSchema = z5.object({
  metadata: ProjectMetadataSchema.nullable()
});
var ProjectMountFileEntrySchema = z5.object({
  isDirectory: z5.literal(false),
  fileName: z5.string(),
  path: z5.string(),
  sizeBytes: z5.number(),
  lastModifiedMs: z5.number(),
  contentType: z5.string(),
  fileId: z5.string().nullable(),
  thumbnailUrl: z5.string().nullable(),
  signedDownloadUrl: z5.string().nullable()
});
var ProjectMountDirectoryEntrySchema = z5.object({
  isDirectory: z5.literal(true),
  fileName: z5.string(),
  path: z5.string(),
  sizeBytes: z5.number(),
  lastModifiedMs: z5.number()
});
var ProjectMountListEntrySchema = z5.discriminatedUnion("isDirectory", [
  ProjectMountDirectoryEntrySchema,
  ProjectMountFileEntrySchema
]);
var GetProjectFilesResponseSchema = z5.object({
  files: z5.array(ProjectMountListEntrySchema)
});
var GetDocumentsResponseSchema = z5.object({
  documents: z5.array(CoreAPIDocumentSchema),
  total: z5.number()
});
var GetTableRowsResponseSchema = z5.object({
  row: CoreAPIRowSchema
});
var UpsertTableRowsRequestSchema = z5.object({
  rows: z5.array(
    z5.object({
      row_id: z5.string(),
      value: z5.record(
        z5.union([
          z5.string(),
          z5.number(),
          z5.boolean(),
          z5.object({
            type: z5.literal("datetime"),
            epoch: z5.number()
          })
        ]).nullable()
      )
    })
  ),
  truncate: z5.boolean().optional()
});
var UpsertTableRowsResponseSchema = z5.object({
  table: z5.object({
    name: z5.string(),
    table_id: z5.string(),
    description: z5.string(),
    schema: CoreAPITableSchema.nullable()
  })
});
var ListTableRowsResponseSchema = z5.object({
  rows: z5.array(CoreAPIRowSchema),
  offset: z5.number(),
  limit: z5.number(),
  total: z5.number()
});
var GetTableResponseSchema = z5.object({
  table: CoreAPITablePublicSchema
});
var PostTableParentsRequestSchema = z5.object({
  parent_id: z5.string().nullable().optional(),
  parents: z5.array(z5.string())
});
var PostTableParentsResponseSchema = z5.object({
  updated: z5.literal(true)
});
var UpsertTableFromCsvRequestSchema = z5.object({
  name: z5.string(),
  description: z5.string(),
  timestamp: z5.number().nullable().optional(),
  tags: z5.array(z5.string()).nullable().optional(),
  parentId: z5.string().nullable().optional(),
  parents: z5.array(z5.string()).nullable().optional(),
  truncate: z5.boolean(),
  async: z5.boolean().optional(),
  title: z5.string(),
  mimeType: z5.string(),
  sourceUrl: z5.string().nullable().optional(),
  tableId: z5.string(),
  fileId: z5.string(),
  allowEmptySchema: z5.boolean().optional()
});
var PostTableCSVAsyncResponseSchema = z5.object({
  table: z5.object({
    table_id: z5.string()
  })
});
var PostTableCSVResponseSchema = z5.object({
  table: CoreAPITableSchema
});
var ListTablesResponseSchema = z5.object({
  tables: z5.array(CoreAPITablePublicSchema)
});
var UpsertDatabaseTableRequestSchema = z5.object({
  table_id: z5.string().optional(),
  name: z5.string(),
  description: z5.string(),
  timestamp: z5.number().nullable().optional(),
  tags: z5.array(z5.string()).nullable().optional(),
  parent_id: z5.string().nullable().optional(),
  parents: z5.array(z5.string()).nullable().optional(),
  remote_database_table_id: z5.string().nullable().optional(),
  remote_database_secret_id: z5.string().nullable().optional(),
  title: z5.string(),
  mime_type: z5.string().nullable().optional(),
  source_url: z5.string().nullable().optional()
});
var UpsertTableResponseSchema = z5.object({
  table: CoreAPITablePublicSchema
});
var SupportedUsageTablesSchema = z5.enum([
  "users",
  "assistant_messages",
  "builders",
  "assistants",
  "feedback",
  "all"
]);
var CoreAPIFolderSchema = z5.object({
  data_source_id: z5.string(),
  folder_id: z5.string(),
  title: z5.string(),
  parents: z5.array(z5.string()),
  timestamp: z5.number()
});
var GetFoldersResponseSchema = z5.object({
  folders: z5.array(CoreAPIFolderSchema),
  total: z5.number()
});
var GetFolderResponseSchema = z5.object({
  folder: CoreAPIFolderSchema
});
var DeleteFolderResponseSchema = z5.object({
  folder: z5.object({
    folder_id: z5.string()
  })
});
var UpsertFolderResponseSchema = z5.object({
  folder: CoreAPIFolderSchema,
  data_source: DataSourceTypeSchema
});
var ProviderVisibilitySchema = FlexibleEnumSchema();
var UpsertDataSourceFolderRequestSchema = z5.object({
  timestamp: z5.number(),
  parents: z5.array(z5.string()).nullable().optional(),
  parent_id: z5.string().nullable().optional(),
  title: z5.string(),
  mime_type: z5.string(),
  source_url: z5.string().nullable().optional(),
  provider_visibility: ProviderVisibilitySchema.nullable().optional()
});
var DateSchema = z5.string().refine(
  (s) => /^\d{4}-(0[1-9]|1[0-2])(-([0-2]\d|3[01]))?$/.test(s),
  "YYYY-MM or YYYY-MM-DD"
);
var IncludeInactiveSchema = z5.preprocess((value) => {
  if (value === void 0) {
    return void 0;
  }
  if (Array.isArray(value)) {
    const [first] = value;
    if (first === void 0) {
      return void 0;
    }
    return first === "true" || first === true;
  }
  if (typeof value === "string") {
    return value === "true";
  }
  if (typeof value === "boolean") {
    return value;
  }
  return void 0;
}, z5.boolean().optional());
var GetWorkspaceUsageRequestSchema = z5.union([
  z5.object({
    start: DateSchema,
    end: z5.undefined(),
    mode: z5.literal("month"),
    table: SupportedUsageTablesSchema,
    format: z5.enum(["csv", "json"]).optional().default("csv"),
    includeInactive: IncludeInactiveSchema
  }),
  z5.object({
    start: DateSchema,
    end: DateSchema,
    mode: z5.literal("range"),
    table: SupportedUsageTablesSchema,
    format: z5.enum(["csv", "json"]).optional().default("csv"),
    includeInactive: IncludeInactiveSchema
  })
]);
var GetWorkspaceUsageResponseSchema = z5.string().or(z5.undefined()).or(z5.instanceof(Buffer));
var AnalyticsExportTableSchema = z5.enum([
  "usage_metrics",
  "active_users",
  "source",
  "agents",
  "users",
  "skills",
  "skill_usage",
  "tool_usage",
  "messages",
  "feedback"
]);
var AnalyticsDateSchema = z5.string().refine(
  (s) => /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/.test(s),
  { message: "Date must be in YYYY-MM-DD format" }
);
var GetAnalyticsExportRequestSchema = z5.object({
  table: AnalyticsExportTableSchema,
  startDate: AnalyticsDateSchema,
  endDate: AnalyticsDateSchema,
  timezone: Timezone.optional(),
  format: z5.enum(["csv", "json"]).optional()
}).refine((d) => d.startDate <= d.endDate, {
  message: "startDate must be before or equal to endDate"
});
var FileUploadUrlRequestSchema = z5.object({
  contentType: SupportedFileContentFragmentTypeSchema,
  fileName: z5.string().max(4096, "File name must be less than 4096 characters"),
  fileSize: z5.number(),
  useCase: z5.union([z5.literal("conversation"), z5.literal("upsert_table")]),
  useCaseMetadata: z5.object({
    conversationId: z5.string(),
    sourceProvider: z5.string().optional(),
    sourceIcon: z5.string().optional()
  }).optional()
});
var FileTypeStatusSchema = FlexibleEnumSchema();
var FileTypeUseCaseSchema = FlexibleEnumSchema();
var FileTypeSchema = z5.object({
  // TODO(spolu): move this to ModelIdSchema
  id: z5.string(),
  sId: z5.string(),
  contentType: z5.string(),
  downloadUrl: z5.string().optional(),
  fileName: z5.string(),
  fileSize: z5.number(),
  status: FileTypeStatusSchema,
  uploadUrl: z5.string().optional(),
  publicUrl: z5.string().optional(),
  useCase: FileTypeUseCaseSchema
});
var FileTypeWithUploadUrlSchema = FileTypeSchema.extend({
  uploadUrl: z5.string()
});
var FileUploadRequestResponseSchema = z5.object({
  file: FileTypeWithUploadUrlSchema
});
var FileUploadedRequestResponseSchema = z5.object({
  file: FileTypeSchema
});
var PublicFrameResponseBodySchema = z5.object({
  accessToken: z5.string(),
  conversationUrl: z5.string().nullable(),
  projectUrl: z5.string().nullable(),
  file: FileTypeSchema
});
var PublicVizContentResponseBodySchema = z5.object({
  content: z5.string(),
  contentType: z5.string(),
  metadata: z5.record(z5.unknown()).optional()
});
var MembershipOriginType = FlexibleEnumSchema();
var WorkOSOrganizationSchema = z5.object({
  id: z5.string(),
  name: z5.string(),
  createdAt: z5.string(),
  updatedAt: z5.string(),
  externalId: z5.string().nullable(),
  metadata: z5.record(z5.string())
});
var MeResponseSchema = z5.object({
  user: UserSchema.and(
    z5.object({
      workspaces: WorkspaceSchema.array().or(ExtensionWorkspaceSchema.array()),
      organizations: WorkOSOrganizationSchema.array().optional(),
      origin: MembershipOriginType.optional(),
      selectedWorkspace: z5.string().optional()
    })
  )
});
var CancelMessageGenerationResponseSchema = z5.object({
  success: z5.literal(true)
});
var CancelMessageGenerationRequestSchema = z5.object({
  messageIds: z5.array(z5.string())
});
function isAgentMention(arg) {
  return arg.configurationId !== void 0;
}
function assertNever(x) {
  throw new Error(
    `${typeof x === "object" ? JSON.stringify(x) : x} is not of type never. This should never happen.`
  );
}
function assertNeverAndIgnore(_x) {
}
function removeNulls(arr) {
  return arr.filter((v) => v !== null && v !== void 0);
}
function getProviderFromRetrievedDocument(document) {
  if (document.dataSourceView) {
    if (document.dataSourceView.dataSource.connectorProvider === "webcrawler") {
      return "document";
    }
    return document.dataSourceView.dataSource.connectorProvider || "document";
  }
  return "document";
}
function getTitleFromRetrievedDocument(document) {
  const provider = getProviderFromRetrievedDocument(document);
  if (provider === "slack") {
    for (const t of document.tags) {
      if (t.startsWith("channelName:")) {
        return `#${t.substring(12)}`;
      }
    }
  }
  for (const t of document.tags) {
    if (t.startsWith("title:")) {
      return t.substring(6);
    }
  }
  return document.documentId;
}
var AppsCheckRequestSchema = z5.object({
  apps: z5.array(
    z5.object({
      appId: z5.string(),
      appHash: z5.string()
    })
  )
});
var AppsCheckResponseSchema = z5.object({
  apps: z5.array(
    z5.object({
      appId: z5.string(),
      appHash: z5.string(),
      deployed: z5.boolean()
    })
  )
});
var GetSpacesResponseSchema = z5.object({
  spaces: z5.array(SpaceTypeSchema)
});
var InternalAllowedIconSchema = FlexibleEnumSchema();
var CustomServerIconSchema = FlexibleEnumSchema();
var MCPServerTypeSchema = z5.object({
  sId: z5.string(),
  name: z5.string(),
  version: z5.string(),
  description: z5.string(),
  icon: z5.union([InternalAllowedIconSchema, CustomServerIconSchema]),
  authorization: z5.object({
    provider: OAuthProviderSchema,
    supported_use_cases: z5.array(
      z5.enum(["personal_actions", "platform_actions"])
    ),
    scope: z5.string().optional()
  }).nullable(),
  tools: z5.array(
    z5.object({
      name: z5.string(),
      description: z5.string(),
      inputSchema: z5.any().optional()
    })
  ),
  availability: z5.enum(["manual", "auto", "auto_hidden_builder"]),
  allowMultipleInstances: z5.boolean(),
  documentationUrl: z5.string().nullable()
});
var MCPServerViewTypeSchema = z5.object({
  id: z5.number(),
  sId: z5.string(),
  name: z5.string().nullable(),
  description: z5.string().nullable(),
  createdAt: z5.number(),
  updatedAt: z5.number(),
  spaceId: z5.string(),
  serverType: z5.enum(["remote", "internal"]),
  server: MCPServerTypeSchema,
  oAuthUseCase: z5.enum(["personal_actions", "platform_actions"]).nullable(),
  editedByUser: EditedByUserSchema.nullable()
});
var GetMCPServerViewsResponseSchema = z5.object({
  success: z5.literal(true),
  serverViews: z5.array(MCPServerViewTypeSchema)
});
var GetMCPServerViewsQuerySchema = z5.object({
  includeAuto: z5.enum(["true", "false"]).transform((val) => val === "true").optional()
});
var CallMCPToolRequestBodySchema = z5.object({
  serverViewId: z5.string(),
  toolName: z5.string(),
  arguments: z5.record(z5.unknown()).optional()
});
var CallMCPToolResponseSchema = z5.object({
  success: z5.literal(true),
  result: z5.object({
    content: z5.array(ContentBlockSchema),
    isError: z5.boolean()
  })
});
var BaseSearchBodySchema = z5.object({
  viewType: ContentNodesViewTypeSchema,
  spaceIds: z5.array(z5.string()),
  includeDataSources: z5.boolean(),
  limit: z5.number()
});
var TextSearchBodySchema = z5.intersection(
  BaseSearchBodySchema,
  z5.object({
    query: z5.string(),
    nodeIds: z5.undefined().optional(),
    searchSourceUrls: z5.boolean().optional()
  })
);
var NodeIdSearchBodySchema = z5.intersection(
  BaseSearchBodySchema,
  z5.object({
    nodeIds: z5.array(z5.string()),
    query: z5.undefined().optional()
  })
);
var SearchRequestBodySchema = z5.union([
  TextSearchBodySchema,
  NodeIdSearchBodySchema
]);
var ContentNodeSchema = z5.object({
  expandable: z5.boolean(),
  internalId: z5.string(),
  lastUpdatedAt: z5.number().nullable(),
  mimeType: z5.string(),
  // The direct parent ID of this content node
  parentInternalId: z5.string().nullable(),
  // permission: ConnectorPermissionSchema,
  preventSelection: z5.boolean().optional(),
  providerVisibility: ProviderVisibilitySchema.nullable().optional(),
  sourceUrl: z5.string().nullable().optional(),
  title: z5.string(),
  type: ContentNodeTypeSchema
});
var ContentNodeWithParentSchema = z5.intersection(
  ContentNodeSchema,
  z5.object({
    parentsInternalIds: z5.array(z5.string()).optional(),
    parentTitle: z5.string().optional().nullable()
  })
);
var DataSourceContentNodeSchema = z5.intersection(
  ContentNodeWithParentSchema,
  z5.object({
    dataSource: DataSourceTypeSchema,
    dataSourceViews: DataSourceViewSchema.array()
  })
);
var DataSourceViewContentNodeSchema = z5.intersection(
  ContentNodeWithParentSchema,
  z5.object({
    dataSourceView: DataSourceViewSchema
  })
);
var SearchWarningCodeSchema = z5.literal("truncated-query-clauses");
var PostWorkspaceSearchResponseBodySchema = z5.object({
  nodes: DataSourceContentNodeSchema.array(),
  warningCode: SearchWarningCodeSchema.optional().nullable(),
  resultsCount: z5.number().optional().nullable()
});
var TOOL_RUNNING_LABEL = "Using a tool";
var ValidateActionResponseSchema = z5.object({
  success: z5.boolean()
});
var ActionApprovalStateSchema = z5.enum([
  "approved",
  "rejected",
  "always_approved"
]);
var ValidateActionRequestBodySchema = z5.object({
  actionId: z5.string(),
  approved: ActionApprovalStateSchema
});
var AnswerUserQuestionResponseSchema = z5.object({
  success: z5.boolean()
});
var AnswerUserQuestionRequestBodySchema = z5.object({
  actionId: z5.string(),
  answer: z5.object({
    selectedOptions: z5.array(z5.number()),
    customResponse: z5.string().optional()
  })
});
var ClientSideMCPServerNameSchema = z5.string().min(5).max(30);
var PublicRegisterMCPRequestBodySchema = z5.object({
  serverName: ClientSideMCPServerNameSchema
});
var PublicHeartbeatMCPRequestBodySchema = z5.object({
  serverId: z5.string()
});
var RegisterMCPResponseSchema = z5.object({
  expiresAt: z5.string(),
  serverId: z5.string()
});
var HeartbeatMCPResponseSchema = z5.object({
  success: z5.boolean(),
  expiresAt: z5.string()
});
var PublicPostMCPResultsRequestBodySchema = z5.object({
  result: z5.unknown(),
  serverId: z5.string()
});
var PostMCPRequestsRequestQuerySchema = z5.object({
  serverId: z5.string(),
  lastEventId: z5.string().optional()
});
var PostMCPResultsResponseSchema = z5.object({
  success: z5.boolean()
});
var REMOTE_MCP_TOOL_STAKE_LEVELS = ["high", "low"];
var MCP_TOOL_STAKE_LEVELS = [
  ...REMOTE_MCP_TOOL_STAKE_LEVELS,
  "medium",
  "never_ask"
];
var BlockedActionsResponseSchema = z5.object({
  blockedActions: z5.array(BlockedActionExecutionSchema)
});
var MCPViewsRequestAvailabilitySchema = z5.enum(["manual", "auto"]);
var GetMCPViewsRequestSchema = z5.object({
  spaceIds: z5.array(z5.string()),
  availabilities: z5.array(MCPViewsRequestAvailabilitySchema)
});
var PostSpaceMembersRequestBodySchema = z5.object({
  userIds: z5.array(z5.string())
});
var RichMentionSchema = z5.object({
  id: z5.string(),
  type: z5.enum(["agent", "user"]),
  label: z5.string(),
  pictureUrl: z5.string(),
  description: z5.string(),
  userFavorite: z5.boolean().optional()
});
var GetMentionSuggestionsRequestQuerySchema = z5.object({
  query: z5.string(),
  select: z5.union([z5.array(z5.enum(["agents", "users"])), z5.enum(["agents", "users"])]).optional(),
  current: z5.boolean().optional()
});
var GetMentionSuggestionsResponseBodySchema = z5.object({
  suggestions: z5.array(RichMentionSchema)
});
var ParseMentionsRequestBodySchema = z5.object({
  markdown: z5.string()
});
var ParseMentionsResponseBodySchema = z5.object({
  markdown: z5.string()
});

// src/high_level/files.ts
var FilesAPI = class {
  constructor(client) {
    this._client = client;
  }
  async upload(file) {
    if (isFileIdAttachment(file)) {
      return {
        id: file.fileId,
        name: "Unknown",
        contentType: "application/octet-stream",
        size: 0,
        uploadedAt: Date.now()
      };
    }
    if (isFilePathAttachment(file)) {
      throw new Error(
        "File path uploads are not yet supported. Please pass a File or Blob object."
      );
    }
    if (!isBlobAttachment(file)) {
      throw new Error("Invalid attachment type");
    }
    const fileName = file instanceof File ? file.name : `upload-${Date.now()}`;
    const fileObj = file instanceof File ? file : new File([file], fileName, {
      type: file.type || "application/octet-stream"
    });
    const contentType = fileObj.type || "application/octet-stream";
    const result = await this._client.uploadFile({
      contentType: isSupportedFileContentType(contentType) ? contentType : "application/octet-stream",
      fileName: fileObj.name,
      fileSize: fileObj.size,
      useCase: "conversation",
      fileObject: fileObj
    });
    if (result.isErr()) {
      throw result.error instanceof Error ? result.error : apiErrorToDustError(result.error);
    }
    const uploadedFile = result.value;
    return {
      id: uploadedFile.sId,
      name: uploadedFile.fileName,
      contentType: uploadedFile.contentType,
      size: uploadedFile.fileSize,
      uploadedAt: uploadedFile.createdAt ?? Date.now()
    };
  }
  async delete(fileId) {
    const result = await this._client.deleteFile({ fileID: fileId });
    if (result.isErr()) {
      throw apiErrorToDustError(result.error);
    }
  }
  async download(fileId) {
    const result = await this._client.downloadFile({ fileID: fileId });
    if (!result) {
      throw new DustUnknownError("Download failed: no response received");
    }
    if (result.isErr()) {
      if (result.error instanceof Error) {
        throw new DustUnknownError(result.error.message, {
          cause: result.error
        });
      }
      const parsed = APIErrorSchema.safeParse(result.error);
      if (parsed.success) {
        throw apiErrorToDustError(parsed.data);
      }
      const message = hasStringProperty(result.error, "message") ? result.error.message : "Download failed with unknown error";
      throw new DustUnknownError(message);
    }
    return result.value;
  }
};

// src/mcp_transport.ts
import { EventSourcePolyfill } from "event-source-polyfill";
var logger = console;
var HEARTBEAT_INTERVAL_MS = 5 * 60 * 1e3;
var RECONNECT_DELAY_MS = 5 * 1e3;
var DustMcpServerTransport = class {
  constructor(dustAPI, onServerIdReceived, serverName = "Dust Extension", verbose = false, eventSourceHeartbeat = 45e3) {
    this.dustAPI = dustAPI;
    this.onServerIdReceived = onServerIdReceived;
    this.serverName = serverName;
    this.verbose = verbose;
    this.eventSourceHeartbeat = eventSourceHeartbeat;
    this.eventSource = null;
    this.lastEventId = null;
    this.heartbeatTimer = null;
    this.serverId = null;
  }
  /**
   * Register the MCP server with the Dust backend
   */
  async registerServer() {
    const registerRes = await this.dustAPI.registerMCPServer({
      serverName: this.serverName
    });
    if (registerRes.isErr()) {
      this.logError(`Failed to register MCP server: ${registerRes.error}`);
      return false;
    }
    const { serverId } = registerRes.value;
    this.serverId = serverId;
    this.onServerIdReceived(serverId);
    this.setupHeartbeat(serverId);
    return true;
  }
  /**
   * Send periodic heartbeats to keep the server registration alive.
   */
  setupHeartbeat(serverId) {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
    }
    this.heartbeatTimer = setInterval(async () => {
      const heartbeatRes = await this.dustAPI.heartbeatMCPServer({
        serverId
      });
      if (heartbeatRes.isErr() || heartbeatRes.value.success === false) {
        const error = heartbeatRes.isErr() ? heartbeatRes.error : new Error("Server not registered");
        this.logError(`Failed to heartbeat MCP server: ${error}`);
        await this.registerServer();
      }
    }, HEARTBEAT_INTERVAL_MS);
  }
  /**
   * Start the transport and connect to the SSE endpoint
   * This method is required by the Transport interface
   */
  async start() {
    try {
      const registered = await this.registerServer();
      if (!registered) {
        throw new Error("Failed to register MCP server");
      }
      await this.connectToRequestsStream();
      this.logInfo("MCP transport started successfully");
    } catch (error) {
      this.logError("Failed to start MCP transport:", error);
      this.onerror?.(error instanceof Error ? error : new Error(String(error)));
      throw error;
    }
  }
  /**
   * Connect to the SSE stream for the workspace
   */
  async connectToRequestsStream() {
    if (!this.serverId) {
      this.logError("Server ID is not set");
      return;
    }
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
    const connectionResult = await this.dustAPI.getMCPRequestsConnectionDetails(
      {
        serverId: this.serverId,
        lastEventId: this.lastEventId
      }
    );
    if (connectionResult.isErr()) {
      throw new Error(
        `Failed to get connection details: ${connectionResult.error.message}`
      );
    }
    const { url, headers } = connectionResult.value;
    this.eventSource = new EventSourcePolyfill(url, {
      headers,
      heartbeatTimeout: this.eventSourceHeartbeat
    });
    this.eventSource.onmessage = (event) => {
      try {
        if (event.data === "done") {
          return;
        }
        const eventData = JSON.parse(event.data);
        if (eventData.eventId) {
          this.lastEventId = eventData.eventId;
        }
        const { data } = eventData;
        if (!data) {
          this.logError("No data field found in the event");
          return;
        }
        if (this.onmessage) {
          this.onmessage(data);
        } else {
          this.logError(
            "ERROR: onmessage handler not set - MCP response won't be sent"
          );
        }
      } catch (error) {
        this.logError("Failed to parse MCP request:", error);
        this.onerror?.(new Error(`Failed to parse MCP request: ${error}`));
      }
    };
    this.eventSource.onerror = (error) => {
      this.eventSource?.close();
      this.logError("Error in MCP EventSource connection:", error);
      this.onerror?.(new Error(`SSE connection error: ${error}`));
      setTimeout(() => {
        if (this.eventSource) {
          this.logInfo("Attempting to reconnect to SSE...");
          void this.connectToRequestsStream().catch((reconnectError) => {
            this.logError("Failed to reconnect:", reconnectError);
          });
        }
      }, RECONNECT_DELAY_MS);
    };
    this.eventSource.onopen = () => {
      this.logInfo("MCP SSE connection established");
    };
    this.eventSource.addEventListener("close", () => {
      this.logInfo("MCP SSE connection closed");
      this.onclose?.();
    });
  }
  /**
   * Send a message to the server
   * This method is required by the Transport interface
   */
  async send(message) {
    if (!this.serverId) {
      this.logError("Server ID is not set");
      return;
    }
    const postResultsRes = await this.dustAPI.postMCPResults({
      serverId: this.serverId,
      result: message
    });
    if (postResultsRes.isErr()) {
      this.logError("Failed to send MCP result:", postResultsRes.error);
      this.onerror?.(
        new Error(`Failed to send MCP result: ${postResultsRes.error}`)
      );
    }
  }
  /**
   * Close the transport and disconnect from the SSE endpoint
   * This method is required by the Transport interface
   */
  async close() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    if (this.eventSource) {
      this.logInfo("Closing MCP SSE connection");
      this.eventSource.close();
      this.eventSource = null;
    }
    this.onclose?.();
  }
  logError(...args) {
    if (this.verbose) {
      logger.error(...args);
    }
  }
  logInfo(...args) {
    if (this.verbose) {
      logger.log(...args);
    }
  }
  /**
   * Get the current server ID
   */
  getServerId() {
    return this.serverId ?? void 0;
  }
};

// src/index.ts
function isStreamTerminationError(e) {
  if (!e) {
    return false;
  }
  const err = normalizeError(e);
  const msg = err.message;
  const name = err.name || "";
  const patterns = [
    /terminated/i,
    /aborted/i,
    /The operation was aborted/i,
    /network.*(error|changed|lost)/i,
    /socket hang up/i
  ];
  if (name === "AbortError") {
    return true;
  }
  if (name === "TypeError" && /terminated|aborted/i.test(msg)) {
    return true;
  }
  return patterns.some((p) => p.test(msg));
}
function isTransientHttpStatus(status) {
  return status === 408 || status === 429;
}
function isRecord(value) {
  return typeof value === "object" && value !== null;
}
var DEFAULT_MAX_RECONNECT_ATTEMPTS = 10;
var DEFAULT_RECONNECT_DELAY = 5e3;
var textFromResponse = async (response) => {
  if (typeof response.body === "string") {
    return response.body;
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let result = "";
  try {
    let done = false;
    while (!done) {
      const { value, done: doneReading } = await reader.read();
      done = doneReading;
      if (value) {
        result += decoder.decode(value, { stream: true });
      }
    }
    result += decoder.decode();
  } finally {
    reader.releaseLock();
  }
  return result;
};
function isDustAPIOptions(obj) {
  return typeof obj === "object" && obj !== null && "workspaceId" in obj && "apiKey" in obj;
}
var DustAPI = class {
  constructor(configOrOptions, credentials, logger2, urlOverride) {
    if (isDustAPIOptions(configOrOptions)) {
      this._url = configOrOptions.baseUrl ?? "https://dust.tt";
      this._credentials = {
        workspaceId: configOrOptions.workspaceId,
        apiKey: configOrOptions.apiKey,
        extraHeaders: configOrOptions.extraHeaders
      };
      this._logger = configOrOptions.logger ?? console;
      this._urlOverride = null;
      this._options = configOrOptions;
    } else {
      this._url = configOrOptions.url;
      this._credentials = credentials;
      this._logger = logger2;
      this._urlOverride = urlOverride;
    }
  }
  get agents() {
    if (!this._agents) {
      this._agents = new AgentsAPI(this, this._options);
    }
    return this._agents;
  }
  get conversations() {
    if (!this._conversations) {
      this._conversations = new ConversationsAPI(this);
    }
    return this._conversations;
  }
  get files() {
    if (!this._files) {
      this._files = new FilesAPI(this);
    }
    return this._files;
  }
  workspaceId() {
    return this._credentials.workspaceId;
  }
  setWorkspaceId(workspaceId) {
    this._credentials.workspaceId = workspaceId;
  }
  apiUrl() {
    return this._urlOverride ? this._urlOverride : this._url;
  }
  async getApiKey() {
    if (typeof this._credentials.apiKey === "function") {
      return this._credentials.apiKey();
    }
    return this._credentials.apiKey;
  }
  async baseHeaders() {
    const headers = {
      Authorization: `Bearer ${await this.getApiKey()}`
    };
    if (this._credentials.extraHeaders) {
      Object.assign(headers, this._credentials.extraHeaders);
    }
    return headers;
  }
  /**
   * Fetches the current user's information from the API.
   *
   * This method sends a GET request to the `/api/v1/me` endpoint with the necessary authorization
   * headers. It then processes the response to extract the user information.  Note that this will
   * only work if you are using an OAuth2 token. It will always fail with a workspace API key.
   *
   * @returns {Promise<Result<User, Error>>} A promise that resolves to a Result object containing
   * either the user information or an error.
   */
  async me() {
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await this.getApiKey()}`
    };
    const res = await this._fetchWithError(`${this.apiUrl()}/api/v1/me`, {
      method: "GET",
      headers
    });
    const r = await this._resultFromResponse(MeResponseSchema, res);
    if (r.isErr()) {
      return r;
    }
    return new Ok(r.value.user);
  }
  async request(args) {
    args.path = args.path.replace(/^\/+/, "");
    let url = `${this.apiUrl()}/api/v1/w/${args.overrideWorkspaceId ?? this.workspaceId()}/${args.path}`;
    if (args.query) {
      url += `?${args.query.toString()}`;
    }
    const headers = { ...await this.baseHeaders(), ...args.headers };
    headers["Content-Type"] = "application/json";
    if (args.stream) {
      headers["Accept"] = "text/event-stream";
    }
    const res = await this._fetchWithError(url, {
      method: args.method,
      headers,
      body: args.body ? JSON.stringify(args.body) : void 0,
      signal: args.signal,
      stream: args.stream
    });
    return res;
  }
  /**
   * This functions talks directly to the Dust production API to create a run.
   *
   * @param app DustAppType the app to run streamed
   * @param config DustAppConfigType the app config
   * @param inputs any[] the app inputs
   */
  async runApp({
    workspaceId,
    appId,
    appHash,
    appSpaceId
  }, config, inputs, { useWorkspaceCredentials } = {
    useWorkspaceCredentials: false
  }) {
    const res = await this.request({
      overrideWorkspaceId: workspaceId,
      path: `spaces/${appSpaceId}/apps/${appId}/runs`,
      query: new URLSearchParams({
        use_workspace_credentials: useWorkspaceCredentials ? "true" : "false"
      }),
      method: "POST",
      body: {
        specification_hash: appHash,
        config,
        stream: false,
        blocking: true,
        inputs
      }
    });
    const r = await this._resultFromResponse(RunAppResponseSchema, res);
    if (r.isErr()) {
      return r;
    }
    return new Ok(r.value.run);
  }
  /**
   * This functions talks directly to the Dust production API to create a streamed run.
   *
   * @param app DustAppType the app to run streamed
   * @param config DustAppConfigType the app config
   * @param inputs any[] the app inputs
   */
  async runAppStreamed({
    workspaceId,
    appId,
    appHash,
    appSpaceId
  }, config, inputs, { useWorkspaceCredentials } = {
    useWorkspaceCredentials: false
  }) {
    const res = await this.request({
      overrideWorkspaceId: workspaceId,
      path: `spaces/${appSpaceId}/apps/${appId}/runs`,
      query: new URLSearchParams({
        use_workspace_credentials: useWorkspaceCredentials ? "true" : "false"
      }),
      method: "POST",
      body: {
        specification_hash: appHash,
        config,
        stream: true,
        blocking: false,
        inputs
      },
      stream: true
    });
    if (res.isErr()) {
      return res;
    }
    async function processStreamedRunResponse(res2, logger2) {
      if (!res2.ok || !res2.body) {
        const text = await textFromResponse(res2);
        return new Err({
          type: "dust_api_error",
          message: `Error running streamed app: status_code=${res2.status} body=${text}`
        });
      }
      let hasRunId = false;
      let rejectDustRunIdPromise;
      let resolveDustRunIdPromise;
      const dustRunIdPromise = new Promise((resolve, reject) => {
        rejectDustRunIdPromise = reject;
        resolveDustRunIdPromise = resolve;
      });
      let pendingEvents = [];
      const parser = createParser((event) => {
        if (event.type === "event") {
          if (event.data) {
            try {
              const data = JSON.parse(event.data);
              switch (data.type) {
                case "error": {
                  pendingEvents.push({
                    type: "error",
                    content: {
                      code: data.content.code,
                      message: data.content.message
                    }
                  });
                  break;
                }
                case "run_status": {
                  pendingEvents.push({
                    type: data.type,
                    content: data.content
                  });
                  break;
                }
                case "block_status": {
                  pendingEvents.push({
                    type: data.type,
                    content: data.content
                  });
                  break;
                }
                case "block_execution": {
                  pendingEvents.push({
                    type: data.type,
                    content: data.content
                  });
                  break;
                }
                case "tokens": {
                  pendingEvents.push({
                    type: "tokens",
                    content: data.content
                  });
                  break;
                }
                case "reasoning_tokens": {
                  pendingEvents.push({
                    type: "reasoning_tokens",
                    content: data.content
                  });
                  break;
                }
                case "reasoning_item": {
                  pendingEvents.push({
                    type: "reasoning_item",
                    content: data.content
                  });
                  break;
                }
                case "function_call": {
                  pendingEvents.push({
                    type: "function_call",
                    content: data.content
                  });
                  break;
                }
                case "function_call_arguments_tokens": {
                  pendingEvents.push({
                    type: "function_call_arguments_tokens",
                    content: data.content
                  });
                  break;
                }
                case "final": {
                  pendingEvents.push({
                    type: "final"
                  });
                  break;
                }
              }
              if (data.content?.run_id && !hasRunId) {
                hasRunId = true;
                resolveDustRunIdPromise(data.content.run_id);
              }
            } catch (err) {
              logger2.error(
                { error: err },
                "Failed parsing chunk from Dust API"
              );
            }
          }
        }
      });
      const streamEvents = async function* () {
        if (!res2.body || typeof res2.body === "string") {
          throw new Error(
            "Expected a stream response, but got a string or null"
          );
        }
        const reader = res2.body.getReader();
        const decoder = new TextDecoder();
        try {
          for (; ; ) {
            const { value, done } = await reader.read();
            if (value) {
              parser.feed(decoder.decode(value, { stream: true }));
              for (const event of pendingEvents) {
                yield event;
              }
              pendingEvents = [];
            }
            if (done) {
              break;
            }
          }
          if (!hasRunId) {
            setImmediate(() => {
              logger2.error({}, "No run id received.");
              rejectDustRunIdPromise(new Error("No run id received"));
            });
          }
        } catch (e) {
          logger2.error(
            {
              error: e,
              errorStr: JSON.stringify(e),
              errorSource: "processStreamedRunResponse"
            },
            "DustAPI error: streaming chunks"
          );
          yield {
            type: "error",
            content: {
              code: "stream_error",
              message: "Error streaming chunks"
            }
          };
        }
      };
      return new Ok({
        eventStream: streamEvents(),
        dustRunId: dustRunIdPromise
      });
    }
    return processStreamedRunResponse(res.value.response, this._logger);
  }
  /**
   * This actions talks to the Dust production API to retrieve the list of data sources of the
   * current workspace.
   */
  async getDataSources() {
    const res = await this.request({
      method: "GET",
      path: "data_sources"
    });
    const r = await this._resultFromResponse(GetDataSourcesResponseSchema, res);
    if (r.isErr()) {
      return r;
    }
    return new Ok(r.value.data_sources);
  }
  async getAgentConfigurations({
    view,
    includes = []
  }) {
    function getQueryString() {
      const params = new URLSearchParams();
      if (typeof view === "string") {
        params.append("view", view);
      }
      if (includes.includes("authors")) {
        params.append("withAuthors", "true");
      }
      return params.toString();
    }
    const queryString = view || includes.length > 0 ? getQueryString() : null;
    const path = queryString ? `assistant/agent_configurations?${queryString}` : "assistant/agent_configurations";
    const res = await this.request({
      path,
      method: "GET"
    });
    const r = await this._resultFromResponse(
      GetAgentConfigurationsResponseSchema,
      res
    );
    if (r.isErr()) {
      return r;
    }
    return new Ok(r.value.agentConfigurations);
  }
  /**
   * Parses mentions in markdown text and converts them to the proper mention format.
   * Matches @agentName or @userName patterns against available agents and users.
   *
   * @param markdown - Markdown text containing @ mentions to parse
   * @returns A promise that resolves to a Result containing the parsed markdown with mentions converted to proper format
   */
  async parseForMentions({ markdown }) {
    const body = ParseMentionsRequestBodySchema.parse({ markdown });
    const res = await this.request({
      method: "POST",
      path: "assistant/mentions/parse",
      body
    });
    const r = await this._resultFromResponse(
      ParseMentionsResponseBodySchema,
      res
    );
    if (r.isErr()) {
      return r;
    }
    return new Ok(r.value.markdown);
  }
  /**
   * Get suggestions for mentions (agents and users) based on a query string.
   *
   * @param query - Search query string to filter suggestions
   * @param select - Optional array of mention types to include. Can be "agents", "users", or both.
   * @param conversationId - Optional conversation ID to scope suggestions to a specific conversation
   * @param current - Optional boolean to include the current user in the suggestions
   * @returns A promise that resolves to a Result containing an array of mention suggestions
   */
  async getMentionsSuggestions({
    query,
    select,
    conversationId,
    current
  }) {
    const queryParams = new URLSearchParams({ query });
    if (select) {
      if (Array.isArray(select)) {
        select.forEach((s) => queryParams.append("select", s));
      } else {
        queryParams.append("select", select);
      }
    }
    if (current !== void 0) {
      queryParams.append("current", current ? "true" : "false");
    }
    const path = conversationId ? `assistant/conversations/${conversationId}/mentions/suggestions` : "assistant/mentions/suggestions";
    const res = await this.request({
      method: "GET",
      path,
      query: queryParams.toString() ? queryParams : void 0
    });
    const r = await this._resultFromResponse(
      GetMentionSuggestionsResponseBodySchema,
      res
    );
    if (r.isErr()) {
      return r;
    }
    return new Ok(r.value.suggestions);
  }
  async postContentFragment({
    conversationId,
    contentFragment,
    signal
  }) {
    const res = await this.request({
      method: "POST",
      path: `assistant/conversations/${conversationId}/content_fragments`,
      body: { ...contentFragment },
      signal
    });
    const r = await this._resultFromResponse(
      PostContentFragmentResponseSchema,
      res
    );
    if (r.isErr()) {
      return r;
    }
    return new Ok(r.value.contentFragment);
  }
  // When creating a conversation with a user message, the API returns only after the user message
  // was created (and if applicable the associated agent messages).
  async createConversation({
    title,
    visibility,
    depth,
    message,
    contentFragment,
    contentFragments,
    blocking = false,
    skipToolsValidation = false,
    spaceId,
    params,
    signal
  }) {
    const queryParams = new URLSearchParams(params);
    const res = await this.request({
      method: "POST",
      path: "assistant/conversations",
      query: queryParams.toString() ? queryParams : void 0,
      body: {
        title,
        visibility,
        depth,
        message,
        contentFragment,
        contentFragments,
        blocking,
        skipToolsValidation,
        spaceId
      },
      signal
    });
    return this._resultFromResponse(CreateConversationResponseSchema, res);
  }
  async postUserMessage({
    conversationId,
    message,
    signal
  }) {
    const res = await this.request({
      method: "POST",
      path: `assistant/conversations/${conversationId}/messages`,
      body: { ...message },
      signal
    });
    const r = await this._resultFromResponse(
      PostUserMessageResponseSchema,
      res
    );
    if (r.isErr()) {
      return r;
    }
    return new Ok(r.value.message);
  }
  async postConversationTools({
    conversationId,
    action,
    mcpServerViewId,
    agentConfigurationId
  }) {
    const res = await this.request({
      method: "POST",
      path: `assistant/conversations/${conversationId}/tools`,
      body: {
        action,
        mcp_server_view_id: mcpServerViewId,
        agent_configuration_id: agentConfigurationId
      }
    });
    const r = await this._resultFromResponse(
      PatchConversationResponseSchema,
      res
    );
    if (r.isErr()) {
      return r;
    }
    return new Ok(r.value);
  }
  // Wait for the parent user message to move to `visible` when the agent message is not direclty
  // found in the conversation. This provides natural suoport for steering through the SDK.
  async waitForAgentMessage({
    conversation,
    parentUserMessageId,
    signal
  }) {
    let agentMessage = conversation.content.map((versions) => versions[versions.length - 1]).find((message) => {
      return message?.type === "agent_message" && message.parentMessageId === parentUserMessageId;
    }) ?? null;
    if (agentMessage) {
      return new Ok(agentMessage);
    }
    const streamRes = await this.streamConversationEvents({
      conversationId: conversation.sId,
      signal
    });
    if (streamRes.isErr()) {
      return streamRes;
    }
    for await (const event of streamRes.value.eventStream) {
      if (event.type === "user_message_new" && event.messageId === parentUserMessageId && event.message.visibility === "visible") {
        break;
      }
      if (event.type === "user_message_promoted" && event.messageId === parentUserMessageId) {
        break;
      }
    }
    const conversationRes = await this.getConversation({
      conversationId: conversation.sId
    });
    if (conversationRes.isErr()) {
      return conversationRes;
    }
    agentMessage = conversationRes.value.content.map((versions) => versions[versions.length - 1]).find((message) => {
      return message?.type === "agent_message" && message.parentMessageId === parentUserMessageId;
    }) ?? null;
    return new Ok(agentMessage);
  }
  async streamAgentAnswerEvents({
    conversation,
    userMessageId,
    signal,
    options = {
      maxReconnectAttempts: DEFAULT_MAX_RECONNECT_ATTEMPTS,
      reconnectDelay: DEFAULT_RECONNECT_DELAY,
      autoReconnect: true
    }
  }) {
    const agentMessageRes = await this.waitForAgentMessage({
      conversation,
      parentUserMessageId: userMessageId,
      signal
    });
    if (agentMessageRes.isErr()) {
      return agentMessageRes;
    }
    const agentMessage = agentMessageRes.value;
    if (agentMessage === null) {
      return new Err(new Error("Failed to retrieve agent message"));
    }
    return this.streamAgentMessageEvents({
      conversationId: conversation.sId,
      agentMessageId: agentMessage.sId,
      signal,
      options: {
        maxReconnectAttempts: options.maxReconnectAttempts ?? DEFAULT_MAX_RECONNECT_ATTEMPTS,
        reconnectDelay: options.reconnectDelay ?? DEFAULT_RECONNECT_DELAY,
        autoReconnect: options.autoReconnect ?? true
      }
    });
  }
  async streamConversationEvents({
    conversationId,
    signal,
    options = {
      maxReconnectAttempts: DEFAULT_MAX_RECONNECT_ATTEMPTS,
      reconnectDelay: DEFAULT_RECONNECT_DELAY,
      autoReconnect: true
    }
  }) {
    const createRequest = async (lastId) => {
      let path = `assistant/conversations/${conversationId}/events`;
      if (lastId) {
        path += `?lastEventId=${lastId}`;
      }
      return this.request({
        method: "GET",
        path,
        signal,
        stream: true
      });
    };
    return new Ok({
      eventStream: this._streamEventsWithReconnection({
        createRequest,
        signal,
        options: {
          maxReconnectAttempts: options.maxReconnectAttempts ?? DEFAULT_MAX_RECONNECT_ATTEMPTS,
          reconnectDelay: options.reconnectDelay ?? DEFAULT_RECONNECT_DELAY,
          autoReconnect: options.autoReconnect ?? true
        },
        parseEvent: (eventData) => {
          if (!isRecord(eventData)) {
            return null;
          }
          const { data } = eventData;
          return isRecord(data) ? data : null;
        }
      })
    });
  }
  async streamAgentMessageEvents({
    conversationId,
    agentMessageId,
    signal,
    options
  }) {
    const terminalEventTypes = [
      "agent_message_success",
      "agent_message_gracefully_stopped",
      "agent_error",
      "agent_generation_cancelled",
      "user_message_error"
    ];
    const createRequest = async (lastId) => {
      let path = `assistant/conversations/${conversationId}/messages/${agentMessageId}/events`;
      if (lastId) {
        path += `?lastEventId=${lastId}`;
      }
      return this.request({
        method: "GET",
        path,
        signal,
        stream: true
      });
    };
    let receivedTerminalEvent = false;
    return new Ok({
      eventStream: this._streamEventsWithReconnection({
        createRequest,
        signal,
        options,
        parseEvent: (eventData) => {
          if (!isRecord(eventData)) {
            return null;
          }
          const { data } = eventData;
          return isRecord(data) ? data : null;
        },
        onEvent: (event) => {
          if (terminalEventTypes.includes(event.type)) {
            receivedTerminalEvent = true;
          }
        },
        shouldReconnect: () => !receivedTerminalEvent
      })
    });
  }
  async *_streamEventsWithReconnection({
    createRequest,
    signal,
    options,
    parseEvent,
    onEvent,
    shouldReconnect = () => true
  }) {
    const { maxReconnectAttempts, reconnectDelay, autoReconnect } = options;
    const logger2 = this._logger;
    let lastEventId = null;
    let reconnectAttempts = 0;
    while (true) {
      if (signal?.aborted) {
        return;
      }
      const res = await createRequest(lastEventId);
      if (res.isErr()) {
        if (autoReconnect) {
          reconnectAttempts += 1;
          if (reconnectAttempts >= maxReconnectAttempts) {
            throw new Error(
              `Exceeded maximum reconnection attempts (request error): ${res.error.message}`
            );
          }
          await new Promise((resolve) => setTimeout(resolve, reconnectDelay));
          continue;
        }
        const error = res.error;
        throw new Error(`Error requesting event stream: ${error.message}`);
      }
      if (!res.value.response.ok || !res.value.response.body) {
        if (autoReconnect && isTransientHttpStatus(res.value.response.status)) {
          reconnectAttempts += 1;
          if (reconnectAttempts >= maxReconnectAttempts) {
            throw new Error(
              `Exceeded maximum reconnection attempts (http ${res.value.response.status})`
            );
          }
          await new Promise((resolve) => setTimeout(resolve, reconnectDelay));
          continue;
        }
        throw new Error(
          `Error requesting event stream: status_code=${res.value.response.status}`
        );
      }
      let pendingEvents = [];
      let receivedEventsInThisConnection = false;
      const parser = createParser((event) => {
        if (event.type === "event" && event.data && event.data !== "done") {
          try {
            const eventData = JSON.parse(event.data);
            if (isRecord(eventData) && typeof eventData.eventId === "string") {
              lastEventId = eventData.eventId;
            }
            const parsedEvent = parseEvent(eventData);
            if (parsedEvent) {
              pendingEvents.push(parsedEvent);
            }
          } catch (err) {
            logger2.error({ error: err }, "Failed parsing chunk from Dust API");
          }
        }
      });
      if (!res.value.response.body || typeof res.value.response.body === "string") {
        throw new Error("Expected a stream response, but got a string or null");
      }
      const reader = res.value.response.body.getReader();
      const decoder = new TextDecoder();
      let streamEndedWithError = false;
      try {
        for (; ; ) {
          const { value, done } = await reader.read();
          if (value) {
            parser.feed(decoder.decode(value, { stream: true }));
            for (const event of pendingEvents) {
              yield event;
              receivedEventsInThisConnection = true;
              onEvent?.(event);
            }
            pendingEvents = [];
          }
          if (done) {
            break;
          }
        }
      } catch (e) {
        logger2.error({ error: e }, "Failed processing event stream");
        streamEndedWithError = true;
        if (signal?.aborted) {
          return;
        }
        if (!isStreamTerminationError(e)) {
          throw new Error(`Error processing event stream: ${e}`);
        }
      } finally {
        reader.releaseLock();
      }
      if (shouldReconnect() && autoReconnect) {
        if (streamEndedWithError || !receivedEventsInThisConnection) {
          reconnectAttempts += 1;
        } else {
          reconnectAttempts = 0;
        }
        if (reconnectAttempts >= maxReconnectAttempts) {
          throw new Error("Exceeded maximum reconnection attempts");
        }
        await new Promise((resolve) => setTimeout(resolve, reconnectDelay));
        continue;
      }
      return;
    }
  }
  async cancelMessageGeneration({
    conversationId,
    messageIds
  }) {
    const res = await this.request({
      method: "POST",
      path: `assistant/conversations/${conversationId}/cancel`,
      body: {
        messageIds
      }
    });
    const r = await this._resultFromResponse(
      CancelMessageGenerationResponseSchema,
      res
    );
    if (r.isErr()) {
      return r;
    } else {
      return new Ok(r.value);
    }
  }
  async markAsRead({ conversationId }) {
    const res = await this.request({
      method: "PATCH",
      path: `assistant/conversations/${conversationId}`,
      body: {
        read: true
      }
    });
    const r = await this._resultFromResponse(
      PatchConversationResponseSchema,
      res
    );
    if (r.isErr()) {
      return r;
    }
    return new Ok(r.value.success);
  }
  async getConversations() {
    const res = await this.request({
      method: "GET",
      path: `assistant/conversations`
    });
    const r = await this._resultFromResponse(
      GetConversationsResponseSchema,
      res
    );
    if (r.isErr()) {
      return r;
    }
    return new Ok(r.value.conversations);
  }
  async getConversation({
    conversationId,
    signal
  }) {
    const res = await this.request({
      method: "GET",
      path: `assistant/conversations/${conversationId}`,
      signal
    });
    const r = await this._resultFromResponse(
      GetConversationResponseSchema,
      res
    );
    if (r.isErr()) {
      return r;
    }
    return new Ok(r.value.conversation);
  }
  async getConversationFeedback({
    conversationId
  }) {
    const res = await this.request({
      method: "GET",
      path: `assistant/conversations/${conversationId}/feedbacks`
    });
    const r = await this._resultFromResponse(GetFeedbacksResponseSchema, res);
    if (r.isErr()) {
      return r;
    }
    return new Ok(r.value.feedbacks);
  }
  async getSpaceConversationsForDataSource({
    spaceId,
    updatedSince
  }) {
    const query = new URLSearchParams();
    if (updatedSince !== void 0 && updatedSince !== null) {
      query.append("updatedSince", String(updatedSince));
    }
    const res = await this.request({
      method: "GET",
      path: `spaces/${spaceId}/conversations`,
      query
    });
    return this._resultFromResponse(
      GetSpaceConversationsForDataSourceResponseSchema,
      res
    );
  }
  async getSpaceConversationIds({ spaceId }) {
    const res = await this.request({
      method: "GET",
      path: `spaces/${spaceId}/conversation_ids`
    });
    return this._resultFromResponse(GetSpaceConversationIdsResponseSchema, res);
  }
  async getSpaceMetadata({ spaceId }) {
    const res = await this.request({
      method: "GET",
      path: `spaces/${spaceId}/project_metadata`
    });
    return this._resultFromResponse(GetSpaceMetadataResponseSchema, res);
  }
  async getSpaceProjectFiles({
    spaceId,
    updatedSince
  }) {
    const query = new URLSearchParams();
    if (updatedSince !== void 0 && updatedSince !== null) {
      query.append("updatedSince", String(updatedSince));
    }
    const res = await this.request({
      method: "GET",
      path: `spaces/${spaceId}/project_files`,
      query
    });
    return this._resultFromResponse(GetProjectFilesResponseSchema, res);
  }
  async postFeedback(conversationId, messageId, feedback) {
    const res = await this.request({
      method: "POST",
      path: `assistant/conversations/${conversationId}/messages/${messageId}/feedbacks`,
      body: feedback
    });
    return this._resultFromResponse(PostMessageFeedbackResponseSchema, res);
  }
  async deleteFeedback(conversationId, messageId) {
    const res = await this.request({
      method: "DELETE",
      path: `assistant/conversations/${conversationId}/messages/${messageId}/feedbacks`
    });
    return this._resultFromResponse(PostMessageFeedbackResponseSchema, res);
  }
  async tokenize(text, dataSourceId, opts) {
    const res = await this.request({
      method: "POST",
      path: `data_sources/${dataSourceId}/tokenize`,
      body: { text },
      signal: opts?.signal
    });
    const r = await this._resultFromResponse(TokenizeResponseSchema, res);
    if (r.isErr()) {
      return r;
    }
    return new Ok(r.value.tokens);
  }
  async upsertFolder({
    dataSourceId,
    folderId,
    timestamp,
    title,
    parentId,
    parents,
    mimeType,
    sourceUrl,
    providerVisibility
  }) {
    const res = await this.request({
      method: "POST",
      path: `data_sources/${dataSourceId}/folders/${encodeURIComponent(
        folderId
      )}`,
      body: {
        timestamp: Math.floor(timestamp),
        title,
        parent_id: parentId,
        parents,
        mime_type: mimeType,
        source_url: sourceUrl,
        provider_visibility: providerVisibility
      }
    });
    const r = await this._resultFromResponse(UpsertFolderResponseSchema, res);
    if (r.isErr()) {
      return r;
    }
    return new Ok(r.value);
  }
  async deleteFolder({
    dataSourceId,
    folderId
  }) {
    const res = await this.request({
      method: "DELETE",
      path: `data_sources/${dataSourceId}/folders/${encodeURIComponent(
        folderId
      )}`
    });
    const r = await this._resultFromResponse(DeleteFolderResponseSchema, res);
    if (r.isErr()) {
      return r;
    }
    return new Ok(r.value);
  }
  _validateRedirectUrl(url) {
    const urlObj = new URL(url);
    if (urlObj.protocol !== "https:" || urlObj.hostname !== "storage.googleapis.com") {
      return false;
    }
    return true;
  }
  async downloadFile({ fileID }) {
    const res = await this.request({
      method: "GET",
      path: `files/${fileID}?action=download`
    });
    if (res.isErr()) {
      return res;
    }
    if (res.value.response.status >= 200 && res.value.response.status < 400) {
      const redirectUrl = res.value.response.url;
      if (!this._validateRedirectUrl(redirectUrl)) {
        return new Err({
          type: "unexpected_network_error",
          message: `Invalid redirect URL format. Expected format: https://storage.googleapis.com/... Got: ${redirectUrl}`
        });
      }
      try {
        const fileResponse = await fetch(redirectUrl);
        if (!fileResponse.ok) {
          return new Err({
            type: "unexpected_network_error",
            message: `Failed to download file from signed URL: ${fileResponse.status}`
          });
        }
        const buffer = Buffer.from(await fileResponse.arrayBuffer());
        return new Ok(buffer);
      } catch (error) {
        return new Err({
          type: "unexpected_network_error",
          message: `Failed to download file: ${error}`
        });
      }
    }
  }
  async getFileContent({
    fileId,
    version = "original"
  }) {
    const res = await this.request({
      method: "GET",
      path: `files/${fileId}?action=view&version=${version}`,
      stream: true
    });
    if (res.isErr()) {
      return res;
    }
    const { body } = res.value.response;
    if (typeof body === "string") {
      return new Ok(new Blob([body]));
    }
    return new Ok(await new Response(body).blob());
  }
  async uploadFile({
    contentType,
    fileName,
    fileSize,
    useCase,
    useCaseMetadata,
    fileObject,
    signal
  }) {
    const res = await this.request({
      method: "POST",
      path: "files",
      body: {
        contentType,
        fileName,
        fileSize,
        useCase,
        useCaseMetadata
      },
      signal
    });
    const fileRes = await this._resultFromResponse(
      FileUploadRequestResponseSchema,
      res
    );
    if (fileRes.isErr()) {
      return fileRes;
    }
    const { file } = fileRes.value;
    const formData = new FormData();
    formData.append("file", fileObject);
    try {
      const headers = await this.baseHeaders();
      const response = await fetch(file.uploadUrl, {
        method: "POST",
        headers,
        body: formData,
        signal
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return new Err(
          new Error(
            errorData?.error?.message || `Failed to upload file: ${response.status}`
          )
        );
      }
      const responseData = await response.json();
      return new Ok(responseData.file);
    } catch (err) {
      return new Err(
        new Error(err instanceof Error ? err.message : "Unknown error")
      );
    }
  }
  async deleteFile({ fileID }) {
    const res = await this.request({
      method: "DELETE",
      path: `files/${fileID}`
    });
    return res;
  }
  async getActiveMemberEmailsInWorkspace() {
    const res = await this.request({
      method: "GET",
      path: "members/emails",
      query: new URLSearchParams({ activeOnly: "true" })
    });
    const r = await this._resultFromResponse(
      GetActiveMemberEmailsInWorkspaceResponseSchema,
      res
    );
    if (r.isErr()) {
      return r;
    }
    return new Ok(r.value.emails);
  }
  async getWorkspaceVerifiedDomains() {
    const res = await this.request({
      method: "GET",
      path: "verified_domains"
    });
    const r = await this._resultFromResponse(
      GetWorkspaceVerifiedDomainsResponseSchema,
      res
    );
    if (r.isErr()) {
      return r;
    }
    return new Ok(r.value.verified_domains);
  }
  async getWorkspaceFeatureFlags() {
    const res = await this.request({
      method: "GET",
      path: "feature_flags"
    });
    const r = await this._resultFromResponse(
      GetWorkspaceFeatureFlagsResponseSchema,
      res
    );
    if (r.isErr()) {
      return r;
    }
    return new Ok(r.value.feature_flags);
  }
  async searchDataSourceViews(searchParams) {
    const res = await this.request({
      method: "GET",
      path: "data_source_views/search",
      query: searchParams
    });
    const r = await this._resultFromResponse(
      SearchDataSourceViewsResponseSchema,
      res
    );
    if (r.isErr()) {
      return r;
    }
    return new Ok(r.value.data_source_views);
  }
  async patchDataSourceView(dataSourceView, patch) {
    const res = await this.request({
      method: "PATCH",
      path: `spaces/${dataSourceView.spaceId}/data_source_views/${dataSourceView.sId}`,
      body: patch
    });
    const r = await this._resultFromResponse(DataSourceViewResponseSchema, res);
    if (r.isErr()) {
      return r;
    }
    return new Ok(r.value.dataSourceView);
  }
  async exportApps({ appSpaceId }) {
    const res = await this.request({
      method: "GET",
      path: `spaces/${appSpaceId}/apps/export`
    });
    const r = await this._resultFromResponse(GetAppsResponseSchema, res);
    if (r.isErr()) {
      return r;
    }
    return new Ok(r.value.apps);
  }
  async checkApps(apps, appSpaceId) {
    const res = await this.request({
      method: "POST",
      path: `spaces/${appSpaceId}/apps/check`,
      body: apps
    });
    const r = await this._resultFromResponse(AppsCheckResponseSchema, res);
    if (r.isErr()) {
      return r;
    }
    return new Ok(r.value.apps);
  }
  async getSpaces() {
    const res = await this.request({
      method: "GET",
      path: "spaces"
    });
    const r = await this._resultFromResponse(GetSpacesResponseSchema, res);
    if (r.isErr()) {
      return r;
    }
    return new Ok(r.value.spaces);
  }
  async getMCPServerViews(spaceId, includeAuto = false) {
    const res = await this.request({
      method: "GET",
      path: `spaces/${spaceId}/mcp_server_views`,
      query: new URLSearchParams({ includeAuto: includeAuto.toString() })
    });
    const r = await this._resultFromResponse(
      GetMCPServerViewsResponseSchema,
      res
    );
    if (r.isErr()) {
      return r;
    }
    return new Ok(r.value.serverViews);
  }
  async searchNodes(searchParams) {
    const res = await this.request({
      method: "POST",
      path: "search",
      body: searchParams
    });
    const r = await this._resultFromResponse(
      PostWorkspaceSearchResponseBodySchema,
      res
    );
    if (r.isErr()) {
      return r;
    }
    return new Ok(r.value.nodes);
  }
  /**
   * Unified search with SSE streaming for both knowledge and tool results.
   * Returns a Result containing an async generator that yields search results as they arrive.
   *
   * @param params Search parameters
   * @returns Promise resolving to Result with eventStream AsyncGenerator
   */
  async searchUnified({
    query,
    limit = 25,
    cursor,
    viewType = "all",
    spaceIds,
    includeDataSources = true,
    searchSourceUrls = false,
    includeTools = true
  }) {
    const params = new URLSearchParams();
    params.append("query", query);
    params.append("limit", limit.toString());
    params.append("viewType", viewType);
    params.append("includeDataSources", includeDataSources.toString());
    params.append("searchSourceUrls", searchSourceUrls.toString());
    params.append("includeTools", includeTools.toString());
    if (spaceIds && spaceIds.length > 0) {
      params.append("spaceIds", spaceIds.join(","));
    }
    if (cursor) {
      params.append("cursor", cursor);
    }
    const res = await this.request({
      method: "GET",
      path: `search?${params.toString()}`,
      stream: true
    });
    if (res.isErr()) {
      return new Err({
        type: "search_error",
        message: `Search request failed: ${res.error.message}`
      });
    }
    if (typeof res.value.response.body === "string") {
      return new Err({
        type: "search_error",
        message: "Expected stream body but got string"
      });
    }
    const logger2 = this._logger;
    const streamSearchResults = async function* () {
      if (!res.value.response.body || typeof res.value.response.body === "string") {
        throw new Error("Expected a stream response, but got a string or null");
      }
      const reader = res.value.response.body.getReader();
      const decoder = new TextDecoder();
      try {
        let pendingChunks = [];
        const parser = createParser((event) => {
          if (event.type === "event") {
            if (event.data) {
              try {
                const chunk = JSON.parse(event.data);
                pendingChunks.push(chunk);
              } catch (err) {
                logger2.error(
                  {
                    error: normalizeError(err)
                  },
                  "Error parsing search stream chunk"
                );
              }
            }
          }
        });
        for (; ; ) {
          const { value, done } = await reader.read();
          if (value) {
            parser.feed(decoder.decode(value, { stream: true }));
            for (const chunk of pendingChunks) {
              yield chunk;
            }
            pendingChunks = [];
          }
          if (done) {
            break;
          }
        }
      } finally {
        reader.releaseLock();
      }
    };
    return new Ok({ eventStream: streamSearchResults() });
  }
  async retryMessage({
    conversationId,
    messageId,
    blockedOnly = false
  }) {
    const query = blockedOnly ? new URLSearchParams({ blocked_only: "true" }) : void 0;
    const res = await this.request({
      method: "POST",
      path: `assistant/conversations/${conversationId}/messages/${messageId}/retry`,
      query
    });
    const r = await this._resultFromResponse(RetryMessageResponseSchema, res);
    if (r.isErr()) {
      return r;
    }
    return new Ok(r.value.message);
  }
  // MCP Related.
  async getBlockedActions({
    conversationId
  }) {
    const res = await this.request({
      method: "GET",
      path: `assistant/conversations/${conversationId}/actions/blocked`
    });
    return this._resultFromResponse(BlockedActionsResponseSchema, res);
  }
  async validateAction({
    conversationId,
    messageId,
    actionId,
    approved,
    signal
  }) {
    const res = await this.request({
      method: "POST",
      path: `assistant/conversations/${conversationId}/messages/${messageId}/validate-action`,
      body: {
        actionId,
        approved
      },
      signal
    });
    return this._resultFromResponse(ValidateActionResponseSchema, res);
  }
  async answerUserQuestion({
    conversationId,
    messageId,
    actionId,
    answer,
    signal
  }) {
    const res = await this.request({
      method: "POST",
      path: `assistant/conversations/${conversationId}/messages/${messageId}/answer-question`,
      body: {
        actionId,
        answer
      },
      signal
    });
    return this._resultFromResponse(AnswerUserQuestionResponseSchema, res);
  }
  async registerMCPServer({
    serverName
  }) {
    const body = {
      serverName
    };
    const res = await this.request({
      method: "POST",
      path: "mcp/register",
      body
    });
    return this._resultFromResponse(RegisterMCPResponseSchema, res);
  }
  async heartbeatMCPServer({
    serverId
  }) {
    const body = {
      serverId
    };
    const res = await this.request({
      method: "POST",
      path: "mcp/heartbeat",
      body
    });
    return this._resultFromResponse(HeartbeatMCPResponseSchema, res);
  }
  async postMCPResults({
    result,
    serverId
  }) {
    const body = {
      result,
      serverId
    };
    const res = await this.request({
      method: "POST",
      path: "mcp/results",
      body
    });
    return this._resultFromResponse(PostMCPResultsResponseSchema, res);
  }
  async getMCPRequestsConnectionDetails({
    serverId,
    lastEventId
  }) {
    const url = `${this.apiUrl()}/api/v1/w/${this.workspaceId()}/mcp/requests`;
    const params = new URLSearchParams({
      serverId,
      ...lastEventId ? { lastEventId } : {}
    });
    const headers = await this.baseHeaders();
    return new Ok({
      url: `${url}?${params.toString()}`,
      headers
    });
  }
  async _fetchWithError(url, {
    method = "GET",
    headers = {},
    body,
    signal,
    stream = false
  } = {}) {
    const now = Date.now();
    try {
      const res = await fetch(url, {
        method,
        headers,
        body,
        signal
      });
      const responseBody = stream && res.body ? res.body : await res.text();
      const response = {
        status: res.status,
        url: res.url,
        body: responseBody,
        ok: res.ok
      };
      return new Ok({ response, duration: Date.now() - now });
    } catch (e) {
      const duration = Date.now() - now;
      const err = {
        type: "unexpected_network_error",
        message: `Unexpected network error from DustAPI: ${e}`
      };
      this._logger.error(
        {
          dustError: err,
          url,
          duration,
          connectorsError: err,
          error: e
        },
        "DustAPI error"
      );
      return new Err(err);
    }
  }
  async _resultFromResponse(schema, res) {
    if (res.isErr()) {
      return res;
    }
    if (res.value.response.status === 413) {
      const err = {
        type: "content_too_large",
        message: "Your request content is too large, please try again with a shorter content."
      };
      this._logger.error(
        {
          dustError: err,
          status: res.value.response.status,
          url: res.value.response.url,
          duration: res.value.duration
        },
        "DustAPI error"
      );
      return new Err(err);
    }
    const text = await textFromResponse(res.value.response);
    try {
      const response = JSON.parse(text);
      const r = schema.safeParse(response);
      if (r.success) {
        return new Ok(r.data);
      } else {
        const rErr = APIErrorSchema.safeParse(response["error"]);
        if (rErr.success) {
          this._logger.error(
            {
              dustError: rErr.data,
              status: res.value.response.status,
              url: res.value.response.url,
              duration: res.value.duration
            },
            "DustAPI error"
          );
          return new Err(rErr.data);
        } else {
          const err = {
            type: "unexpected_response_format",
            message: `Unexpected response format from DustAPI calling ${res.value.response.url} : ${r.error.message}`
          };
          this._logger.error(
            {
              dustError: err,
              parseError: r.error.message,
              rawText: text,
              status: res.value.response.status,
              url: res.value.response.url,
              duration: res.value.duration
            },
            "DustAPI error"
          );
          return new Err(err);
        }
      }
    } catch (e) {
      const err = {
        type: "unexpected_response_format",
        message: `Fail to parse response from DustAPI calling ${res.value.response.url} : ${e}`
      };
      this._logger.error(
        {
          dustError: err,
          error: e,
          rawText: text,
          status: res.value.response.status,
          url: res.value.response.url,
          duration: res.value.duration
        },
        "DustAPI error"
      );
      return new Err(err);
    }
  }
};
export {
  APIErrorSchema,
  ActionApprovalStateSchema,
  AgentConfigurationViewSchema,
  AgentMessageEventDataSchema,
  AgentPauseOutputResourceSchema,
  AgentsAPI,
  AnswerUserQuestionRequestBodySchema,
  AnswerUserQuestionResponseSchema,
  AppsCheckRequestSchema,
  AppsCheckResponseSchema,
  AuthRequiredOutputResourceSchema,
  BaseSearchBodySchema,
  BlockedActionsResponseSchema,
  BlockedAwaitingInputOutputResourceSchema,
  BrowseResultResourceSchema,
  CONTENT_NODE_MIME_TYPES,
  CallMCPToolRequestBodySchema,
  CallMCPToolResponseSchema,
  CancelMessageGenerationRequestSchema,
  CancelMessageGenerationResponseSchema,
  ClientSideMCPServerNameSchema,
  ContentNodeSchema,
  ContentNodeTypeSchema,
  ContentNodeWithParentSchema,
  ContentNodesViewTypeSchema,
  ConversationEventDataSchema,
  ConversationForDataSourceSyncSchema,
  ConversationSchema,
  ConversationsAPI,
  CoreAPIErrorSchema,
  CoreAPITokenTypeSchema,
  CreateConversationResponseSchema,
  DATA_SOURCE_FOLDER_SPREADSHEET_MIME_TYPE,
  DATA_SOURCE_MIME_TYPE,
  DATA_WAREHOUSE_MIME_TYPE,
  DEFAULT_RETRY_OPTIONS,
  DUST_TABLE_MIME_TYPE,
  DataSourceContentNodeSchema,
  DataSourceSearchQuerySchema,
  DataSourceViewContentNodeSchema,
  DataSourceViewResponseSchema,
  DatabaseSchemaResourceSchema,
  DeleteAgentConfigurationResponseSchema,
  DeleteFolderResponseSchema,
  DustAPI,
  DustAgentError,
  DustAppConfigTypeSchema,
  DustAppRunBlockExecutionEventSchema,
  DustAppRunBlockStatusEventSchema,
  DustAppRunErroredEventSchema,
  DustAppRunFinalEventSchema,
  DustAppRunFunctionCallArgumentsTokensEventSchema,
  DustAppRunFunctionCallEventSchema,
  DustAppRunReasoningItemEventSchema,
  DustAppRunReasoningTokensEventSchema,
  DustAppRunRunStatusEventSchema,
  DustAppRunTokensEventSchema,
  DustAppTypeSchema,
  DustAuthenticationError,
  DustCancelledError,
  DustContentTooLargeError,
  DustError,
  DustMcpServerTransport,
  DustNetworkError,
  DustNotFoundError,
  DustPermissionError,
  DustRateLimitError,
  DustServerError,
  DustTimeoutError,
  DustUnknownError,
  DustValidationError,
  EarlyExitOutputResourceSchema,
  Err,
  ExampleRowsResourceSchema,
  ExtractQueryResourceSchema,
  ExtractResultResourceSchema,
  FileAuthRequiredOutputResourceSchema,
  FileTypeSchema,
  FileTypeWithUploadUrlSchema,
  FileUploadRequestResponseSchema,
  FileUploadUrlRequestSchema,
  FileUploadedRequestResponseSchema,
  FilesAPI,
  GenerationTokensEventSchema,
  GetActiveMemberEmailsInWorkspaceResponseSchema,
  GetAgentConfigurationYAMLExportResponseSchema,
  GetAgentConfigurationsResponseSchema,
  GetAnalyticsExportRequestSchema,
  GetAppsResponseSchema,
  GetConversationResponseSchema,
  GetConversationsResponseSchema,
  GetDataSourcesResponseSchema,
  GetFeedbacksResponseSchema,
  GetFolderResponseSchema,
  GetFoldersResponseSchema,
  GetMCPServerViewsQuerySchema,
  GetMCPServerViewsResponseSchema,
  GetMCPViewsRequestSchema,
  GetMentionSuggestionsRequestQuerySchema,
  GetMentionSuggestionsResponseBodySchema,
  GetOrPatchAgentConfigurationResponseSchema,
  GetProjectFilesResponseSchema,
  GetSpaceConversationIdsResponseSchema,
  GetSpaceConversationsForDataSourceResponseSchema,
  GetSpaceMetadataResponseSchema,
  GetSpacesResponseSchema,
  GetWorkspaceFeatureFlagsResponseSchema,
  GetWorkspaceUsageRequestSchema,
  GetWorkspaceVerifiedDomainsResponseSchema,
  HeartbeatMCPResponseSchema,
  INCLUDABLE_INTERNAL_CONTENT_NODE_MIME_TYPES,
  INCLUDABLE_INTERNAL_MIME_TYPES_VALUES,
  INTERNAL_MIME_TYPES,
  INTERNAL_MIME_TYPES_VALUES,
  ImportAgentConfigurationFromYAMLResponseSchema,
  ImportAppsResponseSchema,
  IncludeResultResourceSchema,
  MCPProgressNotificationSchema,
  MeResponseSchema,
  MembershipOriginType,
  MessageStreamImpl,
  NotificationInteractiveContentFileContentSchema,
  NotificationRunAgentContentSchema2 as NotificationRunAgentContentSchema,
  OAuthProviderSchema,
  Ok,
  ParseMentionsRequestBodySchema,
  ParseMentionsResponseBodySchema,
  PatchAgentConfigurationRequestSchema,
  PatchConversationRequestSchema,
  PatchConversationResponseSchema,
  PatchDataSourceViewRequestSchema,
  PostAppsRequestSchema,
  PostContentFragmentResponseSchema,
  PostConversationsResponseSchema,
  PostDataSourceDocumentRequestSchema,
  PostMCPRequestsRequestQuerySchema,
  PostMCPResultsResponseSchema,
  PostMessageFeedbackResponseSchema,
  PostSpaceMembersRequestBodySchema,
  PostTableParentsRequestSchema,
  PostUserMessageResponseSchema,
  PostWebhookTriggerResponseSchema,
  PostWorkspaceSearchResponseBodySchema,
  ProgressNotificationContentSchema,
  ProgressNotificationOutputSchema,
  ProjectMetadataSchema,
  ProjectMountDirectoryEntrySchema,
  ProjectMountFileEntrySchema,
  ProjectMountListEntrySchema,
  PublicContentFragmentWithContentSchema,
  PublicContentFragmentWithFileIdSchema,
  PublicFrameResponseBodySchema,
  PublicHeartbeatMCPRequestBodySchema,
  PublicPostContentFragmentRequestBodySchema,
  PublicPostConversationsRequestBodySchema,
  PublicPostEditMessagesRequestBodySchema,
  PublicPostMCPResultsRequestBodySchema,
  PublicPostMessageFeedbackRequestBodySchema,
  PublicPostMessagesRequestBodySchema,
  PublicRegisterMCPRequestBodySchema,
  PublicVizContentResponseBodySchema,
  QueryWritingInstructionsResourceSchema,
  ReasoningSuccessOutputSchema,
  RegisterMCPResponseSchema,
  RetrievalDocumentTypeSchema,
  RetryMessageResponseSchema,
  RunAgentQueryResourceSchema,
  RunAgentResultResourceSchema,
  RunAppResponseSchema,
  SearchDataSourceViewsRequestSchema,
  SearchDataSourceViewsResponseSchema,
  SearchRequestBodySchema,
  SearchResultResourceSchema,
  SearchWarningCodeSchema,
  TOOL_RUNNING_LABEL,
  TokenizeResponseSchema,
  ToolsetsResultResourceSchema,
  UpsertDataSourceFolderRequestSchema,
  UpsertDatabaseTableRequestSchema,
  UpsertFolderResponseSchema,
  UpsertTableFromCsvRequestSchema,
  UpsertTableRowsRequestSchema,
  UserAnswerRequiredOutputResourceSchema,
  UserQuestionItemSchema,
  ValidateActionRequestBodySchema,
  ValidateActionResponseSchema,
  ValidateMemberRequestSchema,
  WarningResourceSchema,
  WebsearchResultResourceSchema,
  WebsearchResultSchema,
  WorkOSOrganizationSchema,
  WorkspaceDomainSchema,
  apiErrorToDustError,
  assertNever,
  assertNeverAndIgnore,
  buildContext,
  createRetry,
  errorToString,
  getProviderFromRetrievedDocument,
  getTitleFromRetrievedDocument,
  isAgentMention,
  isAgentMessage2 as isAgentMessage,
  isAgentPauseOutputResourceType,
  isBlobAttachment,
  isBrowseResultResourceType,
  isConnectorProvider,
  isConnectorsAPIError,
  isDatabaseSchemaResourceType,
  isDustError,
  isDustMimeType,
  isExampleRowsResourceType,
  isExtractQueryResourceType,
  isExtractResultResourceType,
  isFileIdAttachment,
  isFilePathAttachment,
  isFolder,
  isImageProgressOutput,
  isIncludableInternalMimeType,
  isIncludeResultResourceType,
  isInteractiveContentFileContentOutput,
  isMCPProgressNotificationType,
  isQueryWritingInstructionsResourceType,
  isReasoningSuccessOutput,
  isResourceWithName,
  isRetryableError,
  isRunAgentQueryResourceType,
  isRunAgentResultResourceType,
  isSearchResultResourceType,
  isSqlQueryOutput,
  isStoreResourceProgressOutput,
  isSupportedAudioContentType,
  isSupportedFileContentType,
  isSupportedImageContentType,
  isSupportedPlainTextContentType,
  isTextContent,
  isThinkingOutput,
  isToolGeneratedFile,
  isToolsetsResultResourceType,
  isWarningResourceType,
  isWebsearchResultResourceType,
  isWebsite,
  normalizeError,
  removeNulls,
  sleep,
  supportedAudioFileFormats,
  supportedFileExtensions,
  supportedFontFileFormats,
  supportedImageFileFormats,
  supportedOtherFileFormats,
  withRetry
};
//# sourceMappingURL=client.esm.js.map
